<template>
  <div ref="playbackRoot" class="absolute inset-0 min-h-0 isolate">
    <!-- Covers stale decoded video layers Chrome can leave after a video→still advance (logs: tail on-air, no hidden video). -->
    <div
      v-if="postVideoImageBlackoutActive"
      class="absolute inset-0 z-[50] bg-black pointer-events-none"
      aria-hidden="true"
    />
    <WaitingBufferScreen
      v-if="surfaceForRender.frames.length === 0 && (queue?.length ?? 0) === 0"
      :show-clock="false"
    />
    <!-- Absolute leave so loading dissolves into first frame (not a hard cut). -->
    <transition name="playback-surface-reveal">
      <PlayerPlaybackSurfaceLoading
        v-if="surfaceForRender.frames.length === 0 && (queue?.length ?? 0) > 0"
        key="surface-loading"
      />
    </transition>
    <transition name="playback-surface-reveal">
      <div
        v-if="surfaceForRender.frames.length > 0"
        key="surface-frames"
        class="absolute inset-0 min-h-0"
      >
        <!-- Key by slot + frame id: same buffer index can swap creatives when schedule spine changes (e.g. network operator default merged / removed next to an ad-order line). `:key="i"` reused components and caused visible flicker. -->
        <template
          v-for="(frame, i) in surfaceForRender.frames"
          :key="`${i}-${frame.id}`"
        >
          <VBufferFrame
            v-if="bufferSlotShouldRender(i)"
            :buffer-index="i"
            :frame="frame"
            :rotation="rotation"
            :asset-index="assetIndex"
            :show="surfaceForRender.viewIdx === i"
            :seamless-incoming-from-link="surfaceForRender.viewIdx === i && seamlessAfterContinuousLink"
            :class="[surfaceForRender.viewIdx === i ? 'z-10' : 'z-0']"
            @playing="playingFrame = frame"
          />
        </template>
        <!-- Frozen outgoing video frame: one still, no second decoder (video→video included). -->
        <img
          v-if="stickyOutgoingHold?.frozenStillUrl"
          :src="stickyOutgoingHold.frozenStillUrl"
          alt=""
          class="pointer-events-none absolute inset-0 z-[40] h-full w-full object-contain bg-black transition-opacity ease-linear"
          :class="stickyHoldFadingOut ? 'opacity-0' : 'opacity-100'"
          :style="{ transitionDuration: `${stickyHoldFadeDurationMs}ms` }"
        >
        <!-- Phase B: keep outgoing pixels on top until incoming is paint-ready (or timeout). -->
        <!-- Phase C (tenant default on): fade sticky out over CROSSFADE_MS when clear reason is paint-ready or warm dissolve.
             Arm transition-duration while still opaque, then flip opacity — same-tick duration+opacity-0 skips CSS. -->
        <VBufferFrame
          v-else-if="stickyOutgoingHold != null"
          :key="`sticky-outgoing-${stickyOutgoingHold.frame.id}-${stickyOutgoingHold.generation}`"
          data-lucimark-sticky-hold="true"
          class="z-[40] transition-opacity ease-linear"
          :class="stickyHoldFadingOut ? 'opacity-0' : 'opacity-100'"
          :style="{ transitionDuration: `${stickyHoldFadeDurationMs}ms` }"
          :buffer-index="-1"
          :frame="stickyOutgoingHold.frame"
          :rotation="rotation"
          :asset-index="assetIndex"
          :show="true"
          :presentation-hold-overlay="true"
        />
      </div>
    </transition>
    <p
      v-if="skippedUncachedFrameCount > 0 && surfaceForRender.frames.length > 0"
      class="pointer-events-none absolute inset-x-0 bottom-[2.5vh] z-[60] text-center text-white/80"
      style="font-size: clamp(0.7rem, 1.4vw, 1.05rem); letter-spacing: 0.04em;"
      role="status"
    >
      {{ partialCacheHeadline }}
    </p>
    <WidgetTwoSlotLayer
      v-if="surfaceForRender.frames.length >= 2"
      :frame-buffer="surfaceForRender.frames"
      :view-idx="surfaceForRender.viewIdx"
      :asset-index="assetIndex"
      :all-frames="queue ?? []"
      :show="surfaceForRender.viewIdx >= 0 && surfaceForRender.viewIdx < surfaceForRender.frames.length"
      :crossfade-ms="presentationCrossfadeMs"
    />
  </div>
</template>

<script lang="ts" setup>
import type { PlaybackSegment } from '@lucimark/shared/helpers/playbackScheduling'
import type { VNode } from 'vue'
import * as Sentry from '@sentry/vue'
import type { TRotation } from './VFrame.vue'
import type { IPlaybackTimerStuckStatus } from '@/helpers/detectPlaybackTimerStuck'
import type { IAssetIndex, IQueueFrame, TCampaignAsset } from '@/types'
import { facetAssetIndexKey } from '@lucimark/shared/helpers/facetAssetIndexKey'
import { playerGlassStateHeadline } from '@lucimark/shared/helpers/playerGlassState'
import { isVideoPlaybackAllowedOnCapacity } from '@lucimark/shared/helpers/playerLowCapacityPolicy'
import { playlistUrnIsPlayerMasterPlaylist } from '@lucimark/shared/helpers/playerMasterPlaylist'
import { normalizeWidgetAspectRatioPolicy } from '@lucimark/shared/helpers/widgetAspectRatioPolicy'
import Bottleneck from 'bottleneck'
import { proxy } from 'comlink'
import debounce from 'lodash.debounce'
import { computed, h, nextTick, onBeforeUnmount, onMounted, provide, ref, shallowRef, toRefs, unref, watch } from 'vue'
import ImageContainer from '@/components/ImageContainer.vue'
import PlayerPlaybackSurfaceLoading from '@/components/PlayerPlaybackSurfaceLoading.vue'
import PortalTailFillerImage from '@/components/PortalTailFillerImage.vue'
import VBufferFrame from '@/components/VBufferFrame.vue'
import VideoContainer from '@/components/VideoContainer.vue'
import WaitingBufferScreen from '@/components/WaitingBufferScreen.vue'
import WidgetTwoSlotLayer from '@/components/WidgetTwoSlotLayer.vue'
import usePlayer from '@/composables/usePlayer'
import { PLAYBACK_HEARTBEAT_INTERVAL_MS, PROOF_OF_PLAY_WALL_BUCKET_FLUSH_LEAD_MS } from '@/constants'
import { shouldMarkBootPaintFromSurfaceApply } from '@/helpers/resolveBootSplashDismiss'
import {
  armPlaybackHeartbeatCadence,
  type PlaybackHeartbeatCadenceHandle
} from '@/helpers/playbackHeartbeatJitter'
import {
  capturePaintableVideoStill,
  shouldArmFrozenOutgoingStill
} from '@/helpers/captureOutgoingVideoStill'
import { shouldRemountVideoForCapacityPolicy } from '@/helpers/capacityVideoAssetIndex'
import { IMAGE_PRELOAD_LEAD_MS, VIDEO_PRELOAD_LEAD_MS } from '@/constants/playback'
import {
  clearImagePredecodeCache,
  isImagePredecoded,
  shouldArmPostVideoImageBlackout,
  predecodeImageSrc,
  releaseImagePredecode,
  touchImagePredecode
} from '@/helpers/imagePredecodeCache'
import {
  clearContentTransitionHoldOwnership,
  isContentTransitionHoldOverdue,
  releaseOwnedContentTransitionHold,
  takeContentTransitionHoldOwnership,
  type ContentTransitionHoldOwnership
} from '@/helpers/contentTransitionHoldOwnership'
import {
  holdUntilReadyTimeoutMsForTargetKind,
  isIncomingPaintReadyAtCommit,
  shouldArmPostVideoBlackoutOnStickyHoldClear,
  queueFramesDifferInFacetAssets,
  sameQueueFrameIdentity,
  shouldSkipStickyForNativeHybridSurface,
  shouldSkipStickyForVideoToVideo,
  shouldStickyHoldPresentationAdvance
} from '@/helpers/holdUntilReadyPresentation'
import { collectScheduleRootAssetUrns } from '@/helpers/collectScheduleRootAssetUrns'
import {
  clearIncompleteCampaignAssetRematerializeSentryDedupe,
  reportIncompleteCampaignAssetRematerializeToSentry
} from '@/helpers/reportIncompleteCampaignAssetRematerializeToSentry'
import { resolveTimersLastFrameDurationMs, stampTimersLastFrameOnTransition } from '@/helpers/resolveTimersLastFrameDurationMs'
import { setMediaLoopDeferredBoundaryListener, notifyMediaLoopDeferredBoundary } from '@/helpers/mediaLoopDeferredBoundary'
import { resolveSectionFit } from '@lucimark/shared/helpers/sectionMediaFit'
import {
  hybridSlotForViewIdx,
  hybridSlotMuted,
  listenHybridPlaybackEvent,
  hybridPlaybackStageKey,
  presentHybridSlot,
  preloadHybridSlot,
  releaseHybridPlayback,
  requestOmxHalWedgeReboot,
  setHybridPlaybackBudget,
  setHybridVisibleSlot,
  stageHybridPlaybackBlob,
  readHybridPlaybackFileAsBlob
} from '@/helpers/androidHybridPlayback'
import {
  nativeHybridSameUriNeedsRestart,
  presentNativeHybridRestartOrRemount
} from '@/helpers/nativeHybridFullReplace'
import {
  createHybridFirstFrameWatch,
  clearNativeHybridHalWedgeDisabled,
  HYBRID_BUFFERING_STALL_CODE,
  HYBRID_FIRST_FRAME_WATCH_MS,
  HYBRID_OMX_FIRST_FRAME_TIMEOUT_CODE,
  HYBRID_OMX_HAL_REBOOT_FIRED_CODE,
  HYBRID_OMX_HAL_REBOOT_SKIPPED_CODE,
  isHybridHalWedgeErrorCode,
  isNativePlaybackSurfaceEnabled,
  persistNativeHybridHalWedgeDisabled,
  setNativePlaybackUnderlayChrome,
  shouldSkipNativeHybridSurface
} from '@/helpers/nativePlaybackSurface'
import { getNativeShellAppVersionName } from '@/helpers/resolvedPlayerAppVersion'
import {
  EMPTY_PLAYBACK_SURFACE_MAX_RECOVERY_ATTEMPTS,
  EMPTY_PLAYBACK_SURFACE_PENDING_STALL_MS,
  EMPTY_PLAYBACK_SURFACE_RECOVERY_COMMIT_WAIT_MS,
  EMPTY_PLAYBACK_SURFACE_RECOVERY_COOLDOWN_MS,
  EMPTY_PLAYBACK_SURFACE_STALL_MS,
  FIRST_PAINT_CLOCK_REPHASE_MIN_EMPTY_MS,
  PENDING_SURFACE_COMMIT_MAX_RETRIES,
  PENDING_SURFACE_COMMIT_RETRY_MS,
  REGISTRATION_PLAYBACK_RESYNC_MAX_MS,
  ASSET_RESYNC_RECOVERY_GUARD_MAX_MS,
  pendingSurfaceCommitUrnsIncompleteMaterialization,
  pendingSurfaceCommitUrnsUnsatisfiable,
  shouldForceReloadIncompleteCampaignAssets,
  playbackBufferFramesMissingAssetIndex as bufferFramesMissingAssetIndex,
  facetsRequiredForPlaybackSurfaceCommit,
  assetIndexUrnsSafeToRevoke,
  registrationPlaybackResyncDeadline,
  settleEmptySurfaceRecoveryBootstrap,
  shouldDropPresentationForTransitionHold,
  shouldEscalateEmptyPlaybackSurfaceRecovery,
  shouldPublishPlaybackTimingOverlay,
  shouldRecoverEmptyPlaybackSurface,
  shouldRephaseFrameClockOnFirstPaint,
  shouldSchedulePendingCommitRetry
} from '@/helpers/playbackBufferAssetIndexGate'
import {
  isPresentationCrossfadeEnabled,
  resolvePresentationCrossfadeMs,
  shouldFadeStickyHoldOnClear,
  shouldWarmPathPresentationDissolve,
  waitAnimationFrames
} from '@/helpers/presentationCrossfade'
import { resolveWebPlaybackTickMode } from '@/helpers/webPlaybackTick'
import {
  isFullPlaylistReplaceMatchTier,
  shouldForcePresentationAlignAfterWorkerRematch,
  shouldSuppressPresentationDuringRegistrationAssetRebuild,
  shouldSyncCommitRegistrationRematchPresentation
} from '@/helpers/registrationRematchPresentation'
import { shouldSeamlesslyContinueFromPreviousFrame } from '@/helpers/continuousMediaPlayback'
import { resolveActiveFrameForProofOfPlay } from '@/helpers/proofOfPlayActiveFrame'
import { isWidgetEntry } from '@/helpers/widgetSlotHelpers'
import {
  detectPlaybackTimerStuck
} from '@/helpers/detectPlaybackTimerStuck'
import {
  installE2ePlaybackProbe,
  isE2ePlaybackProbeEnabled,
  notifyE2eLoopWrap,
  recordE2eHoldEvent,
  recordE2ePopHeartbeat,
  recordE2ePresentationCommit,
  registerE2eCompletedFrameTransitionInjector,
  registerE2eDiagnosticsSnapshotProvider,
  registerE2ePopFlushHandler,
  setE2eHoldDiagnostics,
  setE2ePopDiagnostics,
  setE2eQueueFrameIds,
  setE2eWidgetRevealMs
} from '@/helpers/e2ePlaybackProbe'
import {
  houseEditorialRotationCycleDurationMs,
  houseEditorialRotationElapsedMs
} from '@/helpers/houseEditorialTimerOverlay'
import { assetEntryMediaKind, isPortalTailAssetUrn } from '@/helpers/playbackAssetHelpers'
import {
  bootstrapPlaybackPresentationOnVBufferMount,
  clearPlaybackReloadColdStartPending,
  forceSetPresentationPlanIdx,
  getPeakPresentationPlanIdx,
  getPlaybackPresentationSession,
  isInitWorkerBootstrapPending,
  nextPlaybackWorkerInitGeneration,
  peekPlaybackWorkerInitGeneration,
  playbackPresentationAuthorityFromSession,
  recordPresentationPlanIdx,
  restorePlaybackPresentationSessionFromStorage,
  setInitWorkerBootstrapPending,
  shouldDiscardPresentationStorageOnVBufferMount,
  shouldSkipPresentationStorageRestore
} from '@/helpers/playbackPresentationSession'
import { healStalePresentationPeakAuthority } from '@/helpers/healStalePresentationPeakAuthority'
import { shouldLoopWrapOnContentNotAdvancingRecovery } from '@/helpers/shouldLoopWrapOnContentNotAdvancingRecovery'
import { shouldRecordPresentationAfterVisibleCommit } from '@/helpers/shouldRecordPresentationAfterVisibleCommit'
import { shouldRestashFailedPresentationCommit } from '@/helpers/shouldRestashFailedPresentationCommit'
import {
  noteWallDwellEscapeSamePaint,
  resetWallDwellEscapeSamePaintCounter,
  shouldCreditWallDwellEscapeSamePaint
} from '@/helpers/wallDwellEscapeSamePaintRemount'
import type { TFramePresentationMeta } from '@/services/PlaybackScheduler'
import type { ILastCompletedFrameTransition } from '@/helpers/playerDiagnostics/detectFrameTransitionAccelerated'
import { houseEmbedLoopWrapMinDwellMs } from '@/helpers/houseEmbedLoopWrapMinDwellMs'
import { detectContentNotAdvancing } from '@/helpers/playerDiagnostics/detectContentNotAdvancing'
import {
  buildPlayerDiagnosticsSnapshot,
  clearContentNotAdvancingForPlaybackTeardown,
  runPlayerDiagnosticsSafely,
  recordMainLoopGap,
  sampleContentProgressAlwaysOn,
  samplePlayerDiagnostics
} from '@/helpers/playerDiagnostics/playerDiagnosticService'
import { classifyMainLoopGap } from '@/helpers/playerDiagnostics/classifyMainLoopGap'
import { rebasePaintedVisibleFrameDwellOnResume, rebasePlaybackTimerStuckClocksOnResume, stampPaintedVisibleFrameChange } from '@/helpers/playerDiagnostics/stampPaintedVisibleFrameChange'
import { frameMainExpectsVideo as frameMainExpectsVideoFromFacets } from '@/helpers/playerDiagnostics/frameMainExpectsVideo'
import { sampleVisibleVideoDiagnostics } from '@/helpers/playerDiagnostics/sampleVisibleVideoDiagnostics'
import {
  proofOfPlayAttributionKey,
  shouldFlushProofOfPlayOnAttributionChange
} from '@/helpers/proofOfPlayFlush'
import {
  defaultMonotonicNowMs,
  defaultWallNowMs,
  finalizeProofOfPlayDurationSeconds,
  measureProofOfPlayDeltaMs
} from '@/helpers/proofOfPlayDuration'
import {
  msUntilNextProofOfPlayWallBucket
} from '@lucimark/shared/helpers/proofOfPlayWallClock'
import {
  areCampaignAssetListsEquivalent,
  getPlaybackWorkerPayloadSequenceHash,
  getPlayerGroupPlaybackWorkerSyncFingerprint,
  getQueueHash,
  HASH_EMPTY
} from '@/helpers/queueHash'
import { reportPlaybackTimerStuckToSentry } from '@/helpers/reportPlaybackTimerStuckToSentry'
import { reportPlayerVideoPlaybackToSentry } from '@/helpers/reportPlayerVideoPlaybackToSentry'
import {
  performPlaybackRecoveryReload,
  playbackFailureBucketFromHybridErrorCode,
  recordPlaybackFailureForReloadPolicy,
  recordPlaybackTimerStuckForReloadPolicy,
  tryPerformPlaybackRecoveryReload
} from '@/helpers/playbackFailureReloadPolicy'
import { shouldProphylacticPlaybackRecycle } from '@/helpers/playbackProphylacticRecycle'
import { shouldPreloadNextVideoSlot } from '@/helpers/shouldPreloadNextVideoSlot'
import {
  correctBufferOnlyNotifiedIdx,
  correctPresentationCommitNotifiedIdx,
  resolveVBufferRemountBootstrapAction,
  shouldApplyInitialWorkerNotifiedIdx
} from '@/helpers/vbufferPlaybackBootstrap'
import { extractWidgetIndexHtmlBlob, getWidgetPackageJsonFromBlob, htmlBlobToDataUrl, htmlBlobToSrcdoc, isWidgetTgzBlob, isWidgetTgzBlobAsync, mountWidgetBundle, shouldUseSrcdocForHtmlBlob, unmountWidgetBundle, useAndroidHtmlFallback } from '@/helpers/widgetSpaLoader'
import {
  bindPresentationViewToQueueNotifiedIdx,
  coercePresentationToQueuePlan,
  findQueueFrameForWorkerSegment,
  isLegitimatePortalBookLoopWrap,
  isRegressivePresentationToLoopHead,
  isSpuriousMidLoopHeadReset,
  isSpuriousPortalBookMidLoopHouseReset,
  playbackPlanHasBookCps,
  queueFramesToWorkerPlaybackPlan,
  resolvePresentationPreloadPlanIdx,
  resolvePresentationPreloadSegment,
  resolveQueueFramesFromWorkerPlaybackPlan,
  shouldAcceptPortalBookResumePlanIdx,
  workerBufferMatchesNotifiedIdx
} from '@/helpers/workerPlaybackPlan'
import { getSharedWorkersService } from '@/services/WorkersService'
import { canonicalStringify } from '@/workers/registrationStatusWorkerHash'

const props = defineProps<{ rotation?: TRotation }>()

const { rotation } = toRefs(props)

const { playbackScheduler } = getSharedWorkersService()
const {
  state,
  getPlaybackAssets,
  getPlaybackScheduleRootEffective,
  reloadPlaybackAssetsForce,
  isPlaybackAssetReloadInFlight,
  getWorkerPlaybackPlanNow,
  refreshEmbeddedPicksAfterLoopWrap,
  notifyDeferredPlaybackScheduleFrameBoundary,
  registerRegistrationPlaybackResyncHandler,
  registerCacheRepairMediaRemountHandler,
  reportPlaybackHeartbeat,
  reportPlaybackAspectTelemetrySample,
  platformWidgetConfig,
  weeklyAvailabilityPlaybackSuppressed
} = usePlayer()
const { queue, playerGroup, campaignAssetsVersion, campaign, skippedUncachedFrameCount } = toRefs(state)
const playbackRoot = ref<HTMLElement | null>(null)
const partialCacheHeadline = computed(() => playerGlassStateHeadline('playing_partial_cache', 'pt'))

const MAX_RETRIES = 3

/** Timeout (ms) after which an iframe asset is treated as loaded so playback is never blocked. */
const IFRAME_LOAD_TIMEOUT_MS = 5000
const iframeLoadTimeoutByUrn = new Map<string, ReturnType<typeof setTimeout>>()

const limiter = new Bottleneck({
  maxConcurrent: 1,
  highWater: 1,
  strategy: Bottleneck.strategy.LEAK
})

/** P1b: Media3 progressive path when tenant flag + APK shell allow it. */
const nativePlaybackSurfaceEnabled = ref(false)
let nativeShellVersionName: string | null = null
const nativeStagePromises = new Map<string, Promise<string | null>>()
const lastHybridSlotUri: [string | null, string | null] = [null, null]
/**
 * Full playlist replace: next same-URI `present` must seek to 0 (Kotlin `restart`) instead of
 * `load skip`. Keep lastHybridSlotUri so we can detect the shared hybrid-cache file.
 */
let nativeHybridRestartOnSameUri = false
/** Last clamped `maxDecoders` from Kotlin (capacityTier request may be higher). */
let hybridDecoderBudget = 1
/**
 * Progressive MP4 URNs that failed Media3 this session — fall back to WebView `<video>`
 * and skip re-staging until a full remount / flag off clears the set.
 */
const nativeHybridFailedUrns = new Set<string>()
/**
 * Process-wide Allwinner OMX wedge: no `onFirstFrame` after present. Skip native for
 * all progressive MP4s until flag-off remount / process death (HAL is not per-URN).
 */
let nativeHybridHalWedged = false
/** Once a recovery reload is armed, skip further native present/preload — `location.reload` is imminent. */
let hybridRecoveryReloadArmed = false
let hybridHalWedgeReported = false
let hybridHalRebootReported = false
const hybridFirstFrameWatch = createHybridFirstFrameWatch()
let hybridFirstFrameWatchTimer: ReturnType<typeof setTimeout> | null = null
let hybridEventUnsubs: Array<() => void> = []
let hybridSyncGen = 0
let hybridSyncChain: Promise<void> = Promise.resolve()

function nativeHybridSurfaceBlocked(urn: string): boolean {
  return shouldSkipNativeHybridSurface({
    urn,
    failedUrns: nativeHybridFailedUrns,
    halWedged: nativeHybridHalWedged,
    recoveryReloadArmed: hybridRecoveryReloadArmed
  })
}

function disarmHybridFirstFrameWatch(): void {
  hybridFirstFrameWatch.disarm()
  if (hybridFirstFrameWatchTimer != null) {
    clearTimeout(hybridFirstFrameWatchTimer)
    hybridFirstFrameWatchTimer = null
  }
}

function armHybridFirstFrameWatch(urn: string): void {
  if (nativeHybridHalWedged || hybridRecoveryReloadArmed) {
    return
  }
  hybridFirstFrameWatch.arm(urn)
  if (hybridFirstFrameWatchTimer != null) {
    clearTimeout(hybridFirstFrameWatchTimer)
  }
  hybridFirstFrameWatchTimer = setTimeout(() => {
    hybridFirstFrameWatchTimer = null
    if (!hybridFirstFrameWatch.isArmed()) {
      return
    }
    hybridFirstFrameWatch.disarm()
    onHybridHalWedged(HYBRID_OMX_FIRST_FRAME_TIMEOUT_CODE)
  }, HYBRID_FIRST_FRAME_WATCH_MS)
}

function onHybridHalWedged(code: string): void {
  if (nativeHybridHalWedged || hybridRecoveryReloadArmed) {
    return
  }
  nativeHybridHalWedged = true
  persistNativeHybridHalWedgeDisabled()
  nativePlaybackSurfaceEnabled.value = false
  disarmHybridFirstFrameWatch()
  hybridSyncGen += 1
  const lowMemoryMode = state.lowMemoryMode === true
  if (!hybridHalWedgeReported) {
    hybridHalWedgeReported = true
    reportPlayerVideoPlaybackToSentry({
      kind: 'media_error',
      userVisibleReason: code === HYBRID_BUFFERING_STALL_CODE
        ? 'Native Media3 BUFFERING stall (OMX HAL wedge)'
        : 'Native Media3 first frame timeout (OMX HAL wedge)',
      capacityTier: state.capacityTier,
      lowMemoryMode,
      playbackSurface: 'native_media3',
      hybridErrorCode: code
    })
  }
  // Session + device-local demote to WebView. Do not recovery-reload: reload clears the
  // in-memory wedge but OMX stays wedged in mediaserver and tenant flag stays on.
  void remountProgressiveVideosForNativeGate().then(() => {
    void requestOmxHalWedgeRebootAndReport()
  })
}

async function requestOmxHalWedgeRebootAndReport(): Promise<void> {
  const result = await requestOmxHalWedgeReboot()
  if (result == null || hybridHalRebootReported) {
    return
  }
  hybridHalRebootReported = true
  reportPlayerVideoPlaybackToSentry({
    kind: 'media_error',
    userVisibleReason: result.fired
      ? 'Device Owner reboot for OMX HAL wedge'
      : `OMX HAL wedge reboot skipped (${result.skipped ?? 'unknown'})`,
    capacityTier: state.capacityTier,
    lowMemoryMode: state.lowMemoryMode === true,
    playbackSurface: 'native_media3',
    hybridErrorCode: result.fired ? HYBRID_OMX_HAL_REBOOT_FIRED_CODE : HYBRID_OMX_HAL_REBOOT_SKIPPED_CODE
  })
}

async function refreshNativePlaybackSurfaceEnabled(): Promise<boolean> {
  try {
    nativeShellVersionName = await getNativeShellAppVersionName()
  }
  catch {
    nativeShellVersionName = null
  }
  const next = isNativePlaybackSurfaceEnabled({
    tenantFlag: state.tenant?.nativePlaybackSurface,
    shellVersionName: nativeShellVersionName
  })
  const prev = nativePlaybackSurfaceEnabled.value
  nativePlaybackSurfaceEnabled.value = next
  if (next) {
    nativeHybridHalWedged = false
    hybridHalWedgeReported = false
    hybridHalRebootReported = false
    void installNativeHybridPlaybackListeners()
  }
  else if (prev) {
    for (const u of hybridEventUnsubs) {
      u()
    }
    hybridEventUnsubs = []
    disarmHybridFirstFrameWatch()
    nativeHybridFailedUrns.clear()
    nativeHybridHalWedged = false
    hybridHalWedgeReported = false
    hybridHalRebootReported = false
    hybridRecoveryReloadArmed = false
  }
  if (prev !== next) {
    await remountProgressiveVideosForNativeGate()
  }
  return next
}

async function remountProgressiveVideosForNativeGate(): Promise<void> {
  disarmHybridFirstFrameWatch()
  const force = new Set<string>()
  const nativeUrns: string[] = []
  for (const [urn, entry] of Object.entries(unref(assetIndex))) {
    if (entry != null && typeof entry === 'object' && 'isVideoBlob' in entry && entry.isVideoBlob === true) {
      force.add(urn)
    }
    if (
      entry != null
      && typeof entry === 'object'
      && 'isNativeVideo' in entry
      && entry.isNativeVideo === true
    ) {
      nativeUrns.push(urn)
    }
  }
  nativeStagePromises.clear()
  lastHybridSlotUri[0] = null
  lastHybridSlotUri[1] = null
  // Leave native: swap vnodes after Exo release (same as onError) so the punched
  // underlay is not an empty hole while buildAssetIndex is still queued.
  const demoteNativeToWebView = !nativePlaybackSurfaceEnabled.value || nativeHybridHalWedged
  await releaseHybridPlayback()
  if (demoteNativeToWebView) {
    await Promise.all(nativeUrns.map(urn => remountHybridFailedUrnAsWebView(urn)))
    nativeUnderlaySurfaceArmed.value = false
  }
  if (force.size > 0) {
    await limiter.schedule(() => buildAssetIndex(force))
  }
  else {
    scheduleNativeHybridSync()
  }
}

function scheduleNativeHybridSync(): void {
  if (hybridRecoveryReloadArmed || nativeHybridHalWedged) {
    return
  }
  const gen = ++hybridSyncGen
  hybridSyncChain = hybridSyncChain
    .catch(() => {})
    .then(async () => {
      if (gen !== hybridSyncGen || hybridRecoveryReloadArmed) {
        return
      }
      await syncNativeHybridPlaybackSurface(gen)
    })
}

/**
 * Full playlist replace: invalidate in-flight hybrid sync and cover the underlay, but do not
 * `release()` Exo (OMX remount). Rematch `present({ restart: true })` seeks the same hybrid-cache
 * file; old APKs that ignore `restart` fall back to release + present after a position check.
 */
function prepareNativeHybridForFullPlaylistReplace(): void {
  hybridSyncGen++
  disarmHybridFirstFrameWatch()
  nativeUnderlaySurfaceArmed.value = false
  nativeHybridRestartOnSameUri = true
}

function nativeHybridUrnIsPlayable(urn: string): boolean {
  if (nativeHybridSurfaceBlocked(urn)) {
    return false
  }
  const entry = unref(assetIndex)[urn]
  return entry != null
    && typeof entry === 'object'
    && 'isNativeVideo' in entry
    && entry.isNativeVideo === true
}

async function ensureNativeVideoStaged(urn: string, blob: Blob): Promise<string | null> {
  if (nativeHybridSurfaceBlocked(urn)) {
    return null
  }
  const existing = unref(assetIndex)[urn]
  if (
    existing != null
    && typeof existing === 'object'
    && 'nativeFilePath' in existing
    && typeof existing.nativeFilePath === 'string'
    && existing.nativeFilePath.length > 0
  ) {
    return existing.nativeFilePath
  }
  let pending = nativeStagePromises.get(urn)
  if (pending == null) {
    pending = (async () => {
      const key = await hybridPlaybackStageKey(urn, blob)
      const path = await stageHybridPlaybackBlob(key, blob)
      if (path == null) {
        nativeStagePromises.delete(urn)
        return null
      }
      if (nativeHybridSurfaceBlocked(urn)) {
        nativeStagePromises.delete(urn)
        return null
      }
      const entry = unref(assetIndex)[urn]
      if (
        entry != null
        && typeof entry === 'object'
        && 'isNativeVideo' in entry
        && entry.isNativeVideo === true
      ) {
        unref(assetIndex)[urn] = { ...entry, nativeFilePath: path }
        scheduleNativeHybridSync()
      }
      return path
    })()
    nativeStagePromises.set(urn, pending)
  }
  return pending
}

async function syncNativeHybridPlaybackSurface(expectedGen: number): Promise<void> {
  if (expectedGen !== hybridSyncGen || hybridRecoveryReloadArmed) {
    return
  }
  if (!nativePlaybackSurfaceEnabled.value) {
    if (lastHybridSlotUri[0] != null || lastHybridSlotUri[1] != null) {
      lastHybridSlotUri[0] = null
      lastHybridSlotUri[1] = null
      await releaseHybridPlayback()
    }
    return
  }
  const { frames, viewIdx } = playbackSurface.value
  if (frames.length === 0 || viewIdx < 0) {
    /**
     * During registration hold the Vue surface is emptied on full replace. Releasing Exo here
     * remounts OMX; keep the underlay playing under opaque loading chrome until rematch present.
     */
    if (registrationTransitionHoldActive.value) {
      nativeUnderlaySurfaceArmed.value = false
      return
    }
    lastHybridSlotUri[0] = null
    lastHybridSlotUri[1] = null
    await releaseHybridPlayback()
    return
  }
  const requestedBudget = state.capacityTier === 'low' ? 1 : 2
  const budgetStatus = await setHybridPlaybackBudget(requestedBudget)
  if (expectedGen !== hybridSyncGen || hybridRecoveryReloadArmed) {
    return
  }
  // Prefer native clamp (RAM / low-RAM device) over capacityTier alone.
  const decoderBudget = Math.max(1, Math.min(2, budgetStatus?.maxDecoders ?? 1))
  hybridDecoderBudget = decoderBudget
  const vis = hybridSlotForViewIdx(viewIdx, decoderBudget)

  if (decoderBudget <= 1 && lastHybridSlotUri[1] != null) {
    await releaseHybridPlayback(1)
    if (expectedGen !== hybridSyncGen || hybridRecoveryReloadArmed) {
      return
    }
    lastHybridSlotUri[1] = null
  }

  const slotPaths: [string | null, string | null] = [null, null]
  const slotMuted: [boolean, boolean] = [true, true]
  const slotFit: ['contain' | 'cover', 'contain' | 'cover'] = ['contain', 'contain']
  for (let i = 0; i < Math.min(2, frames.length); i++) {
    const frame = frames[i]
    if (frame == null) {
      continue
    }
    const main = frame.facets?.main
    if (main == null || typeof main.asset !== 'string' || main.asset.trim() === '') {
      continue
    }
    const urn = facetAssetIndexKey(main)
    const entry = unref(assetIndex)[urn]
    // Single-decoder: only the on-air buffer frame maps to hybrid slot 0.
    const slot: 0 | 1 = decoderBudget <= 1
      ? 0
      : (i as 0 | 1)
    if (decoderBudget <= 1 && i !== viewIdx) {
      continue
    }
    // Session-failed URNs / process-wide HAL wedge stay skipped until remount.
    if (
      entry == null
      || typeof entry !== 'object'
      || !('isNativeVideo' in entry)
      || entry.isNativeVideo !== true
      || nativeHybridSurfaceBlocked(urn)
    ) {
      if (decoderBudget > 1 && lastHybridSlotUri[slot] != null) {
        await releaseHybridPlayback(slot)
        if (expectedGen !== hybridSyncGen || hybridRecoveryReloadArmed) {
          return
        }
        lastHybridSlotUri[slot] = null
      }
      continue
    }
    let path = typeof entry.nativeFilePath === 'string' ? entry.nativeFilePath : undefined
    if ((path == null || path === '') && 'blob' in entry && entry.blob instanceof Blob) {
      path = (await ensureNativeVideoStaged(urn, entry.blob)) ?? undefined
      if (expectedGen !== hybridSyncGen || hybridRecoveryReloadArmed) {
        return
      }
    }
    if (path == null || path === '' || !nativeHybridUrnIsPlayable(urn)) {
      continue
    }
    slotPaths[slot] = path
    slotMuted[slot] = hybridSlotMuted(main.unmuted)
    slotFit[slot] = resolveSectionFit(frame.layout?.sections?.main?.fit, 'video')
    // Warm hidden slot only when budget allows a second decoder.
    if (decoderBudget > 1 && slot !== vis && lastHybridSlotUri[slot] !== path) {
      const preloadStatus = await preloadHybridSlot(slot, path, 'video', { muted: slotMuted[slot], fit: slotFit[slot] })
      if (expectedGen !== hybridSyncGen || hybridRecoveryReloadArmed) {
        return
      }
      const loaded = slot === 0 ? preloadStatus?.slot0Loaded : preloadStatus?.slot1Loaded
      if (loaded === true) {
        lastHybridSlotUri[slot] = path
      }
    }
  }

  // On-air left native path while single-decoder still holds slot 0.
  if (decoderBudget <= 1 && slotPaths[0] == null && lastHybridSlotUri[0] != null) {
    await releaseHybridPlayback(0)
    if (expectedGen !== hybridSyncGen || hybridRecoveryReloadArmed) {
      return
    }
    lastHybridSlotUri[0] = null
  }

  // Same file already warm on the incoming slot: set volume before the swap.
  // setVisibleSlot starts playback immediately. A present afterwards leaks the
  // previous volume for one invoke, including a same-URI playlist replace
  // (`restart`) that seeks only after the slot is shown.
  const visPathWarm = slotPaths[vis]
  let warmRestartResult: Awaited<ReturnType<typeof presentNativeHybridRestartOrRemount>> | null = null
  if (visPathWarm != null && lastHybridSlotUri[vis] === visPathWarm) {
    if (nativeHybridSameUriNeedsRestart(lastHybridSlotUri[vis], visPathWarm, nativeHybridRestartOnSameUri)) {
      warmRestartResult = await presentNativeHybridRestartOrRemount({
        slot: vis,
        uri: visPathWarm,
        muted: slotMuted[vis],
        fit: slotFit[vis],
        present: presentHybridSlot,
        release: releaseHybridPlayback
      })
    }
    else {
      await presentHybridSlot(vis, visPathWarm, 'video', { muted: slotMuted[vis], fit: slotFit[vis] })
    }
    if (expectedGen !== hybridSyncGen || hybridRecoveryReloadArmed) {
      return
    }
  }

  await setHybridVisibleSlot(vis)
  if (expectedGen !== hybridSyncGen || hybridRecoveryReloadArmed) {
    return
  }
  const visPath = slotPaths[vis]
  const visFrameIdx = decoderBudget <= 1 ? viewIdx : vis
  const visFrame = visFrameIdx >= 0 && visFrameIdx < frames.length ? frames[visFrameIdx] : null
  const visMain = visFrame?.facets?.main
  const visUrn = visMain != null && typeof visMain.asset === 'string' && visMain.asset.trim() !== ''
    ? facetAssetIndexKey(visMain)
    : null
  // onError may have demoted this URN while we awaited budget/stage — never re-present it.
  if (visUrn != null && !nativeHybridUrnIsPlayable(visUrn)) {
    disarmHybridFirstFrameWatch()
    return
  }
  if (visPath == null) {
    disarmHybridFirstFrameWatch()
    return
  }
  const visFit = resolveSectionFit(visFrame?.layout?.sections?.main?.fit, 'video')
  const lastVisUri = lastHybridSlotUri[vis]
  const restartSameUri = nativeHybridSameUriNeedsRestart(
    lastVisUri,
    visPath,
    nativeHybridRestartOnSameUri
  )
  nativeHybridRestartOnSameUri = false
  if (lastVisUri !== visPath) {
    if (visUrn != null) {
      armHybridFirstFrameWatch(visUrn)
    }
    const presentStatus = await presentHybridSlot(vis, visPath, 'video', { muted: slotMuted[vis], fit: visFit })
    if (expectedGen !== hybridSyncGen || hybridRecoveryReloadArmed) {
      if (hybridRecoveryReloadArmed) {
        disarmHybridFirstFrameWatch()
      }
      return
    }
    const loaded = vis === 0 ? presentStatus?.slot0Loaded : presentStatus?.slot1Loaded
    if (loaded === true) {
      lastHybridSlotUri[vis] = visPath
    }
    else {
      disarmHybridFirstFrameWatch()
    }
  }
  else if (restartSameUri) {
    const { status: presentStatus, remounted } = warmRestartResult ?? await presentNativeHybridRestartOrRemount({
      slot: vis,
      uri: visPath,
      muted: slotMuted[vis],
      fit: visFit,
      present: presentHybridSlot,
      release: releaseHybridPlayback
    })
    if (expectedGen !== hybridSyncGen || hybridRecoveryReloadArmed) {
      if (hybridRecoveryReloadArmed) {
        disarmHybridFirstFrameWatch()
      }
      return
    }
    const loaded = vis === 0 ? presentStatus?.slot0Loaded : presentStatus?.slot1Loaded
    if (loaded !== true) {
      disarmHybridFirstFrameWatch()
      return
    }
    lastHybridSlotUri[vis] = visPath
    if (remounted) {
      if (visUrn != null) {
        armHybridFirstFrameWatch(visUrn)
      }
    }
    else {
      disarmHybridFirstFrameWatch()
      if (visUrn != null && nativeHybridUrnIsPlayable(visUrn)) {
        nativeUnderlaySurfaceArmed.value = true
      }
    }
  }
  else {
    // URI unchanged — still nudge play/visibility (Kotlin skips setMediaItem).
    // Do not re-arm: a new first frame is not guaranteed, and resetting would
    // false-trigger the OMX wedge while the clip is already on-air.
    await presentHybridSlot(vis, visPath, 'video', { muted: slotMuted[vis], fit: visFit })
    if (expectedGen !== hybridSyncGen || hybridRecoveryReloadArmed) {
      if (hybridRecoveryReloadArmed) {
        disarmHybridFirstFrameWatch()
      }
    }
  }
}

async function installNativeHybridPlaybackListeners(): Promise<void> {
  for (const u of hybridEventUnsubs) {
    u()
  }
  hybridEventUnsubs = []
  if (!nativePlaybackSurfaceEnabled.value) {
    return
  }
  // Arm chrome on READY so CSS punch is not stuck if firstFrame events are delayed/missed.
  hybridEventUnsubs.push(await listenHybridPlaybackEvent('onReady', (payload) => {
    const { frames, viewIdx } = playbackSurface.value
    const onAir = viewIdx >= 0 && viewIdx < frames.length ? frames[viewIdx] : null
    if (onAir == null) {
      return
    }
    const hybridSlot = hybridSlotForViewIdx(viewIdx, hybridDecoderBudget)
    if (
      (payload.slot === hybridSlot || payload.visibleSlot === hybridSlot)
      && frameMainIsNativeVideo(onAir)
    ) {
      nativeUnderlaySurfaceArmed.value = true
    }
  }))
  hybridEventUnsubs.push(await listenHybridPlaybackEvent('onFirstFrame', (payload) => {
    const { frames, viewIdx } = playbackSurface.value
    const onAir = viewIdx >= 0 && viewIdx < frames.length ? frames[viewIdx] : null
    if (onAir == null) {
      return
    }
    const hybridSlot = hybridSlotForViewIdx(viewIdx, hybridDecoderBudget)
    if (payload.slot === hybridSlot || payload.visibleSlot === hybridSlot) {
      disarmHybridFirstFrameWatch()
      playingFrame.value = onAir
      reportHoldPaintReady({ type: 'video' })
      state.firstPlaybackFramePainted = true
      if (frameMainIsNativeVideo(onAir)) {
        nativeUnderlaySurfaceArmed.value = true
      }
    }
  }))
  hybridEventUnsubs.push(await listenHybridPlaybackEvent('onEnded', (payload) => {
    const { frames, viewIdx } = playbackSurface.value
    const hybridSlot = hybridSlotForViewIdx(viewIdx, hybridDecoderBudget)
    const slot = payload.slot === 1 ? 1 : 0
    if (slot !== hybridSlot) {
      return
    }
    if (nativeHybridHalWedged || hybridRecoveryReloadArmed) {
      return
    }
    /**
     * Full playlist replace keeps Exo mounted (no OMX remount) and sets
     * {@link nativeHybridRestartOnSameUri} until rematch hybrid sync. Re-presenting
     * `lastHybridSlotUri` in that window loops the outgoing last clip (#1632) — including
     * after sync-commit has already painted the new surface while hold is still latched.
     * Empty-surface hold is the same window before commit.
     * Soft-defer: only skip same-URI present once head is ready and we wait for the current
     * frame to finish (`ready_at_boundary`) — pausing at EOF. During `downloading`, keep
     * normal in-slot loops so the on-air program still advances (#1634 Bugbot).
     */
    if (
      nativeHybridRestartOnSameUri
      || state.pendingPlaylistSwap?.phase === 'ready_at_boundary'
      || state.pendingPlaylistSwap?.phase === 'rematching'
      || (registrationTransitionHoldActive.value && playbackSurface.value.frames.length === 0)
    ) {
      notifyMediaLoopDeferredBoundary()
      return
    }
    const uri = lastHybridSlotUri[slot]
    if (uri == null) {
      return
    }
    const frameIdx = hybridDecoderBudget <= 1 ? viewIdx : slot
    const muted = hybridSlotMuted(frames[frameIdx]?.facets?.main?.unmuted)
    // Same URI loop — do not re-arm. Media3 often skips another onFirstFrame after
    // ENDED→seekTo(0); a fresh 6s watch would false-wedge healthy playback.
    void presentHybridSlot(slot, uri, 'video', { muted }).then(() => {
      notifyMediaLoopDeferredBoundary()
    })
  }))
  hybridEventUnsubs.push(await listenHybridPlaybackEvent('onError', (payload) => {
    console.warn('[hybridPlayback] onError', payload)
    disarmHybridFirstFrameWatch()
    if (payload.halWedge === true || isHybridHalWedgeErrorCode(
      typeof payload.code === 'string' ? payload.code : undefined
    )) {
      onHybridHalWedged(
        typeof payload.code === 'string' && payload.code.trim() !== ''
          ? payload.code
          : HYBRID_BUFFERING_STALL_CODE
      )
      return
    }
    const slot = payload.slot === 1 ? 1 : 0
    lastHybridSlotUri[slot] = null
    // Dual-buffer: slot ↔ frame index. Single-decoder: always slot 0 ↔ on-air viewIdx.
    const frameIdx = hybridDecoderBudget <= 1 ? playbackSurface.value.viewIdx : slot
    const frame = playbackSurface.value.frames[frameIdx]
    const main = frame?.facets?.main
    const urn = main != null && typeof main.asset === 'string' && main.asset.trim() !== ''
      ? facetAssetIndexKey(main)
      : null
    if (urn != null) {
      nativeStagePromises.delete(urn)
      nativeHybridFailedUrns.add(urn)
    }
    const bucket = playbackFailureBucketFromHybridErrorCode(
      typeof payload.code === 'string' ? payload.code : undefined
    )
    const lowMemoryMode = state.lowMemoryMode === true
    reportPlayerVideoPlaybackToSentry({
      kind: 'media_error',
      userVisibleReason: typeof payload.message === 'string' ? payload.message : undefined,
      playbackSrc: typeof payload.uri === 'string' ? payload.uri : undefined,
      capacityTier: state.capacityTier,
      lowMemoryMode,
      mediaErrorMessage: typeof payload.message === 'string' ? payload.message : undefined,
      playbackSurface: 'native_media3',
      hybridErrorCode: typeof payload.code === 'string' ? payload.code : undefined
    })
    const decision = recordPlaybackFailureForReloadPolicy({
      bucket,
      lowMemoryMode
    })
    if (decision.reload === true) {
      hybridRecoveryReloadArmed = true
      hybridSyncGen += 1
    }
    // Keep underlay chrome until Exo is released — disarming first punches opaque WebView
    // over a still-visible SurfaceView (black flash). Reload skips remount + native sync.
    void releaseHybridPlayback(slot).then(async () => {
      if (urn != null && decision.reload !== true) {
        await remountHybridFailedUrnAsWebView(urn)
      }
      nativeUnderlaySurfaceArmed.value = false
      if (decision.reload !== true) {
        scheduleNativeHybridSync()
      }
      if (decision.reload === true && decision.reason != null) {
        performPlaybackRecoveryReload(`hybrid:${decision.reason}`)
      }
    })
  }))
}

/** Session demote: native `onError` → WebView `VideoContainer` for this URN (after Exo release). */
async function remountHybridFailedUrnAsWebView(urn: string): Promise<void> {
  const entry = unref(assetIndex)[urn]
  if (
    entry != null
    && typeof entry === 'object'
    && 'isNativeVideo' in entry
    && entry.isNativeVideo === true
  ) {
    let blob: Blob | null = 'blob' in entry && entry.blob instanceof Blob ? entry.blob : null
    if (blob == null) {
      blob = await readHybridPlaybackFileAsBlob(urn, 'video/mp4')
    }
    if (blob instanceof Blob) {
      const src = URL.createObjectURL(blob)
      if (typeof entry.src === 'string' && entry.src.startsWith('blob:')) {
        URL.revokeObjectURL(entry.src)
      }
      unref(assetIndex)[urn] = {
        src,
        blob,
        vnode: h(VideoContainer, { src }),
        isVideoBlob: true as const
      }
      return
    }
    unref(assetIndex)[urn] = { ...entry, isNativeVideo: undefined, nativeFilePath: undefined }
  }
}

/** After cache repair / redownload, `Campaign.load()` yields new `Blob` instances; revoke old object URLs and unmount widgets so we do not keep rendering stale binaries (black frame). */
function releasePreviousBinaryAssetEntry(urn: string, prevEntry: IAssetIndex[string] | undefined): void {
  if (prevEntry == null) {
    return
  }
  if ('src' in prevEntry && typeof prevEntry.src === 'string' && prevEntry.src.startsWith('blob:')) {
    releaseImagePredecode(prevEntry.src)
    URL.revokeObjectURL(prevEntry.src)
  }
  if ('blob' in prevEntry && prevEntry.blob instanceof Blob && isWidgetTgzBlob(prevEntry.blob)) {
    unmountWidgetBundle(urn)
  }
}

/** URNs still painted by surface / sticky — must survive campaign asset list rebuilds. */
function collectOnScreenAssetUrns(): Set<string> {
  const urns = new Set<string>()
  const addFrames = (frames: IQueueFrame[] | null | undefined) => {
    if (frames == null) {
      return
    }
    for (const frame of frames) {
      for (const urn of getAssetUrnsFromFrame(frame)) {
        urns.add(urn)
      }
    }
  }
  addFrames(playbackSurface.value.frames)
  addFrames(lastNonEmptyPlaybackSurface.value?.frames)
  const sticky = stickyOutgoingHold.value
  if (sticky != null) {
    for (const urn of getAssetUrnsFromFrame(sticky.frame)) {
      urns.add(urn)
    }
  }
  return urns
}

/** Freeze facet.asset URNs so a later queue replace cannot mutate the held paint set in place. */
function snapshotPlaybackSurfaceForHold(
  hold: { frames: IQueueFrame[], viewIdx: number }
): { frames: IQueueFrame[], viewIdx: number } {
  return {
    viewIdx: hold.viewIdx,
    frames: hold.frames.map((frame) => {
      const facets = frame.facets
      if (facets == null) {
        return { ...frame }
      }
      const nextFacets: IQueueFrame['facets'] = {}
      for (const [key, facet] of Object.entries(facets)) {
        nextFacets[key] = { ...facet }
      }
      return { ...frame, facets: nextFacets }
    })
  }
}

/**
 * True when {@link pinContentTransitionHoldFromCurrentSurface} owns the transition hold.
 * Registration resync can share `registrationTransitionHoldActive` without this flag — early
 * flush exits must not clear a registration hold they did not pin (#1352 review).
 */
const contentTransitionHoldOwnership: ContentTransitionHoldOwnership = { owned: false }

function pinContentTransitionHoldFromCurrentSurface(): void {
  if (registrationTransitionHoldActive.value) {
    return
  }
  const rawHold = lastNonEmptyPlaybackSurface.value ?? playbackSurface.value
  if (rawHold.frames.length === 0) {
    return
  }
  if (!takeContentTransitionHoldOwnership(
    contentTransitionHoldOwnership,
    registrationTransitionHoldActive.value
  )) {
    return
  }
  lastNonEmptyPlaybackSurface.value = snapshotPlaybackSurfaceForHold(rawHold)
  registrationTransitionHoldActive.value = true
}

/** Drop a content-only pin; no-op when registration resync owns the hold. */
function releaseContentTransitionHoldIfOwned(): void {
  if (!releaseOwnedContentTransitionHold(contentTransitionHoldOwnership)) {
    return
  }
  registrationTransitionHoldActive.value = false
  // Assigned after S1 helpers exist — replay queue facets missed while the hold blocked commits.
  schedulePostContentHoldSurfaceCatchUp()
}

/**
 * Early {@link flushQueueAssetsWatch} exits must not release while buildAssetIndex / finishResync
 * still owns the pin (lastFlushed* is updated before the async finally) (#1352 review).
 */
function releaseContentTransitionHoldIfIdle(): void {
  if (queueAssetResyncInProgress.value) {
    return
  }
  releaseContentTransitionHoldIfOwned()
}

/** Filled once {@link syncSameSequenceQueueFacetsToSurface} is defined (below). */
let schedulePostContentHoldSurfaceCatchUp: () => void = () => {}

function revokeAssetIndexEntry(assetId: string): void {
  const timeoutId = iframeLoadTimeoutByUrn.get(assetId)
  if (timeoutId) {
    clearTimeout(timeoutId)
    iframeLoadTimeoutByUrn.delete(assetId)
  }
  const entry = unref(assetIndex)?.[assetId]
  if (entry) {
    if ('src' in entry && typeof entry.src === 'string' && entry.src.startsWith('blob:')) {
      releaseImagePredecode(entry.src)
      URL.revokeObjectURL(entry.src)
    }
    else if ('blob' in entry && entry.blob instanceof Blob && isWidgetTgzBlob(entry.blob)) {
      unmountWidgetBundle(assetId)
    }
  }
  delete unref(assetIndex)[assetId]
}

/** Drop index entries that are neither in the campaign list nor still on screen. */
function purgeAssetIndexEntriesNotNeeded(): void {
  const requiredAssetIds = new Set(getPlaybackAssets().map(({ urn }) => urn))
  const retainUrns = collectOnScreenAssetUrns()
  for (const assetId of assetIndexUrnsSafeToRevoke({
    indexedUrns: Object.keys(unref(assetIndex)),
    requiredAssetIds,
    retainUrns
  })) {
    revokeAssetIndexEntry(assetId)
  }
}

limiter.on('failed', (error, jobInfo) => {
  console.warn('job failed', error)
  const delay = 2 ** jobInfo.retryCount + Math.random() * 1000
  if (jobInfo.retryCount < MAX_RETRIES - 1) {
    return delay
  }
})

// const { fetchAssets, assetDownloadProgressIndex, downloadProgress } = useAssets()
const playingFrame = ref<IQueueFrame | null>(null)
/** Brief full-screen black after video→image advances while the still decodes (Chrome stale layer). */
const postVideoImageBlackoutActive = ref(false)
let postVideoImageBlackoutTimer: ReturnType<typeof setTimeout> | null = null

/**
 * Sticky outgoing frame while the committed (underneath) surface becomes paint-ready.
 * Scheduler / PoP still follow `playbackSurface`; this is presentation-only (Phase B / #966).
 */
const stickyOutgoingHold = shallowRef<{
  frame: IQueueFrame
  targetFrameId: string
  targetKind: string
  targetImageSrc: string | null
  prevKind: string
  generation: number
  /** JPEG of the outgoing `<video>` so the hold does not start another decoder. */
  frozenStillUrl: string | null
} | null>(null)
let stickyHoldGeneration = 0
let stickyHoldTimer: ReturnType<typeof setTimeout> | null = null
/** Phase C: opacity fade-out of sticky overlay before unmount (default on). */
const stickyHoldFadingOut = ref(false)
/** CSS transition-duration for sticky (armed before opacity flip so dissolve actually animates). */
const stickyHoldFadeDurationMs = ref(0)
let stickyFadeTimer: ReturnType<typeof setTimeout> | null = null
/** Cancels in-flight fade arming when a newer clear/begin supersedes. */
let stickyFadeArmToken = 0

function finishClearStickyOutgoingHold(
  reason: 'ready' | 'timeout' | 'supersede' | 'teardown',
  hold: NonNullable<typeof stickyOutgoingHold.value>
): void {
  const outgoingFrameId = hold.frame.id
  const targetFrameId = hold.targetFrameId
  stickyOutgoingHold.value = null
  stickyHoldFadingOut.value = false
  stickyHoldFadeDurationMs.value = 0
  stickyFadeArmToken += 1
  if (stickyHoldTimer != null) {
    clearTimeout(stickyHoldTimer)
    stickyHoldTimer = null
  }
  if (stickyFadeTimer != null) {
    clearTimeout(stickyFadeTimer)
    stickyFadeTimer = null
  }
  setE2eHoldDiagnostics({ active: false, outgoingFrameId: null, targetFrameId: null })
  recordE2eHoldEvent({
    event: 'clear',
    reason,
    outgoingFrameId,
    targetFrameId,
    ts: performance.now()
  })
  // Sticky remounts must not leave PoP pinned to the outgoing video after the surface advanced.
  const vis = playbackSurface.value.frames[
    Math.min(Math.max(0, playbackSurface.value.viewIdx), Math.max(0, playbackSurface.value.frames.length - 1))
  ]
  const visKind = frameMainMediaKind(vis)
  if (visKind !== 'video-blob' && visKind !== 'hls') {
    playingFrame.value = null
  }
  if (
    shouldArmPostVideoBlackoutOnStickyHoldClear({
      reason,
      prevKind: hold.prevKind,
      targetKind: hold.targetKind,
      targetImageSrc: hold.targetImageSrc,
      shouldArmBlackout: shouldArmPostVideoImageBlackout
    })
  ) {
    armPostVideoImageBlackout()
  }
  // Sticky no longer pins the outgoing URN — drop orphans retained during asset rebuild (#1352).
  purgeAssetIndexEntriesNotNeeded()
}

function clearStickyOutgoingHold(reason: 'ready' | 'timeout' | 'supersede' | 'teardown'): void {
  const hold = stickyOutgoingHold.value
  if (hold == null && stickyHoldTimer == null && stickyFadeTimer == null) {
    stickyFadeArmToken += 1
    stickyHoldFadeDurationMs.value = 0
    stickyHoldFadingOut.value = false
    return
  }
  if (hold == null) {
    stickyFadeArmToken += 1
    if (stickyHoldTimer != null) {
      clearTimeout(stickyHoldTimer)
      stickyHoldTimer = null
    }
    if (stickyFadeTimer != null) {
      clearTimeout(stickyFadeTimer)
      stickyFadeTimer = null
    }
    stickyHoldFadingOut.value = false
    stickyHoldFadeDurationMs.value = 0
    return
  }
  // Already fading this generation — ignore duplicate ready; hard-clear on other reasons.
  if (stickyHoldFadingOut.value) {
    if (reason === 'ready') {
      return
    }
    finishClearStickyOutgoingHold(reason, hold)
    return
  }
  if (shouldFadeStickyHoldOnClear(
    reason,
    isPresentationCrossfadeEnabled(
      state.tenant?.playerCreativeCrossfade,
      state.capacityTier,
      state.tenant?.playerCreativeCrossfadeOnLowCapacity,
      state.pendingPlaylistSwap != null || registrationTransitionHoldActive.value
    )
  )) {
    if (stickyHoldTimer != null) {
      clearTimeout(stickyHoldTimer)
      stickyHoldTimer = null
    }
    const generation = hold.generation
    const armToken = ++stickyFadeArmToken
    // Arm duration while still opaque, wait for paint, then flip opacity (avoids skipped CSS transition).
    stickyHoldFadeDurationMs.value = presentationCrossfadeMs.value
    stickyHoldFadingOut.value = false
    void (async () => {
      await waitAnimationFrames(2)
      if (armToken !== stickyFadeArmToken) {
        return
      }
      if (stickyOutgoingHold.value?.generation !== generation) {
        return
      }
      stickyHoldFadingOut.value = true
      stickyFadeTimer = setTimeout(() => {
        stickyFadeTimer = null
        const current = stickyOutgoingHold.value
        if (current?.generation !== generation) {
          return
        }
        finishClearStickyOutgoingHold(reason, current)
      }, presentationCrossfadeMs.value)
    })()
    return
  }
  finishClearStickyOutgoingHold(reason, hold)
}

function beginStickyOutgoingHold(opts: {
  frame: IQueueFrame
  targetFrameId: string
  targetKind: string
  targetImageSrc: string | null
  prevKind: string
  /**
   * Warm-path dissolve clears after sticky paint in {@link commitPlaybackSurfaceNow}.
   * Skip target-ready auto-clear so an already-warm incoming does not fade the overlay before mount.
   */
  deferReadyClear?: boolean
  frozenStillUrl?: string | null
}): void {
  clearStickyOutgoingHold('supersede')
  const generation = ++stickyHoldGeneration
  stickyHoldFadingOut.value = false
  stickyHoldFadeDurationMs.value = 0
  stickyOutgoingHold.value = {
    frame: opts.frame,
    targetFrameId: opts.targetFrameId,
    targetKind: opts.targetKind,
    targetImageSrc: opts.targetImageSrc,
    prevKind: opts.prevKind,
    generation,
    frozenStillUrl: opts.frozenStillUrl ?? null
  }
  // Pin outgoing still so sticky remount stays warm (MAX_CACHED=3 otherwise evicts → opacity-0 flash).
  const outgoingImageSrc = frameMainImageSrc(opts.frame)
  if (outgoingImageSrc != null && outgoingImageSrc !== '') {
    if (!touchImagePredecode(outgoingImageSrc)) {
      void predecodeImageSrc(outgoingImageSrc)
    }
  }
  setE2eHoldDiagnostics({
    active: true,
    outgoingFrameId: opts.frame.id,
    targetFrameId: opts.targetFrameId
  })
  recordE2eHoldEvent({
    event: 'begin',
    outgoingFrameId: opts.frame.id,
    targetFrameId: opts.targetFrameId,
    ts: performance.now()
  })
  stickyHoldTimer = setTimeout(() => {
    stickyHoldTimer = null
    if (stickyOutgoingHold.value?.generation === generation) {
      clearStickyOutgoingHold('timeout')
    }
  }, holdUntilReadyTimeoutMsForTargetKind(opts.targetKind))
  if (
    !opts.deferReadyClear
    && opts.targetKind === 'image'
    && opts.targetImageSrc != null
    && opts.targetImageSrc !== ''
  ) {
    const src = opts.targetImageSrc
    void predecodeImageSrc(src).then((ok) => {
      if (
        ok
        && stickyOutgoingHold.value?.generation === generation
        && stickyOutgoingHold.value.targetImageSrc === src
        && isImagePredecoded(src)
      ) {
        clearStickyOutgoingHold('ready')
      }
    })
  }
}

function reportHoldPaintReady(info: { type: 'image' | 'video' | 'widget', src?: string }): void {
  const hold = stickyOutgoingHold.value
  if (hold == null) {
    return
  }
  if (info.type === 'widget') {
    clearStickyOutgoingHold('ready')
    return
  }
  if (info.type === 'image' && hold.targetKind === 'image' && hold.targetImageSrc === info.src) {
    clearStickyOutgoingHold('ready')
    return
  }
  if (
    info.type === 'video'
    && (hold.targetKind === 'video-blob' || hold.targetKind === 'hls')
  ) {
    clearStickyOutgoingHold('ready')
  }
}

provide('reportHoldPaintReady', reportHoldPaintReady)

function frameMainMediaKind(frame: IQueueFrame | null | undefined): string {
  const main = frame?.facets?.main
  if (main == null || typeof main.asset !== 'string' || main.asset.trim() === '') {
    return 'none'
  }
  return assetEntryMediaKind(unref(assetIndex)[facetAssetIndexKey(main)])
}

function frameMainIsWidget(frame: IQueueFrame | null | undefined): boolean {
  const main = frame?.facets?.main
  if (main == null || typeof main.asset !== 'string' || main.asset.trim() === '') {
    return false
  }
  return isWidgetEntry(unref(assetIndex)[facetAssetIndexKey(main)])
}

/** Blob/object URL for a frame's main image facet, if present. */
function frameMainImageSrc(frame: IQueueFrame | null | undefined): string | null {
  const main = frame?.facets?.main
  if (main == null || typeof main.asset !== 'string' || main.asset.trim() === '') {
    return null
  }
  const entry = unref(assetIndex)[facetAssetIndexKey(main)]
  if (entry == null || assetEntryMediaKind(entry) !== 'image') {
    return null
  }
  if (!('src' in entry) || typeof entry.src !== 'string' || entry.src === '') {
    return null
  }
  return entry.src
}

function armPostVideoImageBlackout(): void {
  postVideoImageBlackoutActive.value = true
  if (postVideoImageBlackoutTimer != null) {
    clearTimeout(postVideoImageBlackoutTimer)
  }
  postVideoImageBlackoutTimer = setTimeout(() => {
    postVideoImageBlackoutActive.value = false
    postVideoImageBlackoutTimer = null
  }, 200)
}
/**
 * One reactive snapshot for buffer slots + visible index so Vue never renders a mismatched pair
 * (new `frames` with stale `viewIdx` or vice versa — brief wrong-slot / first-frame flash).
 */
const playbackSurface = shallowRef<{ frames: IQueueFrame[], viewIdx: number }>({
  frames: [],
  viewIdx: -1
})

/**
 * When the registration queue updates before the worker repaints, `playbackSurface` can briefly be empty while
 * `queue` is already non-empty — the template used to show a full black layer (visible flicker). We keep the last
 * non-empty presentation until the worker catches up.
 */
const lastNonEmptyPlaybackSurface = shallowRef<{ frames: IQueueFrame[], viewIdx: number } | null>(null)

/** True while queue hash / assets changed and buildAssetIndex is in flight. */
const queueAssetResyncInProgress = ref(false)
/** Block stale worker presentation callbacks only during `setStateImmediate`, not during bootstrap tick. */
let suppressRegistrationPresentationDuringSync = 0
let registrationResyncInFlight: Promise<void> | null = null
/** Bumped when a registration resync starts/ends so a timed-out body cannot mutate after `finally` (#1362). */
let registrationResyncGeneration = 0
/** Keep prior pixels on screen until registration bootstrap commits (queue may already be new). */
const registrationTransitionHoldActive = ref(false)
/** Tenant-tunable dissolve length; 0 when off / soft-defer / registration rematch hold. */
const presentationCrossfadeMs = computed(() =>
  resolvePresentationCrossfadeMs(
    state.tenant?.playerCreativeCrossfadeMs,
    isPresentationCrossfadeEnabled(
      state.tenant?.playerCreativeCrossfade,
      state.capacityTier,
      state.tenant?.playerCreativeCrossfadeOnLowCapacity,
      state.pendingPlaylistSwap != null || registrationTransitionHoldActive.value
    )
  )
)
let resolveRegistrationBootstrapCommit: (() => void) | null = null
/** Ignore frame-boundary deferred schedule flush right after registration bootstrap (avoids double swap). */
let suppressDeferredRegistrationBoundaryUntil = 0
/** Skip redundant worker `setState` pushes immediately after registration resync. */
let lastRegistrationResyncEndedAt = 0

function waitForRegistrationBootstrapCommit(): Promise<void> {
  return new Promise<void>((resolve) => {
    resolveRegistrationBootstrapCommit = resolve
  })
}

function fulfillRegistrationBootstrapCommit(): void {
  if (resolveRegistrationBootstrapCommit != null) {
    resolveRegistrationBootstrapCommit()
    resolveRegistrationBootstrapCommit = null
  }
}

function releaseRegistrationTransitionHold(): void {
  clearContentTransitionHoldOwnership(contentTransitionHoldOwnership)
  registrationTransitionHoldActive.value = false
}

function matchPlaybackSegmentInPlan(
  seg: PlaybackSegment,
  plan: PlaybackSegment[]
): { frameIdx: number, matchTier: string } | null {
  const seq = seg.playlistSeqNo ?? 0
  const strict = plan.findIndex(
    row =>
      row.frameId === seg.frameId
      && row.playlistUrn === seg.playlistUrn
      && (row.playlistSeqNo ?? 0) === seq
  )
  if (strict >= 0) {
    return { frameIdx: strict, matchTier: 'strict' }
  }
  const urnSeqMatches = plan.filter(
    row => row.playlistUrn === seg.playlistUrn && (row.playlistSeqNo ?? 0) === seq
  )
  if (urnSeqMatches.length === 1) {
    const frameIdx = plan.indexOf(urnSeqMatches[0]!)
    return { frameIdx, matchTier: 'urn-seq' }
  }
  if (urnSeqMatches.length > 1) {
    const byId = urnSeqMatches.find(row => row.frameId === seg.frameId)
    if (byId != null) {
      return { frameIdx: plan.indexOf(byId), matchTier: 'urn-seq-id' }
    }
  }
  return null
}

function matchQueueFrameInPlan(
  frame: IQueueFrame,
  plan: PlaybackSegment[]
): { frameIdx: number, matchTier: string } | null {
  const seq = frame.playlistSeqNo ?? 0
  const strict = plan.findIndex(
    row =>
      row.frameId === frame.id
      && row.playlistUrn === frame.playlistUrn
      && (row.playlistSeqNo ?? 0) === seq
  )
  if (strict >= 0) {
    return { frameIdx: strict, matchTier: 'strict' }
  }
  const urnSeqMatches = plan.filter(
    row => row.playlistUrn === frame.playlistUrn && (row.playlistSeqNo ?? 0) === seq
  )
  if (urnSeqMatches.length === 1) {
    const frameIdx = plan.indexOf(urnSeqMatches[0]!)
    return { frameIdx, matchTier: 'urn-seq' }
  }
  if (urnSeqMatches.length > 1) {
    const byId = urnSeqMatches.find(row => row.frameId === frame.id)
    if (byId != null) {
      return { frameIdx: plan.indexOf(byId), matchTier: 'urn-seq-id' }
    }
  }
  return null
}

function workerPlaybackPlanFromQueue(): PlaybackSegment[] {
  const q = unref(queue) ?? []
  if (q.length === 0) {
    return []
  }
  return queueFramesToWorkerPlaybackPlan(q, unref(campaign)?.playlists)
}

function planSpineHashNow(): string {
  const plan = workerPlaybackPlanFromQueue()
  return plan.length > 0 ? getPlaybackWorkerPayloadSequenceHash(plan) : ''
}

/** Re-apply sessionStorage authority once the materialized queue spine is known (hard reload race). */
function syncPresentationAuthorityFromStorage(): void {
  const spineHash = planSpineHashNow()
  if (spineHash === '') {
    return
  }
  if (shouldSkipPresentationStorageRestore()) {
    return
  }
  const authorityBefore = playbackPresentationAuthorityFromSession()
  restorePlaybackPresentationSessionFromStorage(spineHash)
  const authorityAfter = playbackPresentationAuthorityFromSession()
  if (authorityAfter >= 0 && authorityAfter > lastPresentationNotifiedIdx.value) {
    lastPresentationNotifiedIdx.value = authorityAfter
  }
  const bootstrapPending = isInitWorkerBootstrapPending()
  const queueLen = (unref(queue) ?? []).length
  const didSync = authorityAfter > authorityBefore && queueLen > 0 && !bootstrapPending
  if (didSync) {
    void playbackScheduler.syncResumePlanIdx(authorityAfter).catch(() => {})
    void syncPlaybackWorkerStateNow(state.platform, { immediate: true })
  }
}

function findOnPlanIdxForVisibleFrame(plan: PlaybackSegment[], visFrame: IQueueFrame): number {
  const strict = plan.findIndex(
    seg =>
      seg.frameId === visFrame.id
      && (seg.playlistSeqNo ?? 0) === (visFrame.playlistSeqNo ?? 0)
  )
  if (strict >= 0) {
    return strict
  }
  if (playbackPlanHasBookCps(plan)) {
    return plan.findIndex(seg => seg.frameId === visFrame.id)
  }
  return -1
}

/** Commit visible surface at restored plan index after hard-reload remount. */
async function commitRemountPlaybackSurfaceAtPlanIdx(planIdx: number): Promise<void> {
  const plan = workerPlaybackPlanFromQueue()
  const q = unref(queue) ?? []
  if (planIdx < 0 || planIdx >= plan.length || q.length === 0) {
    return
  }
  let frames: IQueueFrame[] = [q[planIdx]!]
  const preloadPlanIdx = resolvePresentationPreloadPlanIdx(plan, planIdx)
  if (preloadPlanIdx != null && preloadPlanIdx >= 0 && preloadPlanIdx < q.length) {
    frames = [q[planIdx]!, q[preloadPlanIdx]!]
  }
  let viewIdx = 0
  if (playbackBufferFramesMissingAssetIndex(frames)) {
    await limiter.schedule(buildAssetIndex)
  }
  const bound = bindPresentationViewToQueueNotifiedIdx(frames, viewIdx, planIdx, q)
  frames = bound.frames
  viewIdx = bound.viewIdx
  if (frames.length === 0) {
    return
  }
  const portalBookOpts = planIdx >= 0
    ? { portalBookPlanIdx: planIdx }
    : undefined
  if (playbackPlanHasBookCps(plan)) {
    portalBookAllowVisibleSurfaceAdvance = true
  }
  try {
    commitPlaybackSurface(frames, viewIdx, portalBookOpts)
    flushPendingPlaybackSurfaceCommit()
    if (playbackPlanHasBookCps(plan)) {
      await recordPortalBookPresentationAfterVisibleCommit(plan, planIdx, frames, viewIdx)
    }
    else {
      await recordMainPresentationAfterVisibleCommit(plan, planIdx, frames, viewIdx)
    }
  }
  finally {
    portalBookAllowVisibleSurfaceAdvance = false
  }
}

/** Worker sync resume index: portal book uses on-air plan index; else notified idx or surface mapping. */
function resolveResumePlanIdxForWorkerSync(): number | null {
  const plan = workerPlaybackPlanFromQueue()
  const q = unref(queue) ?? []
  const onAir = playbackSurface.value.frames[playbackSurface.value.viewIdx]
  let resolved: number | null = null
  if (plan.length > 0 && onAir != null) {
    if (playbackPlanHasBookCps(plan) && q.length > 0) {
      const qIdx = queueIndexForPresentationFrame(q, onAir)
      if (
        qIdx >= 0
        && qIdx < plan.length
        && plan[qIdx]?.frameId === onAir.id
        && (plan[qIdx]?.playlistSeqNo ?? 0) === (onAir.playlistSeqNo ?? 0)
      ) {
        resolved = qIdx
      }
    }
    if (resolved == null) {
      const onPlanIdx = findOnPlanIdxForVisibleFrame(plan, onAir)
      if (onPlanIdx >= 0) {
        if (playbackPlanHasBookCps(plan)) {
          resolved = onPlanIdx
        }
        else {
          resolved = onPlanIdx
        }
      }
    }
  }
  if (resolved == null) {
    const notifiedIdx = lastPresentationNotifiedIdx.value
    if (notifiedIdx >= 0) {
      resolved = notifiedIdx
    }
  }
  if (resolved == null && getPlaybackPresentationSession().lastLoggedPlanIdx >= 0) {
    resolved = getPlaybackPresentationSession().lastLoggedPlanIdx
  }
  if (resolved == null && plan.length > 0) {
    const hold = lastNonEmptyPlaybackSurface.value ?? playbackSurface.value
    if (hold.frames.length > 0) {
      const fromHold = resolveWorkerFrameIndexForVisibleSurface(hold)
      if (fromHold.frameIdx >= 0 && fromHold.frameIdx < plan.length) {
        resolved = fromHold.frameIdx
      }
    }
  }
  if (resolved == null) {
    return null
  }
  if (playbackPlanHasBookCps(plan) && plan.length > 1) {
    const authority = portalBookPresentationAuthority()
    if (
      authority >= 2
      && isSpuriousPortalBookMidLoopHouseReset(authority, resolved, plan.length)
    ) {
      return authority
    }
  }
  return resolved
}

function resolveWorkerFrameIndexForVisibleSurface(
  hold: { frames: IQueueFrame[], viewIdx: number },
  opts?: { priorLoop?: { notifiedIdx: number, segments: PlaybackSegment[] } }
): {
  frameIdx: number
  matchTier: string
} {
  const plan = workerPlaybackPlanFromQueue()
  if (plan.length === 0) {
    return { frameIdx: 0, matchTier: 'no-plan' }
  }
  if (hold.frames.length === 0) {
    return { frameIdx: 0, matchTier: 'empty-hold' }
  }
  const prior = opts?.priorLoop
  if (prior != null && prior.segments.length > 0 && prior.notifiedIdx >= 0 && prior.notifiedIdx < prior.segments.length) {
    const onAir = prior.segments[prior.notifiedIdx]!
    const fromPrior = matchPlaybackSegmentInPlan(onAir, plan)
    if (fromPrior != null) {
      return { frameIdx: fromPrior.frameIdx, matchTier: `prior-notified-${fromPrior.matchTier}` }
    }
    /** On-air playlist was removed from the DL — advance along the old loop, not the hold buffer (often the "next" slot). */
    for (let step = 1; step < prior.segments.length; step++) {
      const idx = (prior.notifiedIdx + step) % prior.segments.length
      const candidate = prior.segments[idx]!
      const afterRemoved = matchPlaybackSegmentInPlan(candidate, plan)
      if (afterRemoved != null) {
        return { frameIdx: afterRemoved.frameIdx, matchTier: `after-removed-${afterRemoved.matchTier}` }
      }
    }
  }
  const bufferIdx = Math.min(Math.max(0, hold.viewIdx), hold.frames.length - 1)
  const visible = hold.frames[bufferIdx]
  if (visible != null) {
    const fromVisible = matchQueueFrameInPlan(visible, plan)
    if (fromVisible != null) {
      return fromVisible
    }
    const otherIdx = bufferIdx === 0 ? 1 : 0
    const other = hold.frames[otherIdx]
    if (other != null) {
      const fromOther = matchQueueFrameInPlan(other, plan)
      if (fromOther != null) {
        return { frameIdx: fromOther.frameIdx, matchTier: `other-buffer-${fromOther.matchTier}` }
      }
    }
  }
  const masterIdx = plan.findIndex(row => playlistUrnIsPlayerMasterPlaylist(row.playlistUrn))
  if (masterIdx >= 0) {
    return { frameIdx: masterIdx, matchTier: 'master-prepend' }
  }
  /**
   * No shared identity with the prior loop / hold (e.g. Studio assigned a wholly different
   * playlist). Do not clamp `prior.notifiedIdx` into the new plan — that resumes mid-playlist
   * and desyncs timer vs surface. Start at 0.
   */
  return { frameIdx: 0, matchTier: 'full-replace-no-overlap' }
}

/** Point hold at the worker on-air slot (buffer viewIdx is often the "next" slot, not the playing frame). */
function alignHoldViewToPriorLoopOnAir(
  hold: { frames: IQueueFrame[], viewIdx: number },
  priorLoop: { notifiedIdx: number, segments: PlaybackSegment[] }
): { frames: IQueueFrame[], viewIdx: number } {
  if (hold.frames.length === 0 || priorLoop.segments.length === 0 || priorLoop.notifiedIdx < 0) {
    return hold
  }
  const onAir = priorLoop.segments[priorLoop.notifiedIdx]!
  for (let i = 0; i < hold.frames.length; i++) {
    const f = hold.frames[i]
    if (
      f != null
      && f.playlistUrn === onAir.playlistUrn
      && (f.playlistSeqNo ?? 0) === (onAir.playlistSeqNo ?? 0)
      && f.id === onAir.frameId
    ) {
      return { frames: hold.frames, viewIdx: i }
    }
  }
  return hold
}

/** Skip main-thread repaint when bootstrap keeps the same playlist spine slot on screen (worker clock still rephases). */
function shouldSilentRegistrationBootstrap(
  hold: { frames: IQueueFrame[], viewIdx: number },
  resolved: { frameIdx: number, matchTier: string }
): boolean {
  const plan = workerPlaybackPlanFromQueue()
  const planSeg = plan[resolved.frameIdx]
  if (planSeg == null) {
    return false
  }
  const bufferIdx = Math.min(Math.max(0, hold.viewIdx), hold.frames.length - 1)
  const holdVis = hold.frames[bufferIdx]
  if (holdVis == null) {
    return false
  }
  return (
    holdVis.playlistUrn === planSeg.playlistUrn
    && (holdVis.playlistSeqNo ?? 0) === (planSeg.playlistSeqNo ?? 0)
  )
}

/**
 * Single registration-status worker sync (called from usePlayer after materialization, not from queue watch).
 * Stops the ticker, pushes the new plan, bootstraps the visible frame with a full segment duration, then resumes.
 * Bounded by {@link REGISTRATION_PLAYBACK_RESYNC_MAX_MS} so a hung asset rebuild / worker replace cannot latch
 * `queueAssetResyncInProgress` forever and disarm empty-surface recovery (#1362).
 * A generation token invalidates the body if the deadline wins, so late awaits cannot re-stop the ticker
 * or re-assert the transition hold after `finally` cleared them.
 */
async function applyRegistrationPlaybackResync(): Promise<void> {
  if (registrationResyncInFlight != null) {
    await registrationResyncInFlight
    return
  }
  registrationResyncInFlight = (async () => {
    const generation = ++registrationResyncGeneration
    queueAssetResyncInProgress.value = true
    invalidatePlaybackWorkerSyncKey()
    debouncedSetQueueUpdateState.cancel()
    const deadline = registrationPlaybackResyncDeadline(REGISTRATION_PLAYBACK_RESYNC_MAX_MS)
    try {
      await Promise.race([
        runRegistrationPlaybackResyncBody(generation),
        deadline.promise
      ])
    }
    catch (err) {
      console.warn('[VBuffer] registration playback resync aborted', err)
    }
    finally {
      /** Invalidate before clearing flags so a zombie body cannot re-latch hold / stop the ticker. */
      if (registrationResyncGeneration === generation) {
        registrationResyncGeneration++
      }
      deadline.cancel()
      requestAnimationFrame(() => {
        releaseRegistrationTransitionHold()
      })
      queueAssetResyncInProgress.value = false
      playbackScheduler.startTicker()
      registrationResyncInFlight = null
      lastRegistrationResyncEndedAt = performance.now()
      suppressDeferredRegistrationBoundaryUntil = performance.now() + 2000
      syncWebPlaybackTick()
    }
  })()
  await registrationResyncInFlight
}

async function runRegistrationPlaybackResyncBody(generation: number): Promise<void> {
  const alive = (): boolean => registrationResyncGeneration === generation
  /**
   * Latch suppress through rematch sync-commit (not only around setState). Dropping it after
   * worker rematch let stale presentation commits through while the bootstrap waiter keeps
   * the transition-hold gate open (#1561 Bugbot).
   */
  let suppressArmed = false
  try {
    const playbackAssets = getPlaybackAssets()
    const assetsUnchanged
      = lastBuiltCampaignAssets != null
        && areCampaignAssetListsEquivalent(lastBuiltCampaignAssets, playbackAssets)
    const priorLoop = await playbackScheduler.getLoopStateForTransition()
    if (!alive()) {
      return
    }
    const rawHold = lastNonEmptyPlaybackSurface.value ?? playbackSurface.value
    const hold = alignHoldViewToPriorLoopOnAir(rawHold, priorLoop)
    const resolved = resolveWorkerFrameIndexForVisibleSurface(hold, { priorLoop })
    if (!alive()) {
      return
    }
    const isFullReplace = isFullPlaylistReplaceMatchTier(resolved.matchTier)
    registrationTransitionHoldActive.value = true
    if (isFullReplace) {
      /**
       * Studio assigned a wholly different playlist. Keep the outgoing campaign on-air until
       * rematch sync-commit paints frame 0 — emptying early caused loading-cube flashes and
       * left hybrid onEnded looping the last URI while assets rebuilt (#1632).
       * Opaque suppress still blocks stale presentation commits during rebuild (#1561).
       */
      pendingPlaybackSurfaceCommit = null
      resetPendingCommitRetryState()
      clearStickyOutgoingHold('supersede')
      /**
       * Arm suppress before native prepare / asset rebuild. While the bootstrap waiter is open
       * the transition-hold gate allows commits through; old-plan ticks otherwise reseed
       * lastNonEmpty during the rebuild await (#1561).
       */
      if (shouldSuppressPresentationDuringRegistrationAssetRebuild(resolved.matchTier)) {
        suppressRegistrationPresentationDuringSync++
        suppressArmed = true
      }
      prepareNativeHybridForFullPlaylistReplace()
      if (!alive()) {
        return
      }
    }
    else if (hold.frames.length > 0) {
      lastNonEmptyPlaybackSurface.value = { frames: [...hold.frames], viewIdx: hold.viewIdx }
    }
    const bootstrapCommitDone = waitForRegistrationBootstrapCommit()
    if (!assetsUnchanged) {
      await limiter.schedule(buildAssetIndex)
      if (!alive()) {
        return
      }
    }
    if (!alive()) {
      return
    }
    /** Stop ticks only when the worker sync is about to run — keep advancing during asset rebuild (#1236). */
    stopWebTick()
    await playbackScheduler.stopTicker()
    if (!alive()) {
      return
    }
    if (!suppressArmed) {
      suppressRegistrationPresentationDuringSync++
      suppressArmed = true
    }
    let didWorkerRematch = false
    if (!alive()) {
      return
    }
    await syncPlaybackWorkerStateNow(undefined, { immediate: true, registrationScheduleReplace: true })
    didWorkerRematch = true
    /**
     * Rematch completed with `notifyPresentation: false`. Align authority + paint even when the
     * resync generation was invalidated mid-flight — skipping leaves presentation≫worker (#1561).
     * Keep suppress latched until after sync-commit so partial rematch cannot re-skew (#1561 Bugbot).
     */
    if (!shouldForcePresentationAlignAfterWorkerRematch(didWorkerRematch)) {
      return
    }
    const perf = performance.now()
    const planAfter = workerPlaybackPlanFromQueue()
    const rematchIdx = Math.min(
      Math.max(0, resolved.frameIdx),
      Math.max(0, planAfter.length - 1)
    )
    const rematchFrame = planAfter[rematchIdx]
    forceSetPresentationPlanIdx(
      rematchIdx,
      rematchFrame?.frameId ?? null,
      planAfter.length > 0 ? getPlaybackWorkerPayloadSequenceHash(planAfter) : null
    )
    lastPresentationNotifiedIdx.value = rematchIdx
    const silentBootstrap = !isFullReplace
      && hold.frames.length > 0
      && shouldSilentRegistrationBootstrap(hold, {
        frameIdx: rematchIdx,
        matchTier: resolved.matchTier
      })
    /** Clock already rematched in the worker; rephase only — paint is sync-committed below (#1561). */
    playbackScheduler.bootstrapPresentationAfterScheduleReplace(
      perf,
      rematchIdx,
      undefined,
      { notifyPresentation: false }
    )
    if (silentBootstrap) {
      const loop = await playbackScheduler.getLoopStateForTransition()
      const idx = rematchIdx
      const nextIdx = loop.segments.length > 0 ? (idx + 1) % loop.segments.length : 0
      lastWorkerBufferSlim.value = loop.segments.length > 0
        ? [loop.segments[idx]!, loop.segments[nextIdx]!].filter((row): row is PlaybackSegment => row != null)
        : []
      fulfillRegistrationBootstrapCommit()
    }
    else if (shouldSyncCommitRegistrationRematchPresentation({ silentBootstrap })) {
      /**
       * Sync-commit under suppress — listener notify would no-op and never paint / fulfill (#1561).
       */
      await commitRemountPlaybackSurfaceAtPlanIdx(rematchIdx)
      if (resolveRegistrationBootstrapCommit != null && playbackSurface.value.frames.length > 0) {
        fulfillRegistrationBootstrapCommit()
      }
    }
    const q = unref(queue) ?? []
    lastFlushedQueueHash = q.length > 0 ? getQueueHash(q) : HASH_EMPTY
    if (!alive()) {
      return
    }
    await Promise.race([
      bootstrapCommitDone,
      new Promise<void>((resolve) => {
        setTimeout(resolve, 2500)
      })
    ])
  }
  finally {
    if (suppressArmed) {
      suppressRegistrationPresentationDuringSync--
    }
  }
}

function tryApplyCommittedPlaybackSurface(frames: IQueueFrame[], viewIdx: number): boolean {
  if (shouldHoldSurfaceAgainstEmptyWorkerPaint(frames)) {
    return false
  }
  const visIdx = Math.min(Math.max(0, viewIdx), Math.max(0, frames.length - 1))
  const nextVis = frames[visIdx]
  const cur = playbackSurface.value
  const curVis = cur.viewIdx >= 0 && cur.viewIdx < cur.frames.length ? cur.frames[cur.viewIdx] : undefined
  const q = unref(queue) ?? []
  if (
    nextVis != null
    && curVis != null
    && queueLooksLikePortalBook(q)
    && playbackPlanHasBookCps(workerPlaybackPlanFromQueue())
    && cur.viewIdx === visIdx
    && nextVis.id === curVis.id
    && nextVis.playlistUrn === curVis.playlistUrn
    && (nextVis.playlistSeqNo ?? 0) === (curVis.playlistSeqNo ?? 0)
  ) {
    const sameBufferKeys = cur.frames.map(f => queueRowKey(f)).join('|') === frames.map(f => queueRowKey(f)).join('|')
    if (sameBufferKeys) {
      return false
    }
    /** Facet/metadata refresh: update hidden preload slot only — remounting on-air HF3 restarts the video (double play). */
    if (cur.frames.length === frames.length && cur.frames.length <= 2) {
      const merged = frames.map((f, i) => (i === visIdx ? cur.frames[visIdx]! : f))
      playbackSurface.value = { frames: merged, viewIdx: visIdx }
      lastNonEmptyPlaybackSurface.value = { frames: [...merged], viewIdx: visIdx }
      if (
        !state.firstPlaybackFramePainted
        && shouldMarkBootPaintFromSurfaceApply(
          nativePlaybackSurfaceEnabled.value && frameMainIsNativeVideo(cur.frames[visIdx])
        )
      ) {
        state.firstPlaybackFramePainted = true
      }
      return true
    }
  }
  if (frames.length > 0) {
    lastNonEmptyPlaybackSurface.value = { frames: [...frames], viewIdx }
    if (
      !state.firstPlaybackFramePainted
      && shouldMarkBootPaintFromSurfaceApply(
        nativePlaybackSurfaceEnabled.value && frameMainIsNativeVideo(nextVis)
      )
    ) {
      state.firstPlaybackFramePainted = true
    }
  }
  else if ((unref(queue) ?? []).length === 0) {
    lastNonEmptyPlaybackSurface.value = null
  }
  playbackSurface.value = { frames, viewIdx }
  return true
}

/** Hold prior pixels only when we have a last surface to show during worker/queue resync gaps. */
function shouldHoldSurfaceAgainstEmptyWorkerPaint(frames: IQueueFrame[]): boolean {
  if (frames.length > 0) {
    return false
  }
  const q = unref(queue) ?? []
  if (q.length === 0) {
    return false
  }
  const hold = lastNonEmptyPlaybackSurface.value
  return hold != null && hold.frames.length > 0
}

/** Deferred until {@link buildAssetIndex} has entries for the buffer’s main facet URNs (avoids SECTION UNAVAILABLE on reload). */
let pendingPlaybackSurfaceCommit: {
  frames: IQueueFrame[]
  viewIdx: number
  portalBookPlanIdx?: number
} | null = null
/** Set when a presentation commit waits on an in-flight {@link buildAssetIndex} from {@link flushQueueAssetsWatch}. */
let pendingFlushAfterAssetBuild = false
/** Avoid re-entrant worker resync when presentation resolves to zero buffer rows while queue is loaded. */
let emptyBufferWorkerResyncInFlight = false
let pendingCommitRetryTimer: ReturnType<typeof setTimeout> | null = null
/** Successful schedulePendingCommitRetry arms since the current pending commit was set. */
let pendingCommitRetryAttempts = 0
let emptySurfaceEmptySinceMs: number | null = null
let emptySurfaceRecoveryInFlight = false
let emptySurfaceLastRecoveryAtMs = 0
/** Consecutive recoveries that left the surface empty; resets on first paint. */
let emptySurfaceRecoveryAttempts = 0
/** After {@link EMPTY_PLAYBACK_SURFACE_MAX_RECOVERY_ATTEMPTS}, stop silent recovery loops (#1362). */
let emptySurfaceRecoveryExhausted = false
let emptySurfaceStallPollTimer: ReturnType<typeof setInterval> | null = null
/** Always-on poll for painted-but-stuck content (#1382 / #1384). */
let contentNotAdvancingPollTimer: ReturnType<typeof setInterval> | null = null
/** Epoch ms of the last playlist loop wrap; 0 when none observed yet. */
let lastLoopWrapAtMs = 0
/** Whether that wrap swapped in different content (false = pool has a single eligible creative). */
let lastLoopWrapAdvancedContent = false
/**
 * Painted-frame dwell clock for `content_not_advancing` / heartbeat `lastFrameTransitionAt`.
 * Must not share {@link visibleFrameStartMs} (overlay open + currentFrame null flicker reset that).
 */
let paintedVisibleFrameChangedAtMs = 0
let lastPaintedVisibleFrameId: string | null = null
/** Consecutive wall-dwell escapes that left paint on the same frame (#1551 / PLAYER-1J). */
let wallDwellEscapeSamePaint = resetWallDwellEscapeSamePaintCounter()
let wallDwellEscapeRemountInFlight = false
/** Wall clock of the previous dwell poll; a jump here means the player was suspended, not stuck. */
let lastContentNotAdvancingPollWallMs = 0
let contentNotAdvancingRecoveryInFlight = false
let contentNotAdvancingLastRecoveryAtMs = 0
const CONTENT_NOT_ADVANCING_RECOVERY_COOLDOWN_MS = 15_000
/** performance.now() when `queueAssetResyncInProgress` last went true; null when false. */
let queueAssetResyncSinceMs: number | null = null
/** performance.now() when video is expected but no paint-ready video; null when healthy / N/A. */
let videoPaintStallSinceMs: number | null = null

watch(queueAssetResyncInProgress, (inProgress) => {
  queueAssetResyncSinceMs = inProgress ? performance.now() : null
})

function playbackBufferFramesMissingAssetIndex(frames: IQueueFrame[]): boolean {
  return bufferFramesMissingAssetIndex(frames, unref(assetIndex))
}

/**
 * Seed the empty-surface window as soon as a commit is deferred, so the first paint knows how long
 * the cube was up without waiting for {@link pollEmptyPlaybackSurfaceStall} to sample it.
 */
function noteEmptyPlaybackSurfaceWindowStart(): void {
  if (emptySurfaceEmptySinceMs == null && surfaceForRender.value.frames.length === 0) {
    emptySurfaceEmptySinceMs = performance.now()
  }
}

/** Cube time before this commit, or null when pixels were already on screen. */
function emptyPlaybackSurfaceElapsedMs(): number | null {
  if (surfaceForRender.value.frames.length > 0 || emptySurfaceEmptySinceMs == null) {
    return null
  }
  return performance.now() - emptySurfaceEmptySinceMs
}

function frameMainExpectsVideo(frame: IQueueFrame | null | undefined): boolean {
  return frameMainExpectsVideoFromFacets(frame, frameMainMediaKind(frame))
}

/** Stall while surface has frames expecting video but no paint-ready video element. */
function videoPaintStallElapsedMs(opts: {
  expectsVideo: boolean
  surfaceFrameCount: number
  paintReady: boolean
}): number | null {
  if (!opts.expectsVideo || opts.surfaceFrameCount < 1 || opts.paintReady) {
    videoPaintStallSinceMs = null
    return null
  }
  const now = performance.now()
  if (videoPaintStallSinceMs == null) {
    videoPaintStallSinceMs = now
  }
  return now - videoPaintStallSinceMs
}

function clearPendingCommitRetryTimer(): void {
  if (pendingCommitRetryTimer != null) {
    clearTimeout(pendingCommitRetryTimer)
    pendingCommitRetryTimer = null
  }
}

function resetPendingCommitRetryState(): void {
  clearPendingCommitRetryTimer()
  pendingCommitRetryAttempts = 0
}

function clearUnsatisfiablePendingSurfaceCommit(reason: string): boolean {
  const pending = pendingPlaybackSurfaceCommit
  if (pending == null) {
    return false
  }
  const playbackAssetUrns = new Set(
    getPlaybackAssets().map((a) => {
      const row = a as { urn?: unknown, assetUrn?: unknown }
      return typeof row.urn === 'string'
        ? row.urn
        : typeof row.assetUrn === 'string' ? row.assetUrn : ''
    }).filter(u => u !== '')
  )
  const scheduleRoot = getPlaybackScheduleRootEffective() ?? unref(campaign) ?? null
  const scheduleAssetUrns = collectScheduleRootAssetUrns(scheduleRoot)
  const incomplete = pendingSurfaceCommitUrnsIncompleteMaterialization({
    pendingFrames: pending.frames,
    playbackAssetUrns,
    scheduleAssetUrns
  })
  if (incomplete.length > 0) {
    const assetReloadInFlight = isPlaybackAssetReloadInFlight()
    const forceReload = shouldForceReloadIncompleteCampaignAssets({
      incompleteUrns: incomplete,
      assetReloadInFlight,
      assetResyncInProgress: queueAssetResyncInProgress.value
    })
    /** Incomplete `fetchAssets` / stuck memory list — rematerialize; do not treat as #1352 (#1574). */
    console.warn('[VBuffer] pending surface commit waiting on incomplete campaign asset list', {
      reason,
      incompleteCount: incomplete.length,
      incompleteMaterialization: incomplete,
      forceReload,
      assetReloadInFlight,
      assetResyncInProgress: queueAssetResyncInProgress.value
    })
    reportIncompleteCampaignAssetRematerializeToSentry({
      reason,
      incompleteUrns: incomplete
    })
    if (!forceReload) {
      /**
       * Do not recurse into {@link schedulePendingCommitRetry} here (it calls abandon → this
       * function). Arm after the stack unwinds so we re-check once the in-flight fetch/resync
       * settles — callers that already schedule a retry are no-ops via the timer latch.
       */
      queueMicrotask(() => {
        if (pendingPlaybackSurfaceCommit != null) {
          schedulePendingCommitRetry()
        }
      })
      return false
    }
    emptySurfaceRecoveryAttempts = 0
    emptySurfaceRecoveryExhausted = false
    /**
     * Soft-deferred playlist swaps leave the old campaign on-air; rematerialize thrash can starve the
     * frame-id boundary for minutes. Flush any pending registration schedule first (#1577).
     */
    if (performance.now() >= suppressDeferredRegistrationBoundaryUntil) {
      notifyDeferredPlaybackScheduleFrameBoundary()
    }
    reloadPlaybackAssetsForce()
    return false
  }
  clearIncompleteCampaignAssetRematerializeSentryDedupe()
  if (!pendingSurfaceCommitUrnsUnsatisfiable({
    pendingFrames: pending.frames,
    playbackAssetUrns,
    scheduleAssetUrns
  })) {
    return false
  }
  console.warn('[VBuffer] abandoning pending surface commit — referenced assets left the campaign list', {
    reason,
    missingFromCampaign: pending.frames.flatMap(f =>
      Object.values(f.facets ?? {})
        .map(facet => (facet && typeof facet.asset === 'string' ? facet.asset : null))
        .filter((u): u is string => u != null && !playbackAssetUrns.has(u))
    )
  })
  pendingPlaybackSurfaceCommit = null
  resetPendingCommitRetryState()
  return true
}

/**
 * Drop an unsatisfiable pending commit and recover paint. Stale surface frames may still be
 * mounted after their blob URLs were revoked — clear them so empty-surface recovery can run (#1352).
 */
function abandonUnsatisfiablePendingSurfaceCommit(reason: string): boolean {
  if (!clearUnsatisfiablePendingSurfaceCommit(reason)) {
    return false
  }
  if (playbackBufferFramesMissingAssetIndex(surfaceForRender.value.frames)) {
    lastNonEmptyPlaybackSurface.value = null
    playbackSurface.value = { frames: [], viewIdx: -1 }
  }
  void recoverEmptyPlaybackSurface()
  return true
}

/** When assets are still downloading after a build, retry instead of leaving the cube forever. */
function schedulePendingCommitRetry(): void {
  if (pendingCommitRetryTimer != null || pendingPlaybackSurfaceCommit == null) {
    return
  }
  if (abandonUnsatisfiablePendingSurfaceCommit('before-retry')) {
    return
  }
  if (!shouldSchedulePendingCommitRetry({
    attemptCount: pendingCommitRetryAttempts,
    maxAttempts: PENDING_SURFACE_COMMIT_MAX_RETRIES
  })) {
    // Keep waiting (asset index / presentation restash). Force-commit without entries flashes SECTION UNAVAILABLE.
    console.warn(
      '[VBuffer] pending surface commit retry budget exhausted; continuing to wait',
      { attempts: pendingCommitRetryAttempts }
    )
    pendingCommitRetryAttempts = 0
  }
  pendingCommitRetryTimer = setTimeout(() => {
    pendingCommitRetryTimer = null
    const pending = pendingPlaybackSurfaceCommit
    if (pending == null) {
      return
    }
    if (abandonUnsatisfiablePendingSurfaceCommit('retry-tick')) {
      return
    }
    if (!playbackBufferFramesMissingAssetIndex(pending.frames)) {
      flushPendingPlaybackSurfaceCommit()
      return
    }
    pendingCommitRetryAttempts += 1
    scheduleAssetIndexBuildForPendingSurface()
  }, PENDING_SURFACE_COMMIT_RETRY_MS)
}

function flushPendingPlaybackSurfaceCommit(): void {
  const pending = pendingPlaybackSurfaceCommit
  if (pending == null) {
    resetPendingCommitRetryState()
    return
  }
  if (playbackBufferFramesMissingAssetIndex(pending.frames)) {
    if (abandonUnsatisfiablePendingSurfaceCommit('flush')) {
      return
    }
    schedulePendingCommitRetry()
    return
  }
  const presentationPlanIdx = pending.portalBookPlanIdx
  pendingPlaybackSurfaceCommit = null
  commitPlaybackSurfaceNow(pending.frames, pending.viewIdx, presentationPlanIdx)

  const hasPresentationPlanIdx = presentationPlanIdx != null && presentationPlanIdx >= 0
  const plan = hasPresentationPlanIdx ? workerPlaybackPlanFromQueue() : []
  const isPortalBook = playbackPlanHasBookCps(plan)
  if (
    hasPresentationPlanIdx
    && (!isPortalBook || portalBookAllowVisibleSurfaceAdvance)
    && shouldRestashFailedPresentationCommit({
      expectedFrames: pending.frames,
      expectedViewIdx: pending.viewIdx,
      surfaceFrames: playbackSurface.value.frames,
      surfaceViewIdx: playbackSurface.value.viewIdx
    })
  ) {
    pendingPlaybackSurfaceCommit = pending
    /**
     * Restash retries skip the asset-miss increment in {@link schedulePendingCommitRetry}'s
     * timer tick — burn budget here so a permanently rejected apply cannot loop forever.
     * Clear any armed timer first so schedule actually re-arms (and the increment is not wasted).
     */
    clearPendingCommitRetryTimer()
    pendingCommitRetryAttempts += 1
    schedulePendingCommitRetry()
    return
  }

  resetPendingCommitRetryState()
  if (presentationPlanIdx == null || presentationPlanIdx < 0) {
    return
  }
  if (isPortalBook) {
    if (portalBookAllowVisibleSurfaceAdvance) {
      void recordPortalBookPresentationAfterVisibleCommit(
        plan,
        presentationPlanIdx,
        pending.frames,
        pending.viewIdx
      )
    }
    return
  }
  void recordMainPresentationAfterVisibleCommit(
    plan,
    presentationPlanIdx,
    pending.frames,
    pending.viewIdx
  )
}

function pinPortalBookVisibleForWorkerOnlyCommit(
  frames: IQueueFrame[],
  viewIdx: number
): { frames: IQueueFrame[], viewIdx: number } {
  const plan = workerPlaybackPlanFromQueue()
  if (!playbackPlanHasBookCps(plan) || portalBookAllowVisibleSurfaceAdvance) {
    return { frames, viewIdx }
  }
  const cur = playbackSurface.value
  const curVisIdx = Math.min(Math.max(0, cur.viewIdx), Math.max(0, cur.frames.length - 1))
  const curVisible = cur.frames[curVisIdx]
  const visIdx = Math.min(Math.max(0, viewIdx), Math.max(0, frames.length - 1))
  const nextVisible = frames[visIdx]
  if (curVisible == null || nextVisible == null) {
    return { frames, viewIdx }
  }
  const sameOnAir = curVisible.id === nextVisible.id
    && curVisible.playlistUrn === nextVisible.playlistUrn
    && (curVisible.playlistSeqNo ?? 0) === (nextVisible.playlistSeqNo ?? 0)
  if (sameOnAir) {
    return { frames, viewIdx }
  }
  if (cur.frames.length === frames.length && frames.length > 0 && frames.length <= 2) {
    const merged = frames.map((f, i) => (i === curVisIdx ? cur.frames[curVisIdx]! : f))
    return { frames: merged, viewIdx: curVisIdx }
  }
  return { frames: cur.frames, viewIdx: curVisIdx }
}

function scheduleAssetIndexBuildForPendingSurface(): void {
  if (queueAssetResyncInProgress.value) {
    pendingFlushAfterAssetBuild = true
    return
  }
  queueAssetResyncInProgress.value = true
  void limiter.schedule(buildAssetIndex).then(() => {
    queueAssetResyncInProgress.value = false
    flushPendingPlaybackSurfaceCommit()
  }).catch((err) => {
    queueAssetResyncInProgress.value = false
    console.error('error while building asset index for pending surface...', err)
    schedulePendingCommitRetry()
  })
}

/**
 * Queue is ready but the paint surface stayed empty (cube / black) past the stall budget —
 * rebuild the asset index, flush any pending commit, resync the worker, then bootstrap at elapsed.
 * @returns Outcome for the empty-surface attempt counter — `deferred` / `skipped` do not burn the budget (#1362).
 */
async function recoverEmptyPlaybackSurface(): Promise<'painted' | 'deferred' | 'failed' | 'skipped'> {
  if (emptySurfaceRecoveryInFlight) {
    return 'skipped'
  }
  const q = unref(queue) ?? []
  if (
    q.length === 0
    || surfaceForRender.value.frames.length > 0
    || queueAssetResyncInProgress.value
    || isInitWorkerBootstrapPending()
    || performance.now() < suppressQueueWatchWorkerSyncUntilMs
  ) {
    return 'skipped'
  }
  emptySurfaceRecoveryInFlight = true
  emptySurfaceLastRecoveryAtMs = performance.now()
  try {
    if (!queueAssetResyncInProgress.value) {
      queueAssetResyncInProgress.value = true
      try {
        await limiter.schedule(buildAssetIndex)
      }
      finally {
        queueAssetResyncInProgress.value = false
      }
    }
    flushPendingPlaybackSurfaceCommit()
    if (surfaceForRender.value.frames.length > 0) {
      return 'painted'
    }
    // Still waiting on asset index — do not paint (SECTION UNAVAILABLE flash). Pending retries continue.
    if (pendingPlaybackSurfaceCommit != null) {
      return 'deferred'
    }
    if (
      isInitWorkerBootstrapPending()
      || performance.now() < suppressQueueWatchWorkerSyncUntilMs
    ) {
      return 'deferred'
    }
    try {
      await syncPlaybackWorkerStateNow(state.platform, { immediate: true })
    }
    catch (err) {
      console.error('error while syncing worker for empty-surface recovery...', err)
    }
    flushPendingPlaybackSurfaceCommit()
    if (surfaceForRender.value.frames.length > 0) {
      return 'painted'
    }
    if (pendingPlaybackSurfaceCommit != null) {
      return 'deferred'
    }
    if (
      isInitWorkerBootstrapPending()
      || performance.now() < suppressQueueWatchWorkerSyncUntilMs
    ) {
      return 'deferred'
    }
    const plan = workerPlaybackPlanFromQueue()
    if (plan.length === 0) {
      return 'failed'
    }
    let frameIdx = lastPresentationNotifiedIdx.value
    if (frameIdx < 0 || frameIdx >= plan.length) {
      try {
        const timing = await playbackScheduler.getPlaybackTiming()
        const loopElapsed = timing.playlistElapsedMs
        let acc = 0
        frameIdx = 0
        for (let i = 0; i < plan.length; i++) {
          const durMs = Math.max(0, (plan[i]!.durationSeconds ?? 0) * 1000)
          if (loopElapsed < acc + durMs) {
            frameIdx = i
            break
          }
          acc += durMs
          frameIdx = i
        }
      }
      catch {
        frameIdx = 0
      }
    }
    /**
     * Content pins share `registrationTransitionHoldActive` without a registration bootstrap waiter.
     * While the pin is up, presentation notifies are dropped — bootstrap would no-op and burn the
     * recovery budget. Drop the pin only when nothing is already on screen (#1553).
     */
    if (
      contentTransitionHoldOwnership.owned
      && surfaceForRender.value.frames.length === 0
      && resolveRegistrationBootstrapCommit == null
    ) {
      queueAssetResyncInProgress.value = false
      releaseContentTransitionHoldIfOwned()
    }
    /** Await Comlink RPC so the worker has rephased + notified before we start scoring. */
    await playbackScheduler.bootstrapPresentationAfterScheduleReplace(performance.now(), frameIdx)
    /**
     * Poll paint / pending — do not race {@link portalBookPresentationCommitChain}; it is often
     * already settled while the presentation notify is still crossing Comlink (#1553).
     */
    return settleEmptySurfaceRecoveryBootstrap({
      waitMs: EMPTY_PLAYBACK_SURFACE_RECOVERY_COMMIT_WAIT_MS,
      sample: () => {
        flushPendingPlaybackSurfaceCommit()
        return {
          surfaceFrameCount: surfaceForRender.value.frames.length,
          pendingCommit: pendingPlaybackSurfaceCommit != null
        }
      }
    })
  }
  catch (err) {
    console.error('error while recovering empty playback surface...', err)
    return 'failed'
  }
  finally {
    emptySurfaceRecoveryInFlight = false
  }
}

/**
 * A content pin whose release never ran (asset build that never settles, `requestAnimationFrame` that
 * never fires while hidden) freezes the surface indefinitely and silences every stall detector (#1359).
 * Force release so the screen recovers and the detectors can report the stall on their own.
 */
function forceReleaseOverdueContentTransitionHold(nowMs: number): void {
  if (!isContentTransitionHoldOverdue(contentTransitionHoldOwnership, nowMs)) {
    return
  }
  console.warn('[VBuffer] content transition hold exceeded bound; force releasing', {
    heldMs: Math.round(nowMs - (contentTransitionHoldOwnership.sinceMs ?? nowMs)),
    assetResyncInProgress: queueAssetResyncInProgress.value
  })
  /** A hung build leaves this latched, which would make every later flush refuse to release the pin. */
  queueAssetResyncInProgress.value = false
  releaseContentTransitionHoldIfOwned()
}

/**
 * Defense in depth for #1362: if `queueAssetResyncInProgress` stays true past the guard ceiling
 * (hung rebuild outside registration resync, or a race that failed to clear), unlatch so recovery
 * and other flush paths can run. Registration resync itself is also bounded by
 * {@link REGISTRATION_PLAYBACK_RESYNC_MAX_MS}.
 */
function forceClearOverdueAssetResync(nowMs: number): void {
  if (!queueAssetResyncInProgress.value || queueAssetResyncSinceMs == null) {
    return
  }
  const heldMs = nowMs - queueAssetResyncSinceMs
  if (heldMs < ASSET_RESYNC_RECOVERY_GUARD_MAX_MS) {
    return
  }
  console.warn('[VBuffer] asset resync exceeded recovery guard; force clearing latch', {
    heldMs: Math.round(heldMs)
  })
  queueAssetResyncInProgress.value = false
}

function noteEmptySurfaceRecoveryOutcome(outcome: 'painted' | 'deferred' | 'failed' | 'skipped'): void {
  if (outcome === 'skipped' || outcome === 'deferred') {
    return
  }
  if (outcome === 'painted' || surfaceForRender.value.frames.length > 0) {
    emptySurfaceRecoveryAttempts = 0
    emptySurfaceRecoveryExhausted = false
    return
  }
  emptySurfaceRecoveryAttempts += 1
  if (!shouldEscalateEmptyPlaybackSurfaceRecovery({
    attemptCount: emptySurfaceRecoveryAttempts,
    maxAttempts: EMPTY_PLAYBACK_SURFACE_MAX_RECOVERY_ATTEMPTS
  })) {
    return
  }
  if (emptySurfaceRecoveryExhausted) {
    return
  }
  emptySurfaceRecoveryExhausted = true
  console.warn('[VBuffer] empty surface recovery exhausted; stopping silent retries', {
    attempts: emptySurfaceRecoveryAttempts
  })
  try {
    Sentry.addBreadcrumb({
      category: 'empty_playback_surface',
      message: 'empty_playback_surface_recovery_exhausted',
      level: 'error',
      data: { attempts: emptySurfaceRecoveryAttempts }
    })
  }
  catch {
    /* Sentry not initialized or unavailable */
  }
}

function pollEmptyPlaybackSurfaceStall(): void {
  const qLen = (unref(queue) ?? []).length
  const surfaceLen = surfaceForRender.value.frames.length
  const now = performance.now()
  /** Before the early return below: a held surface is non-empty, so that gate would skip the watchdog. */
  forceReleaseOverdueContentTransitionHold(now)
  forceClearOverdueAssetResync(now)
  if (qLen <= 0 || surfaceLen > 0) {
    emptySurfaceEmptySinceMs = null
    /** Reset on idle or paint so a later distribution-list assign can recover again (#1362). */
    emptySurfaceRecoveryAttempts = 0
    emptySurfaceRecoveryExhausted = false
    return
  }
  if (emptySurfaceEmptySinceMs == null) {
    emptySurfaceEmptySinceMs = now
  }
  const stallMs = now - emptySurfaceEmptySinceMs
  /**
   * Only live diagnostics sampler while the queue is ready and nothing has painted: every other call site
   * needs a painted surface (`shouldPublishPlaybackTimingOverlay`, the preload poll's `frames.length < 2`,
   * or a frame transition). Sample before the recovery gate below so a disarmed recovery — latched
   * `queueAssetResyncInProgress`, cooldown, in-flight — still reports `empty_playback_surface`.
   */
  updatePlaybackTimerStuckDetection()
  const assetResyncElapsedMs = queueAssetResyncSinceMs == null
    ? null
    : now - queueAssetResyncSinceMs
  if (!shouldRecoverEmptyPlaybackSurface({
    queueLen: qLen,
    surfaceFrameCount: surfaceLen,
    stallMs,
    thresholdMs: EMPTY_PLAYBACK_SURFACE_STALL_MS,
    recoveryInFlight: emptySurfaceRecoveryInFlight,
    msSinceLastRecovery: now - emptySurfaceLastRecoveryAtMs,
    cooldownMs: EMPTY_PLAYBACK_SURFACE_RECOVERY_COOLDOWN_MS,
    pendingCommit: pendingPlaybackSurfaceCommit != null,
    pendingStallThresholdMs: EMPTY_PLAYBACK_SURFACE_PENDING_STALL_MS,
    assetResyncInProgress: queueAssetResyncInProgress.value,
    assetResyncElapsedMs,
    assetResyncMaxMs: ASSET_RESYNC_RECOVERY_GUARD_MAX_MS,
    recoveryExhausted: emptySurfaceRecoveryExhausted,
    initBootstrapPending: isInitWorkerBootstrapPending(),
    workerSyncSuppressed: now < suppressQueueWatchWorkerSyncUntilMs
  })) {
    return
  }
  void recoverEmptyPlaybackSurface().then((outcome) => {
    noteEmptySurfaceRecoveryOutcome(outcome)
  })
}

function startEmptyPlaybackSurfaceStallPoll(): void {
  if (emptySurfaceStallPollTimer != null) {
    return
  }
  emptySurfaceStallPollTimer = setInterval(pollEmptyPlaybackSurfaceStall, 500)
}

function stopEmptyPlaybackSurfaceStallPoll(): void {
  if (emptySurfaceStallPollTimer != null) {
    clearInterval(emptySurfaceStallPollTimer)
    emptySurfaceStallPollTimer = null
  }
  emptySurfaceEmptySinceMs = null
}

/**
 * Len-1 / rotating-embed freeze: surface is painted but the same creative never changes (#1382).
 * Force embed refresh + (only when appropriate) worker loop-wrap resync.
 *
 * Multi-frame all-video stalls (#1500): do **not** `loopWrapScheduleResync` when embeds did not
 * advance — that reboots plan idx 0 and resets `lastAdvanceMonotonicMs` every cooldown, which
 * starves wall-dwell sequential escape and matches forever-`content_not_advancing` at idx 0.
 */
async function recoverContentNotAdvancing(): Promise<boolean> {
  if (contentNotAdvancingRecoveryInFlight) {
    return false
  }
  const q = unref(queue) ?? []
  if (q.length === 0 || surfaceForRender.value.frames.length < 1) {
    return false
  }
  contentNotAdvancingRecoveryInFlight = true
  contentNotAdvancingLastRecoveryAtMs = performance.now()
  try {
    suppressQueueWatchWorkerSyncUntilMs = performance.now() + 800
    const embedAdvanced = refreshEmbeddedPicksAfterLoopWrap()
    const materializedPlan = getWorkerPlaybackPlanNow()
    if (embedAdvanced) {
      lastHouseEmbedLoopAdvanceWallMs = performance.now()
    }
    const useLoopWrapResync = shouldLoopWrapOnContentNotAdvancingRecovery({
      embedAdvanced,
      planLength: materializedPlan.length > 0 ? materializedPlan.length : q.length
    })
    if (!useLoopWrapResync) {
      // Leave lastAdvanceMonotonicMs alone so stuckOnNotifiedFrameWallDwell can force 0→1.
      return false
    }
    invalidatePlaybackWorkerSyncKey()
    debouncedSetQueueUpdateState.cancel()
    await syncPlaybackWorkerStateNow(state.platform, {
      immediate: true,
      loopWrapScheduleResync: true,
      playbackPlanOverride: materializedPlan.length > 0 ? materializedPlan : undefined
    })
    return embedAdvanced
  }
  catch (err) {
    console.error('error while recovering content-not-advancing...', err)
    return false
  }
  finally {
    contentNotAdvancingRecoveryInFlight = false
  }
}

function paintedFrameIdFromSurface(surface: { viewIdx: number, frames: readonly IQueueFrame[] }): string | null {
  return surface.frames[surface.viewIdx]?.id ?? surface.frames[0]?.id ?? null
}

function syncPaintedVisibleFrameClock(paintedFrameId: string | null, nowMs = Date.now()): void {
  const prevId = lastPaintedVisibleFrameId
  const next = stampPaintedVisibleFrameChange({
    paintedFrameId,
    lastPaintedFrameId: lastPaintedVisibleFrameId,
    paintedChangedAtMs: paintedVisibleFrameChangedAtMs,
    nowMs
  })
  lastPaintedVisibleFrameId = next.lastPaintedFrameId
  paintedVisibleFrameChangedAtMs = next.paintedChangedAtMs
  if (
    prevId != null
    && next.lastPaintedFrameId != null
    && next.lastPaintedFrameId !== prevId
  ) {
    wallDwellEscapeSamePaint = resetWallDwellEscapeSamePaintCounter()
  }
}

function collectFrameForceRemountUrns(
  frame: IQueueFrame | null | undefined,
  into: Set<string>
): void {
  if (frame == null) {
    return
  }
  for (const facet of facetsRequiredForPlaybackSurfaceCommit(frame)) {
    into.add(facetAssetIndexKey(facet))
  }
}

/**
 * After repeated wall-dwell escapes with unchanged paint, recreate blob/HLS media for the stuck
 * creative (+ notified target) so the next presentation can leave — never loopWrapScheduleResync
 * on multi-frame (#1502 / #1551).
 */
async function remountMediaAfterWallDwellEscapeSamePaint(
  stuckPaintedFrameId: string | null,
  notifiedPlanIdx: number
): Promise<void> {
  if (wallDwellEscapeRemountInFlight) {
    return
  }
  const q = unref(queue) ?? []
  if (q.length < 2) {
    return
  }
  wallDwellEscapeRemountInFlight = true
  try {
    forceReleaseOverdueContentTransitionHold(performance.now())
    clearStickyOutgoingHold('supersede')
    const surface = surfaceForRender.value
    const painted = surface.frames.find(f => f.id === stuckPaintedFrameId)
      ?? surface.frames[surface.viewIdx]
      ?? surface.frames[0]
      ?? null
    const target = notifiedPlanIdx >= 0 && notifiedPlanIdx < q.length
      ? q[notifiedPlanIdx]!
      : null
    const forceRemountUrns = new Set<string>()
    collectFrameForceRemountUrns(painted, forceRemountUrns)
    collectFrameForceRemountUrns(target, forceRemountUrns)
    if (forceRemountUrns.size === 0) {
      return
    }
    console.warn('[VBuffer] wall-dwell escape same-paint remount', {
      stuckPaintedFrameId,
      notifiedPlanIdx,
      urnCount: forceRemountUrns.size
    })
    await limiter.schedule(() => buildAssetIndex(forceRemountUrns))
    if (pendingPlaybackSurfaceCommit != null) {
      flushPendingPlaybackSurfaceCommit()
    }
  }
  catch (err) {
    console.error('[VBuffer] wall-dwell escape remount failed', err)
  }
  finally {
    wallDwellEscapeRemountInFlight = false
  }
}

async function noteWallDwellEscapePresentationLeave(args: {
  paintedFrameIdBefore: string | null
  notifiedPlanIdx: number
}): Promise<void> {
  await nextTick()
  /**
   * Sticky outgoing hold = intentional Phase B/C pixels. Remount supersedes the sticky and rebuilds
   * video mid-crossfade — skip credit entirely (do not reset a legitimate stuck counter either).
   */
  if (!shouldCreditWallDwellEscapeSamePaint({
    stickyOutgoingHoldActive: stickyOutgoingHold.value != null
  })) {
    return
  }
  const paintedAfter = paintedFrameIdFromSurface(surfaceForRender.value)
  const noted = noteWallDwellEscapeSamePaint({
    consecutiveSamePaintEscapes: wallDwellEscapeSamePaint.consecutiveSamePaintEscapes,
    paintedFrameIdBefore: args.paintedFrameIdBefore,
    paintedFrameIdAfter: paintedAfter
  })
  wallDwellEscapeSamePaint = {
    consecutiveSamePaintEscapes: noted.consecutiveSamePaintEscapes
  }
  if (!noted.shouldRemount) {
    return
  }
  wallDwellEscapeSamePaint = resetWallDwellEscapeSamePaintCounter()
  await remountMediaAfterWallDwellEscapeSamePaint(
    args.paintedFrameIdBefore ?? paintedAfter,
    args.notifiedPlanIdx
  )
}

/**
 * Ask the worker whether it froze during the same window, so a returning screen can be attributed to
 * a device suspend (dwell is not a stall) or a blocked UI thread (it is) — #1500. Fire-and-forget:
 * a gap this size is rare and the answer only has to reach the next heartbeat.
 */
function captureMainLoopGap(gapMs: number, gapEndedAtMs: number): void {
  void playbackScheduler.getWorkerSuspendProbe().then((probe) => {
    runPlayerDiagnosticsSafely(() => recordMainLoopGap({
      gapMs,
      atMs: gapEndedAtMs,
      cause: classifyMainLoopGap({
        gapMs,
        gapEndedAtMs,
        workerLastGapMs: probe.lastGapMs,
        workerLastGapAtMs: probe.lastGapAtMs
      })
    }))
  }).catch(() => {
    runPlayerDiagnosticsSafely(() => recordMainLoopGap({ gapMs, atMs: gapEndedAtMs, cause: 'unknown' }))
  })
}

function pollContentNotAdvancing(): void {
  const q = unref(queue) ?? []
  const surface = surfaceForRender.value
  const painted = surface.frames[surface.viewIdx] ?? surface.frames[0] ?? null
  const frameDurationMs = painted != null
    ? resolveOverlayFrameDurationMs(painted, q.length > 0 ? q : surface.frames)
    : 0
  const nowMs = Date.now()
  const rebasedForResume = rebasePaintedVisibleFrameDwellOnResume({
    paintedChangedAtMs: paintedVisibleFrameChangedAtMs,
    lastPollWallMs: lastContentNotAdvancingPollWallMs,
    nowMs
  })
  if (rebasedForResume !== paintedVisibleFrameChangedAtMs) {
    captureMainLoopGap(nowMs - lastContentNotAdvancingPollWallMs, nowMs)
  }
  paintedVisibleFrameChangedAtMs = rebasedForResume
  lastContentNotAdvancingPollWallMs = nowMs
  syncPaintedVisibleFrameClock(painted?.id ?? null, nowMs)
  const visibleFrameChangedAtMs = paintedVisibleFrameChangedAtMs
  const msSinceLastLoopWrap = lastLoopWrapAtMs > 0 ? Math.max(0, nowMs - lastLoopWrapAtMs) : null

  runPlayerDiagnosticsSafely(() => sampleContentProgressAlwaysOn({
    nowMs,
    documentVisible: documentVisible.value,
    queueLength: q.length,
    surfaceFrameCount: surface.frames.length,
    visibleFrameId: painted?.id ?? null,
    visibleFrameChangedAtMs,
    frameDurationMs,
    msSinceLastLoopWrap,
    lastLoopWrapAdvancedContent,
    weeklyAvailabilitySuppressed: weeklyAvailabilityPlaybackSuppressed.value,
    suppressBecauseRegistrationResync: registrationResyncInFlight != null,
    suppressBecauseRegistrationTransitionHold: registrationTransitionHoldActive.value
  }))

  /**
   * Always-on self-heal for #1662: peak ≥ planLen poisons wrap even when presentation
   * commits are not firing (hybrid IDLE / soft-stuck). Runs every content poll.
   */
  const planForPeakHeal = workerPlaybackPlanFromQueue()
  if (planForPeakHeal.length > 1) {
    const visibleFallbackIdx = painted != null
      ? findOnPlanIdxForVisibleFrame(planForPeakHeal, painted)
      : -1
    const peakHeal = healStalePresentationPeakAuthority({
      planLen: planForPeakHeal.length,
      fallbackIdx: visibleFallbackIdx,
      frameIdForHealedIdx: idx => planForPeakHeal[idx]?.frameId ?? null,
      nowMs
    })
    if (peakHeal != null) {
      lastPresentationNotifiedIdx.value = peakHeal.afterIdx
      void playbackScheduler.alignOnAirPlanIdx(performance.now(), peakHeal.afterIdx)
    }
  }

  const status = detectContentNotAdvancing({
    documentVisible: documentVisible.value,
    queueLength: q.length,
    surfaceFrameCount: surface.frames.length,
    visibleFrameId: painted?.id ?? null,
    visibleFrameChangedAtMs,
    frameDurationMs,
    nowMs,
    msSinceLastLoopWrap,
    lastLoopWrapAdvancedContent,
    suppressBecauseRegistrationResync: registrationResyncInFlight != null,
    suppressBecauseRegistrationTransitionHold: registrationTransitionHoldActive.value,
    weeklyAvailabilitySuppressed: weeklyAvailabilityPlaybackSuppressed.value
  })
  if (!status.suspected) {
    return
  }
  const wallNow = performance.now()
  if (
    contentNotAdvancingRecoveryInFlight
    || wallNow - contentNotAdvancingLastRecoveryAtMs < CONTENT_NOT_ADVANCING_RECOVERY_COOLDOWN_MS
    || isInitWorkerBootstrapPending()
    || wallNow < suppressQueueWatchWorkerSyncUntilMs
  ) {
    return
  }
  void recoverContentNotAdvancing()
}

function startContentNotAdvancingPoll(): void {
  if (contentNotAdvancingPollTimer != null) {
    return
  }
  /**
   * Fresh arm after stop/unmount: the wall gap while the timer was cleared is not a process
   * suspend or a wedged UI thread. Reset the poll clock (and any pre-stop dwell) so the first
   * tick neither invents `main_thread_block` telemetry nor false-fires content_not_advancing.
   */
  const nowMs = Date.now()
  lastContentNotAdvancingPollWallMs = nowMs
  if (paintedVisibleFrameChangedAtMs > 0) {
    paintedVisibleFrameChangedAtMs = nowMs
  }
  contentNotAdvancingPollTimer = setInterval(pollContentNotAdvancing, 1_000)
}

function stopContentNotAdvancingPoll(): void {
  if (contentNotAdvancingPollTimer != null) {
    clearInterval(contentNotAdvancingPollTimer)
    contentNotAdvancingPollTimer = null
  }
  lastContentNotAdvancingPollWallMs = 0
}

const surfaceForRender = computed(() => {
  if (registrationTransitionHoldActive.value) {
    const held = lastNonEmptyPlaybackSurface.value
    if (held != null && held.frames.length > 0) {
      return held
    }
  }
  const s = playbackSurface.value
  if (s.frames.length > 0) {
    return s
  }
  const q = queue.value ?? []
  const hold = lastNonEmptyPlaybackSurface.value
  if (q.length > 0 && hold != null && hold.frames.length > 0) {
    return hold
  }
  return s
})

watch(
  () => paintedFrameIdFromSurface(surfaceForRender.value),
  (frameId) => {
    syncPaintedVisibleFrameClock(frameId)
  },
  { flush: 'post', immediate: true }
)

function commitPlaybackSurface(
  frames: IQueueFrame[],
  viewIdx: number,
  opts?: { portalBookPlanIdx?: number }
): void {
  const q = unref(queue) ?? []
  if (frames.length === 0 && q.length > 0) {
    if (!emptyBufferWorkerResyncInFlight) {
      emptyBufferWorkerResyncInFlight = true
      void syncPlaybackWorkerStateNow(state.platform, { immediate: true }).finally(() => {
        emptyBufferWorkerResyncInFlight = false
      })
    }
    return
  }
  if (frames.length > 0 && playbackBufferFramesMissingAssetIndex(frames)) {
    const wasPending = pendingPlaybackSurfaceCommit != null
    /** Carry forward plan idx so a faceless re-pend (asset rebuild) cannot drop #1479 authority. */
    pendingPlaybackSurfaceCommit = {
      frames,
      viewIdx,
      portalBookPlanIdx: opts?.portalBookPlanIdx ?? pendingPlaybackSurfaceCommit?.portalBookPlanIdx
    }
    if (!wasPending) {
      pendingCommitRetryAttempts = 0
    }
    noteEmptyPlaybackSurfaceWindowStart()
    scheduleAssetIndexBuildForPendingSurface()
    schedulePendingCommitRetry()
    return
  }
  pendingPlaybackSurfaceCommit = null
  resetPendingCommitRetryState()
  commitPlaybackSurfaceNow(frames, viewIdx, opts?.portalBookPlanIdx)
}

function commitPlaybackSurfaceNow(
  frames: IQueueFrame[],
  viewIdx: number,
  portalBookPlanIdx?: number
): void {
  const planForCommit = workerPlaybackPlanFromQueue()
  if (playbackPlanHasBookCps(planForCommit) && planForCommit.length > 1 && frames.length > 0) {
    const visFrame = frames[Math.min(Math.max(0, viewIdx), Math.max(0, frames.length - 1))]
    const onPlanIdx = visFrame != null ? findOnPlanIdxForVisibleFrame(planForCommit, visFrame) : -1
    const authority = portalBookPresentationAuthority()
    if (
      onPlanIdx >= 0
      && isSpuriousPortalBookMidLoopHouseReset(authority, onPlanIdx, planForCommit.length)
    ) {
      return
    }
  }
  const hold = registrationTransitionHoldActive.value ? lastNonEmptyPlaybackSurface.value : null
  let commitViewIdx = viewIdx
  if (hold != null && hold.frames.length > 0) {
    const holdVis = hold.frames[Math.min(Math.max(0, hold.viewIdx), hold.frames.length - 1)]
    const targetVis = frames[Math.min(Math.max(0, viewIdx), Math.max(0, frames.length - 1))]
    if (
      holdVis != null
      && targetVis != null
      && holdVis.playlistUrn === targetVis.playlistUrn
      && (holdVis.playlistSeqNo ?? 0) === (targetVis.playlistSeqNo ?? 0)
      && holdVis.id === targetVis.id
    ) {
      commitViewIdx = Math.min(Math.max(0, hold.viewIdx), Math.max(0, frames.length - 1))
    }
  }
  const pinnedVisible = pinPortalBookVisibleForWorkerOnlyCommit(frames, commitViewIdx)
  frames = pinnedVisible.frames
  commitViewIdx = pinnedVisible.viewIdx
  const visIdxForTransition = Math.min(Math.max(0, commitViewIdx), Math.max(0, frames.length - 1))
  const prevVisIdx = playbackSurface.value.viewIdx
  const prevFrame = prevVisIdx >= 0 ? playbackSurface.value.frames[prevVisIdx] : undefined
  const nextFrame = frames[visIdxForTransition]
  if (
    nextFrame != null
    && planForCommit.length > 1
    && !playbackPlanHasBookCps(planForCommit)
  ) {
    const onPlanIdx = findOnPlanIdxForVisibleFrame(planForCommit, nextFrame)
    const loggedIdx = getPlaybackPresentationSession().lastLoggedPlanIdx
    if (onPlanIdx >= 0 && onPlanIdx === loggedIdx) {
      const sameVisible = prevFrame != null && nextFrame.id === prevFrame.id
      const hold = lastNonEmptyPlaybackSurface.value
      const holdVis = hold != null && hold.frames.length > 0
        ? hold.frames[Math.min(Math.max(0, hold.viewIdx), hold.frames.length - 1)]
        : undefined
      const sameHold = holdVis != null && holdVis.id === nextFrame.id
      /**
       * Same plan slot + same frame id: skip no-op buffer churn from registration polls.
       * Do **not** skip when facet.asset changed — S1 re-resolve must remount media (#1352).
       */
      if (
        (sameVisible || sameHold)
        && !queueFramesDifferInFacetAssets(prevFrame ?? holdVis, nextFrame)
      ) {
        return
      }
    }
  }
  const prevKind = frameMainMediaKind(prevFrame)
  const nextKind = frameMainMediaKind(nextFrame)
  const nextSrc = frameMainImageSrc(nextFrame)
  const seamlessContinuous = prevFrame != null
    && nextFrame != null
    && shouldSeamlesslyContinueFromPreviousFrame(prevFrame, nextFrame)
  const incomingAlreadyReady = isIncomingPaintReadyAtCommit({
    nextKind,
    nextImageSrc: nextSrc,
    isImagePredecoded,
    seamlessContinuous
  })
  const crossfadeEnabled = isPresentationCrossfadeEnabled(
    state.tenant?.playerCreativeCrossfade,
    state.capacityTier,
    state.tenant?.playerCreativeCrossfadeOnLowCapacity,
    state.pendingPlaylistSwap != null || registrationTransitionHoldActive.value
  )
  const useStickyHold = shouldStickyHoldPresentationAdvance({
    prevFrame,
    nextFrame,
    registrationHoldActive: registrationTransitionHoldActive.value,
    incomingAlreadyReady
  })
  const prevIsWidget = frameMainIsWidget(prevFrame)
  const nextIsWidget = frameMainIsWidget(nextFrame)
  /** Widget↔widget: sticky remount is a black placeholder — let WidgetTwoSlotLayer CSS dissolve. */
  const skipStickyForWidgetPair = prevIsWidget && nextIsWidget
  /**
   * Video↔video: sticky remounts a third live `<video>` (buffer on-air + preload + sticky), exceeding
   * the ≤2 decoder budget on mid-tier Android (#1443 / PLAYER-19). Rely on the double-buffer alone.
   */
  const skipStickyForVideoPair = shouldSkipStickyForVideoToVideo({
    prevKind,
    nextKind
  })
  const skipStickyForNativeOutgoing = shouldSkipStickyForNativeHybridSurface({
    outgoingNative: frameMainIsNativeVideo(prevFrame)
  })
  const skipStickyOverlay = skipStickyForWidgetPair || skipStickyForVideoPair || skipStickyForNativeOutgoing
  const nextSlotDurationMs = nextFrame != null
    ? Math.max(0, (nextFrame.duration ?? 0) * 1000)
    : 0
  const useWarmPathDissolve = !skipStickyOverlay && shouldWarmPathPresentationDissolve({
    crossfadeEnabled,
    useStickyHold,
    incomingAlreadyReady,
    seamlessContinuous,
    registrationHoldActive: registrationTransitionHoldActive.value,
    prevFrame,
    nextFrame,
    onAirSlotDurationMs: nextSlotDurationMs,
    crossfadeMs: presentationCrossfadeMs.value
  })
  const frozenStillUrl = (prevKind === 'video-blob' || prevKind === 'hls')
    ? capturePaintableVideoStill(playbackRoot.value)
    : null
  const armFrozenStill = shouldArmFrozenOutgoingStill({ prevKind, stillUrl: frozenStillUrl })
    && prevFrame != null
    && nextFrame != null
  const armStickyOverlay = !armFrozenStill
    && !skipStickyOverlay
    && (useStickyHold || useWarmPathDissolve)
    && prevFrame != null
    && nextFrame != null
  if (armFrozenStill && prevFrame != null && nextFrame != null) {
    // Still covers the cut, including video→video where a third live decoder is refused.
    beginStickyOutgoingHold({
      frame: prevFrame,
      targetFrameId: nextFrame.id,
      targetKind: nextKind,
      targetImageSrc: nextSrc,
      prevKind,
      deferReadyClear: useWarmPathDissolve,
      frozenStillUrl
    })
  }
  else if (skipStickyOverlay) {
    clearStickyOutgoingHold('supersede')
  }
  else if (armStickyOverlay) {
    // Overlay covers remount; surface applies immediately below (deferred apply stalled E2E advances).
    beginStickyOutgoingHold({
      frame: prevFrame,
      targetFrameId: nextFrame.id,
      targetKind: nextKind,
      targetImageSrc: nextSrc,
      prevKind,
      deferReadyClear: useWarmPathDissolve,
      frozenStillUrl: null
    })
  }
  else {
    clearStickyOutgoingHold('supersede')
    if ((prevKind === 'video-blob' || prevKind === 'hls') && nextKind === 'image') {
      // Skip fixed blackout when Phase C is on — it masks still dissolves / warm cuts.
      if (
        !crossfadeEnabled
        && shouldArmPostVideoImageBlackout(nextSrc)
      ) {
        armPostVideoImageBlackout()
      }
    }
  }
  if (nextKind !== 'video-blob' && nextKind !== 'hls') {
    playingFrame.value = null
  }

  const finishPostSurfaceCommitWork = (): void => {
    if (resolveRegistrationBootstrapCommit != null) {
      fulfillRegistrationBootstrapCommit()
      if (state.showTimersOverlay) {
        void fetchTimersOverlayTimingFromWorker()
      }
    }
    syncPlaybackHeartbeatCadence()
    if (isE2ePlaybackProbeEnabled()) {
      const visFrame = playbackSurface.value.frames[playbackSurface.value.viewIdx]
      const planIdx = portalBookPlanIdx ?? getPlaybackPresentationSession().lastLoggedPlanIdx
      if (visFrame != null && planIdx >= 0) {
        recordE2ePresentationCommit([{
          playlistUrn: visFrame.playlistUrn,
          frameIndex: 0,
          playlistSeqNo: visFrame.playlistSeqNo ?? 0,
          durationSeconds: visFrame.duration,
          frameId: visFrame.id
        }], planIdx)
      }
    }
  }

  const surfaceFrameCountBeforeCommit = surfaceForRender.value.frames.length
  const emptyForMsBeforeCommit = emptyPlaybackSurfaceElapsedMs()

  // Apply surface immediately so duration/heartbeat/E2E probes advance; sticky overlay
  // still covers the remount. Only warm-path clear waits for paint (flicker guard).
  if (!tryApplyCommittedPlaybackSurface(frames, commitViewIdx)) {
    return
  }
  finishPostSurfaceCommitWork()
  scheduleNativeHybridSync()

  if (shouldRephaseFrameClockOnFirstPaint({
    previousSurfaceFrameCount: surfaceFrameCountBeforeCommit,
    nextSurfaceFrameCount: playbackSurface.value.frames.length,
    emptyForMs: emptyForMsBeforeCommit,
    minEmptyMs: FIRST_PAINT_CLOCK_REPHASE_MIN_EMPTY_MS,
    seamlessContinuous
  })) {
    rephaseFrameClockForFirstPaint(planForCommit, nextFrame)
  }

  if (armStickyOverlay && useWarmPathDissolve) {
    const stickyGen = stickyOutgoingHold.value?.generation
    void (async () => {
      await nextTick()
      await waitAnimationFrames(2)
      if (stickyOutgoingHold.value?.generation !== stickyGen) {
        return
      }
      clearStickyOutgoingHold('ready')
    })()
  }
}

/**
 * Restart the on-air slot at the moment the first creative paints. The playlist clock advances
 * while the brand cube waits on the asset index, so the frame would otherwise inherit a nearly
 * spent segment (a 13s video getting ~2s of dwell after a reboot).
 */
function rephaseFrameClockForFirstPaint(
  plan: PlaybackSegment[],
  visibleFrame: IQueueFrame | undefined
): void {
  emptySurfaceEmptySinceMs = null
  const onPlanIdx = visibleFrame != null ? findOnPlanIdxForVisibleFrame(plan, visibleFrame) : -1
  void playbackScheduler.rephaseFrameFromStart(
    performance.now(),
    onPlanIdx >= 0 ? onPlanIdx : undefined
  )
  if (state.showTimersOverlay) {
    void fetchTimersOverlayTimingFromWorker()
  }
}

function commitPlaybackFramesOnly(frames: IQueueFrame[]): void {
  if (registrationTransitionHoldActive.value) {
    return
  }
  if (performance.now() - lastRegistrationResyncEndedAt < 2500) {
    return
  }
  const cur = playbackSurface.value.frames
  if (cur.length === frames.length && cur.length > 0 && getQueueHash(cur) === getQueueHash(frames)) {
    return
  }
  if (frames.length > 0 && playbackBufferFramesMissingAssetIndex(frames)) {
    const wasPending = pendingPlaybackSurfaceCommit != null
    pendingPlaybackSurfaceCommit = { frames, viewIdx: playbackSurface.value.viewIdx }
    if (!wasPending) {
      pendingCommitRetryAttempts = 0
    }
    noteEmptyPlaybackSurfaceWindowStart()
    scheduleAssetIndexBuildForPendingSurface()
    return
  }
  const p = playbackSurface.value
  if (!tryApplyCommittedPlaybackSurface(frames, p.viewIdx)) {
    return
  }
  syncPlaybackHeartbeatCadence()
}

/** True for the visible slot on the first update after a continuous handoff (explicit link or same asset). */
const seamlessAfterContinuousLink = ref(false)
watch(
  () => {
    const s = playbackSurface.value
    if (s.viewIdx < 0 || s.viewIdx >= s.frames.length) {
      return null
    }
    return s.frames[s.viewIdx] ?? null
  },
  (now, prev) => {
    if (now == null) {
      seamlessAfterContinuousLink.value = false
      return
    }
    if (prev != null && prev.id !== now.id) {
      const q = unref(queue) ?? []
      let queueAdjacent = true
      if (q.length > 1) {
        const prevIdx = queueIndexForPresentationFrame(q, prev)
        const nowIdx = queueIndexForPresentationFrame(q, now)
        queueAdjacent = prevIdx >= 0 && nowIdx >= 0 && nowIdx === (prevIdx + 1) % q.length
      }
      seamlessAfterContinuousLink.value = queueAdjacent
        && shouldSeamlesslyContinueFromPreviousFrame(prev, now)
      if (performance.now() >= suppressDeferredRegistrationBoundaryUntil) {
        notifyDeferredPlaybackScheduleFrameBoundary()
      }
    }
    else {
      seamlessAfterContinuousLink.value = false
    }
  },
  { flush: 'pre' }
)

watch(
  playingFrame,
  (frame) => {
    const hold = stickyOutgoingHold.value
    if (hold != null && frame != null && frame.id === hold.targetFrameId) {
      clearStickyOutgoingHold('ready')
    }
  }
)

watch(
  queue,
  (q) => {
    if (isE2ePlaybackProbeEnabled() && q != null) {
      setE2eQueueFrameIds(q.map(f => f.id), q.map(f => f.duration ?? 0))
    }
  },
  { immediate: true }
)

watch(
  () => state.widgetRevealMs,
  (ms) => {
    if (isE2ePlaybackProbeEnabled()) {
      setE2eWidgetRevealMs(ms)
    }
  },
  { immediate: true }
)

/** Last slim buffer from the scheduler worker; used to re-resolve full frames when facets/metadata change without a sequence change (S1). */
const lastWorkerBufferSlim = ref<PlaybackSegment[]>([])
/** On-air queue index from the latest {@link PlaybackScheduler} presentation notify; used by buffer-only updates. */
// HMR / soft remount: restore spine-gated sessionStorage. Page reload discards storage and cold-starts frame 1.
bootstrapPlaybackPresentationOnVBufferMount(planSpineHashNow())
const sessAtMount = getPlaybackPresentationSession()
const restoredNotifiedIdx = Math.max(
  sessAtMount.lastNotifiedPlanIdx,
  sessAtMount.lastLoggedPlanIdx,
  getPeakPresentationPlanIdx()
)
const lastPresentationNotifiedIdx = ref(restoredNotifiedIdx >= 0 ? restoredNotifiedIdx : -1)
watch(
  () => planSpineHashNow(),
  (spineHash) => {
    if (spineHash !== '') {
      syncPresentationAuthorityFromStorage()
    }
  }
)
watch(lastPresentationNotifiedIdx, (v) => {
  const s = getPlaybackPresentationSession()
  if (v < 0) {
    if (s.lastNotifiedPlanIdx >= 0) {
      lastPresentationNotifiedIdx.value = s.lastNotifiedPlanIdx
    }
    return
  }
  recordPresentationPlanIdx(v)
}, { flush: 'sync' })
/** Previous on-air index for detecting non-sequential worker jumps (session-backed — survives remount). */
/** Portal book: only {@link framePresentation} (and bootstrap) may change the on-air frame id. */
let portalBookAllowVisibleSurfaceAdvance = false
/** Serialize portal-book presentation commits (overlapping Comlink notifies). */
let portalBookPresentationCommitChain: Promise<void> = Promise.resolve()

function portalBookPresentationAuthority(): number {
  return playbackPresentationAuthorityFromSession()
}

function recordPortalBookPresentationAuthority(effectiveNotifiedIdx: number, planLen: number): void {
  if (effectiveNotifiedIdx < 0 || planLen <= 1) {
    return
  }
  if (getPlaybackPresentationSession().portalBookAuthorityIdx < 0) {
    getPlaybackPresentationSession().portalBookAuthorityIdx = effectiveNotifiedIdx
    return
  }
  if (isLegitimatePortalBookLoopWrap(getPlaybackPresentationSession().portalBookAuthorityIdx, effectiveNotifiedIdx, planLen)) {
    getPlaybackPresentationSession().portalBookAuthorityIdx = effectiveNotifiedIdx
    return
  }
  if (effectiveNotifiedIdx >= getPlaybackPresentationSession().portalBookAuthorityIdx) {
    getPlaybackPresentationSession().portalBookAuthorityIdx = effectiveNotifiedIdx
  }
}

/** Portal book: advance worker resume only after the surface actually shows the plan slot. */
async function recordPortalBookPresentationAfterVisibleCommit(
  plan: PlaybackSegment[],
  planIdx: number,
  frames: IQueueFrame[],
  viewIdx: number
): Promise<void> {
  if (frames.length === 0) {
    return
  }
  const expectedId = plan[planIdx]?.frameId
  const targetFrame = frames[Math.min(Math.max(0, viewIdx), frames.length - 1)]
  const surfaceFrame = playbackSurface.value.frames[playbackSurface.value.viewIdx]
  if (!shouldRecordPresentationAfterVisibleCommit({
    planIdx,
    planLength: plan.length,
    expectedFrameId: expectedId,
    targetFrameId: targetFrame?.id,
    surfaceFrameId: surfaceFrame?.id,
    lastLoggedPlanIdx: getPlaybackPresentationSession().lastLoggedPlanIdx,
    commitDeferredToPending: pendingPlaybackSurfaceCommit != null
  })) {
    return
  }
  lastPresentationNotifiedIdx.value = planIdx
  getPlaybackPresentationSession().lastLoggedPlanIdx = planIdx
  recordPortalBookPresentationAuthority(planIdx, plan.length)
  await playbackScheduler.syncResumePlanIdx(planIdx)
}

/**
 * Rotating / master playlists: same contract as portal book — do not advance
 * {@link lastPresentationNotifiedIdx} until the surface shows the notified plan frame (#1479).
 */
async function recordMainPresentationAfterVisibleCommit(
  plan: PlaybackSegment[],
  planIdx: number,
  frames: IQueueFrame[],
  viewIdx: number
): Promise<void> {
  if (frames.length === 0) {
    return
  }
  const expectedId = plan[planIdx]?.frameId
  const targetFrame = frames[Math.min(Math.max(0, viewIdx), frames.length - 1)]
  const surfaceFrame = playbackSurface.value.frames[playbackSurface.value.viewIdx]
  if (!shouldRecordPresentationAfterVisibleCommit({
    planIdx,
    planLength: plan.length,
    expectedFrameId: expectedId,
    targetFrameId: targetFrame?.id,
    surfaceFrameId: surfaceFrame?.id,
    lastLoggedPlanIdx: getPlaybackPresentationSession().lastLoggedPlanIdx,
    commitDeferredToPending: pendingPlaybackSurfaceCommit != null
  })) {
    return
  }
  recordPresentationPlanIdx(
    planIdx,
    expectedId ?? null,
    getPlaybackWorkerPayloadSequenceHash(plan)
  )
  lastPresentationNotifiedIdx.value = planIdx
  await playbackScheduler.syncResumePlanIdx(planIdx)
}

const standByFrameCount = ref(0)
/** True when document is visible; drives main-thread playback tick with rAF vs interval when hidden. */
const documentVisible = ref(typeof document !== 'undefined' ? document.visibilityState === 'visible' : true)
const assetIndex = ref<IAssetIndex>({})
const visibleFrameContainerRef = ref<HTMLElement | null>(null)
/** Per-buffer-index refs so WidgetTwoSlotLayer can preposition each slot to its frame's section/container. */
const widgetSectionRefForIndex0 = ref<HTMLElement | null>(null)
const widgetSectionRefForIndex1 = ref<HTMLElement | null>(null)
const frameContainerRefForIndex0 = ref<HTMLElement | null>(null)
const frameContainerRefForIndex1 = ref<HTMLElement | null>(null)
provide('visibleFrameContainerRef', visibleFrameContainerRef)
provide('widgetSectionRefForIndex0', widgetSectionRefForIndex0)
provide('widgetSectionRefForIndex1', widgetSectionRefForIndex1)
provide('frameContainerRefForIndex0', frameContainerRefForIndex0)
provide('frameContainerRefForIndex1', frameContainerRefForIndex1)
provide('platformWidgetConfig', platformWidgetConfig)

/** Ms until next frame (from worker timing); used to trigger next-slot video preload ~3s before transition. */
const preloadMsToNextFrame = ref<number | null>(null)
/**
 * True when we're within VIDEO_PRELOAD_LEAD_MS of the next frame so the hidden slot should start loading.
 * Suppressed in low-memory mode, on low capacity, and on mid + video-heavy queues so we keep a
 * single on-air MediaCodec session (SlimBOX / PLAYER-1J) — image predecode still uses the poll.
 */
const preloadNextSlotNow = computed(() => {
  return shouldPreloadNextVideoSlot({
    lowMemoryMode: state.lowMemoryMode,
    capacityTier: state.capacityTier,
    videoFrameCountInQueue: countVideoFramesInQueue(queue.value ?? []),
    msToNextFrame: preloadMsToNextFrame.value,
    leadMs: VIDEO_PRELOAD_LEAD_MS
  })
})
provide('preloadNextSlotNow', preloadNextSlotNow)
/**
 * Off-DOM still warm window. Not suppressed by lowMemoryMode: on ≤2 GB devices video is often
 * disabled and image decode is the critical path; we only keep one/few decoded bitmaps in cache.
 */
const imagePredecodeNow = computed(() => {
  const ms = preloadMsToNextFrame.value
  return ms != null && ms > 0 && ms <= IMAGE_PRELOAD_LEAD_MS
})
/** While an image frame is on-air (portal tail/house still), do not warm the next video in the hidden buffer (Chrome composites greenscreen over the image). */
const suppressHiddenSlotVideoPreload = computed(() => {
  const s = surfaceForRender.value
  const visIdx = s.viewIdx
  if (visIdx < 0 || visIdx >= s.frames.length) {
    return false
  }
  const main = s.frames[visIdx]?.facets?.main
  if (main == null || typeof main.asset !== 'string' || main.asset.trim() === '') {
    return false
  }
  return assetEntryMediaKind(unref(assetIndex)[facetAssetIndexKey(main)]) === 'image'
})
provide('suppressHiddenSlotVideoPreload', suppressHiddenSlotVideoPreload)
provide('playbackQueue', queue)

function isBookCpsQueueFrame(frame: IQueueFrame | undefined): boolean {
  return frame != null && frame.id.startsWith('book-')
}

function queueLooksLikePortalBook(q: readonly IQueueFrame[]): boolean {
  return q.some(f => isBookCpsQueueFrame(f))
}

function queueRowKey(frame: Pick<IQueueFrame, 'id' | 'playlistUrn' | 'playlistSeqNo'>): string {
  return `${frame.playlistUrn}\t${frame.id}\t${frame.playlistSeqNo ?? 0}`
}

function queueIndexForPresentationFrame(
  queue: readonly IQueueFrame[],
  frame: Pick<IQueueFrame, 'id' | 'playlistUrn' | 'playlistSeqNo'>
): number {
  const strictIdx = queue.findIndex(q => queueRowKey(q) === queueRowKey(frame))
  if (strictIdx >= 0) {
    return strictIdx
  }
  return queue.findIndex(q => q.id === frame.id)
}

/**
 * Chrome composites hidden full-size video (and sometimes images) over the visible buffer slot.
 * While a still/image frame is on-air, omit the hidden slot from the DOM entirely.
 */
function bufferSlotShouldRender(slotIndex: number): boolean {
  const s = surfaceForRender.value
  if (slotIndex < 0 || slotIndex >= s.frames.length) {
    return false
  }
  if (slotIndex === s.viewIdx) {
    return true
  }
  const onAir = s.frames[s.viewIdx]
  const hidden = s.frames[slotIndex]
  const q = unref(queue) ?? []
  const notifiedIdx = lastPresentationNotifiedIdx.value
  /** Portal book CPS on-air: hidden slot must stay book-only (never warm HF3 over book-1). */
  if (queueLooksLikePortalBook(q) && isBookCpsQueueFrame(onAir)) {
    if (hidden == null || !isBookCpsQueueFrame(hidden)) {
      return false
    }
  }
  if (hidden != null && queueLooksLikePortalBook(q)) {
    const plan = workerPlaybackPlanFromQueue()
    if (playbackPlanHasBookCps(plan)) {
      /** Never mount house editorial video in the hidden slot — Chrome composites it over book CPS (looks like HF3 playing twice). */
      if (!isBookCpsQueueFrame(hidden)) {
        return false
      }
      const authorityIdx = portalBookPresentationAuthority()
      const preloadIdx = authorityIdx >= 0 ? resolvePresentationPreloadPlanIdx(plan, authorityIdx) : null
      if (preloadIdx == null) {
        return false
      }
      const expectedHiddenId = plan[preloadIdx]?.frameId
      if (expectedHiddenId != null && hidden.id !== expectedHiddenId) {
        return false
      }
    }
  }
  else if (hidden != null && notifiedIdx >= 0 && q.length > 0) {
    const expectedNext = q[(notifiedIdx + 1) % q.length]
    if (expectedNext != null && queueRowKey(hidden) !== queueRowKey(expectedNext)) {
      return false
    }
  }
  if (unref(suppressHiddenSlotVideoPreload)) {
    return false
  }
  const main = s.frames[slotIndex]?.facets?.main
  if (main == null || typeof main.asset !== 'string' || main.asset.trim() === '') {
    return false
  }
  const kind = assetEntryMediaKind(unref(assetIndex)[facetAssetIndexKey(main)])
  return kind === 'video-blob' || kind === 'hls'
}

const PLAYBACK_PRELOAD_POLL_MS = 1000
let preloadPollTimer: ReturnType<typeof setInterval> | null = null
let preloadPollPending = false
function updatePreloadMsToNextFrame() {
  if (preloadPollPending || playbackSurface.value.frames.length < 2) { return }
  preloadPollPending = true
  void playbackScheduler.getPlaybackTiming().then((t) => {
    const msToNext = Math.max(0, t.frameDurationMs - t.frameElapsedMs)
    preloadMsToNextFrame.value = msToNext
    if (!state.showTimersOverlay) {
      state.playbackTiming = t
      state.playbackTimingSampleAtPerfMs = performance.now()
      notePlaybackTimingSampleForStuckDetect(t)
      updatePlaybackTimerStuckDetection()
    }
  }).catch(() => {
    preloadMsToNextFrame.value = null
  }).finally(() => {
    preloadPollPending = false
    syncPlayingFrameFromActiveVideo()
  })
}
function startPreloadPoll() {
  if (preloadPollTimer != null) { return }
  updatePreloadMsToNextFrame()
  preloadPollTimer = setInterval(updatePreloadMsToNextFrame, PLAYBACK_PRELOAD_POLL_MS)
}
function stopPreloadPoll() {
  if (preloadPollTimer != null) {
    clearInterval(preloadPollTimer)
    preloadPollTimer = null
  }
  preloadMsToNextFrame.value = null
}

/** Current frame (for playback timing). */
const currentFrame = computed(() => {
  const { viewIdx, frames: buf } = playbackSurface.value
  return viewIdx >= 0 && viewIdx < buf.length ? buf[viewIdx] ?? null : null
})

/** Visible progressive MP4 is Media3 underlay — punch opaque WebView chrome so TextureView shows. */
function frameMainIsNativeVideo(frame: IQueueFrame | null | undefined): boolean {
  if (frame == null) {
    return false
  }
  if (!frameMainExpectsVideo(frame)) {
    return false
  }
  const main = frame.facets?.main
  if (main == null || typeof main.asset !== 'string') {
    return false
  }
  const entry = unref(assetIndex)[facetAssetIndexKey(main)]
  return entry != null
    && typeof entry === 'object'
    && 'isNativeVideo' in entry
    && entry.isNativeVideo === true
}

const nativeHybridVideoOnAir = computed(() => {
  if (!nativePlaybackSurfaceEnabled.value) {
    return false
  }
  return frameMainIsNativeVideo(currentFrame.value)
})

/**
 * Punch underlay chrome only once Media3 has painted (or sticky still shows native),
 * and keep it until hybrid sync finishes tearing down — avoids black flash / blank hole.
 */
const nativeUnderlaySurfaceArmed = ref(false)

const nativeUnderlayChromeDesired = computed(() => {
  if (!nativePlaybackSurfaceEnabled.value) {
    return false
  }
  const sticky = stickyOutgoingHold.value
  if (
    sticky != null
    && !stickyHoldFadingOut.value
    && frameMainIsNativeVideo(sticky.frame)
  ) {
    return true
  }
  return nativeHybridVideoOnAir.value && nativeUnderlaySurfaceArmed.value
})

/** Canonical per-frame duration (ms) for the timer overlay from the materialized queue / worker plan. */
function resolveOverlayFrameDurationMs(frame: IQueueFrame, fullQueue: readonly IQueueFrame[]): number {
  const qIdx = queueIndexForPresentationFrame(fullQueue, frame)
  if (qIdx >= 0) {
    return Math.max(0, (fullQueue[qIdx]!.duration ?? 0) * 1000)
  }
  const plan = workerPlaybackPlanFromQueue()
  const onPlanIdx = findOnPlanIdxForVisibleFrame(plan, frame)
  if (onPlanIdx >= 0) {
    const fromPlan = plan[onPlanIdx]!.durationSeconds
    if (typeof fromPlan === 'number' && fromPlan > 0) {
      return Math.max(0, fromPlan * 1000)
    }
    if (onPlanIdx < fullQueue.length) {
      return Math.max(0, (fullQueue[onPlanIdx]!.duration ?? 0) * 1000)
    }
  }
  return Math.max(0, (frame.duration ?? 0) * 1000)
}

/** When the visible frame changes, record start time so the timer overlay can show elapsed for the frame in view. */
let visibleFrameStartMs = 0
/**
 * Dwell baseline for `frame_transition_accelerated` (and related diagnostics).
 * Must not share {@link visibleFrameStartMs} — opening the timers overlay resets that clock for UX.
 */
let diagnosticsVisibleFrameStartMs = 0
/** Last completed on-air frame transition (dwell vs configured duration for accelerated fault). */
let lastCompletedFrameTransition: ILastCompletedFrameTransition | null = null
let lastFrameDurationMsForOverlay = 0
/** Previous-frame duration held until the new frame's elapsed clock advances (timers S1). */
let pendingLastFrameDurationMsForOverlay: number | null = null
/** Tracks {@link state.playbackTiming.frameElapsedMs} changes for stuck-timer detection. */
let lastTimingFrameElapsedMsForStuckDetect: number | null = null
let lastTimingChangeMsForStuckDetect: number | null = null
/** Wall ms of the last playback tick sample used for diagnostics cadence. */
let lastPlaybackDiagnosticTickAtMs = 0
/**
 * Wall ms of the last {@link updatePlaybackTimerStuckDetection} poll.
 * Used only for suspend-gap rebase (#1969) — must not be advanced by
 * {@link notePlaybackTimingSampleForStuckDetect}, or the gap is zeroed before rebase runs.
 */
let lastTimerStuckDetectPollWallMs = 0
/** Last worker on-air index from {@link IPlaybackTiming.notifiedIdx} (−1 when unknown). */
let lastWorkerNotifiedIdxForDiagnostics = -1

function notePlaybackTimingSampleForStuckDetect(timing: typeof state.playbackTiming, nowMs = Date.now()): void {
  if (timing == null) {
    lastTimingFrameElapsedMsForStuckDetect = null
    lastTimingChangeMsForStuckDetect = null
    return
  }
  if (typeof timing.notifiedIdx === 'number') {
    lastWorkerNotifiedIdxForDiagnostics = timing.notifiedIdx
  }
  if (lastTimingFrameElapsedMsForStuckDetect !== timing.frameElapsedMs) {
    lastTimingFrameElapsedMsForStuckDetect = timing.frameElapsedMs
    lastTimingChangeMsForStuckDetect = nowMs
  }
  lastPlaybackDiagnosticTickAtMs = nowMs
}

function updatePlaybackTimerStuckDetection(nowMs = Date.now()): void {
  const rebasedClocks = rebasePlaybackTimerStuckClocksOnResume({
    visibleFrameStartMs,
    lastTimingChangeMs: lastTimingChangeMsForStuckDetect,
    lastPollWallMs: lastTimerStuckDetectPollWallMs,
    nowMs
  })
  if (rebasedClocks.didRebase) {
    visibleFrameStartMs = rebasedClocks.visibleFrameStartMs
    lastTimingChangeMsForStuckDetect = rebasedClocks.lastTimingChangeMs
    if (diagnosticsVisibleFrameStartMs > 0) {
      diagnosticsVisibleFrameStartMs = nowMs
    }
    // Clear continuous-stuck latch so sleep-inflated flatline does not keep recovery armed (#1969).
    recordPlaybackTimerStuckForReloadPolicy({ suspected: false, now: nowMs })
  }

  const status: IPlaybackTimerStuckStatus = detectPlaybackTimerStuck({
    nowMs,
    documentVisible: documentVisible.value,
    queueLength: (queue.value ?? []).length,
    playbackTiming: state.playbackTiming,
    visibleFrame: currentFrame.value,
    visibleFrameStartMs,
    lastTimingFrameElapsedMs: lastTimingFrameElapsedMsForStuckDetect,
    lastTimingChangeMs: lastTimingChangeMsForStuckDetect,
    suppressBecauseRegistrationResync: registrationResyncInFlight != null,
    suppressBecauseRegistrationTransitionHold: registrationTransitionHoldActive.value
  })
  state.playbackTimerStuck = status
  reportPlaybackTimerStuckToSentry(status)
  const stuckReload = recordPlaybackTimerStuckForReloadPolicy({
    suspected: status.suspected,
    now: nowMs
  })
  if (stuckReload.reload && stuckReload.reason) {
    lastTimerStuckDetectPollWallMs = nowMs
    performPlaybackRecoveryReload(stuckReload.reason)
    return
  }

  const fullQueue = queue.value ?? []
  const notifiedIdx = lastPresentationNotifiedIdx.value
  const notifiedFrameId = notifiedIdx >= 0 && notifiedIdx < fullQueue.length
    ? fullQueue[notifiedIdx]!.id
    : null
  const overlayFrameDurationMs = currentFrame.value != null
    ? resolveOverlayFrameDurationMs(currentFrame.value, fullQueue)
    : null
  const mediaDurationSec = sampleActiveVideoMediaDurationSec()
  const surfaceFrameCount = surfaceForRender.value.frames.length
  const emptySurfaceStallMs = surfaceFrameCount === 0 && fullQueue.length > 0
    ? emptyPlaybackSurfaceElapsedMs()
    : null
  const visibleFrameExpectsVideo = frameMainExpectsVideo(currentFrame.value)
  const nativeHybridOnAir = nativeHybridVideoOnAir.value
  const videoSample = sampleVisibleVideoDiagnostics()
  const videoPaintStallMs = videoPaintStallElapsedMs({
    expectsVideo: visibleFrameExpectsVideo && !nativeHybridOnAir,
    surfaceFrameCount,
    paintReady: nativeHybridOnAir ? true : videoSample.paintReady
  })
  const downloadRaw = state.downloadProgress
  const assetDownloadProgress01 = Number.isFinite(downloadRaw)
    ? Math.min(1, Math.max(0, downloadRaw))
    : null
  runPlayerDiagnosticsSafely(() => samplePlayerDiagnostics({
    nowMs,
    documentVisible: documentVisible.value,
    queueLength: fullQueue.length,
    surfaceFrameCount,
    pendingSurfaceCommit: pendingPlaybackSurfaceCommit != null,
    emptySurfaceStallMs,
    visibleFrameExpectsVideo,
    visibleVideoElementCount: videoSample.elementCount,
    visibleVideoReadyState: videoSample.readyState,
    visibleVideoWidth: videoSample.videoWidth,
    visibleVideoNetworkState: videoSample.networkState,
    visibleVideoMediaErrorCode: videoSample.mediaErrorCode,
    visibleVideoSrcScheme: videoSample.srcScheme,
    videoPaintStallMs,
    assetDownloadProgress01,
    visibleFrameId: currentFrame.value?.id ?? null,
    notifiedIdx,
    workerNotifiedIdx: lastWorkerNotifiedIdxForDiagnostics,
    notifiedFrameId,
    frameElapsedMs: state.playbackTiming?.frameElapsedMs ?? null,
    frameDurationMs: state.playbackTiming?.frameDurationMs ?? null,
    overlayFrameDurationMs,
    mediaDurationSec,
    visibleFrameStartMs,
    lastTickAtMs: lastPlaybackDiagnosticTickAtMs > 0 ? lastPlaybackDiagnosticTickAtMs : null,
    // Prefer painted-identity clock; overlay open resets visibleFrameStartMs without a creative change.
    lastFrameTransitionAtMs: paintedVisibleFrameChangedAtMs > 0
      ? paintedVisibleFrameChangedAtMs
      : visibleFrameStartMs,
    weeklyAvailabilitySuppressed: weeklyAvailabilityPlaybackSuppressed.value,
    playbackTimerStuck: status,
    lastCompletedFrameTransition,
    suppressBecauseRegistrationResync: registrationResyncInFlight != null,
    suppressBecauseRegistrationTransitionHold: registrationTransitionHoldActive.value
  }))
  // Always advance the poll wall clock — including when timing samples did not change —
  // so the next call does not treat a normal gap as process_suspend (#1969).
  // Do NOT touch lastPlaybackDiagnosticTickAtMs here — that clock advances only from
  // notePlaybackTimingSampleForStuckDetect so empty-surface stall polls do not suppress
  // tick_cadence_degraded (#1969 Bugbot).
  lastTimerStuckDetectPollWallMs = nowMs
}

/** Sum of segment durations on the materialized queue (authoritative for timer overlay totals). */
function materializedPlaylistDurationMs(frames: IQueueFrame[]): number {
  return frames.reduce((acc, f) => acc + Math.max(0, (f.duration ?? 0) * 1000), 0)
}

/** Cumulative ms from loop start through the current frame index + in-frame elapsed. */
function materializedPlaylistElapsedMs(
  frames: IQueueFrame[],
  frame: IQueueFrame,
  frameElapsedMs: number
): number {
  const qIdx = queueIndexForPresentationFrame(frames, frame)
  if (qIdx < 0) {
    return Math.max(0, frameElapsedMs)
  }
  const beforeMs = frames.slice(0, qIdx).reduce((acc, f) => acc + Math.max(0, (f.duration ?? 0) * 1000), 0)
  const curMs = resolveOverlayFrameDurationMs(frame, frames)
  return beforeMs + Math.min(Math.max(0, frameElapsedMs), curMs)
}

/**
 * Overlay durations from the materialized queue; elapsed times stay on the worker playback clock
 * (wall-clock overrides caused ~1–2s drift vs actual frame advances).
 */
function mergeVisibleFrameIntoPlaybackTiming() {
  if (!state.playbackTiming) {
    return
  }
  const fullQueue = queue.value ?? []
  const frame = currentFrame.value
  let frameDurationMs = state.playbackTiming.frameDurationMs
  if (frame != null) {
    frameDurationMs = resolveOverlayFrameDurationMs(frame, fullQueue)
  }
  else if (lastPresentationNotifiedIdx.value >= 0 && lastPresentationNotifiedIdx.value < fullQueue.length) {
    frameDurationMs = Math.max(0, (fullQueue[lastPresentationNotifiedIdx.value]!.duration ?? 0) * 1000)
  }
  const houseCycleDurationMs = houseEditorialRotationCycleDurationMs(campaign.value, fullQueue)
  const playlistDurationMs = houseCycleDurationMs ?? materializedPlaylistDurationMs(fullQueue)
  const frameElapsedMs = Math.min(
    Math.max(0, state.playbackTiming.frameElapsedMs),
    frameDurationMs > 0 ? frameDurationMs : Number.POSITIVE_INFINITY
  )
  const houseCycleElapsedMs = frame != null
    ? houseEditorialRotationElapsedMs(campaign.value, fullQueue, frame, frameElapsedMs)
    : null
  const playlistElapsedMs = houseCycleElapsedMs != null
    ? houseCycleElapsedMs
    : frame != null && fullQueue.length > 0
      ? materializedPlaylistElapsedMs(fullQueue, frame, frameElapsedMs)
      : state.playbackTiming.playlistElapsedMs
  const lastFrameResolved = resolveTimersLastFrameDurationMs({
    committedLastFrameDurationMs: lastFrameDurationMsForOverlay,
    pendingLastFrameDurationMs: pendingLastFrameDurationMsForOverlay,
    frameElapsedMs
  })
  lastFrameDurationMsForOverlay = lastFrameResolved.lastFrameDurationMs
  pendingLastFrameDurationMsForOverlay = lastFrameResolved.pendingLastFrameDurationMs
  state.playbackTiming = {
    ...state.playbackTiming,
    frameDurationMs,
    frameElapsedMs,
    playlistDurationMs: playlistDurationMs > 0 ? playlistDurationMs : state.playbackTiming.playlistDurationMs,
    playlistElapsedMs,
    lastFrameDurationMs: lastFrameDurationMsForOverlay
  }
  notePlaybackTimingSampleForStuckDetect(state.playbackTiming)
  updatePlaybackTimerStuckDetection()
}

let playbackHeartbeatCadence: PlaybackHeartbeatCadenceHandle | null = null
/**
 * Replaced after {@link getProofOfPlayPayload} is defined. Commits call this so heartbeat cadence does not depend
 * on Vue watch edge cases (computed length staying stable, flush order, etc.).
 */
let syncPlaybackHeartbeatCadence: () => void = () => {}

/** Accumulated on-screen time per asset since the last playback-heartbeat (ms). */
const assetDurationMsByUrn = new Map<string, number>()
/** Spots started per URN in the open flush window (P0″). Cleared with duration on flush. */
const assetPlayCountByUrn = new Map<string, number>()
/**
 * Monotonic start of the open on-air interval (`performance.now`).
 * Wall clock must not be used for duration — device clock jumps inflate PoP (INC-20261022-POP-1D7D).
 */
let frameStartMonoMs: number | null = null
/** Scheduled duration (seconds) of the frame that opened `frameStartMonoMs` (for per-delta ceiling). */
let lastScheduledDurationSeconds: number | null = null
let lastAssetUrns: string[] = []
/** Last frame identity that already counted a spot — avoid remount/ref-churn inflation. */
let lastPopCountedFrame: Pick<IQueueFrame, 'id' | 'playlistUrn' | 'playlistSeqNo'> | null = null

function getAssetUrnsFromFrame(frame: IQueueFrame | null | undefined): string[] {
  if (!frame) { return [] }
  const facets = frame.facets ?? {}
  return [...new Set(Object.values(facets).map(f => f.asset).filter((asset): asset is string => Boolean(asset)))]
}

/**
 * Accumulate clamped monotonic delta onto the given URNs (defaults to `lastAssetUrns`).
 * Returns the end mono time used so callers can reopen the interval.
 */
function accumulateOpenProofOfPlayInterval(
  endMonoMs: number = defaultMonotonicNowMs(),
  urns: readonly string[] = lastAssetUrns
): number {
  if (frameStartMonoMs != null && urns.length > 0) {
    const { deltaMs } = measureProofOfPlayDeltaMs({
      startMonoMs: frameStartMonoMs,
      endMonoMs,
      scheduledDurationSeconds: lastScheduledDurationSeconds
    })
    if (deltaMs != null && deltaMs > 0) {
      for (const urn of urns) {
        const current = assetDurationMsByUrn.get(urn) ?? 0
        assetDurationMsByUrn.set(urn, current + deltaMs)
      }
    }
  }
  return endMonoMs
}

function scheduledDurationSecondsFromFrame(frame: IQueueFrame | null | undefined): number | null {
  const d = frame?.duration
  return typeof d === 'number' && Number.isFinite(d) && d > 0 ? d : null
}

/** Map an actively playing `<video>` to its buffer frame (VNode fallthrough `@playing` is unreliable). */
function isVideoElementActivelyPlaying(el: HTMLVideoElement): boolean {
  return el.currentTime > 0 && !el.paused && !el.ended && el.readyState > HTMLMediaElement.HAVE_CURRENT_DATA
}

function syncPlayingFrameFromActiveVideo(): void {
  for (const el of document.querySelectorAll('video')) {
    // Phase B sticky overlay remounts the outgoing creative; ignore it for PoP / playingFrame.
    if (!(el instanceof HTMLVideoElement)
      || el.closest('[data-lucimark-sticky-hold]') != null
      || !isVideoElementActivelyPlaying(el)) {
      continue
    }
    const videoSrc = el.currentSrc || el.src
    // Only pin PoP when the playing element maps to the *committed* on-air surface frame.
    // Do not fall back to "any video in the buffer" — that re-pins preload / sticky leftovers (POP-1).
    const { viewIdx, frames: buf } = playbackSurface.value
    const onAir = viewIdx >= 0 && viewIdx < buf.length ? buf[viewIdx] : null
    if (onAir == null) {
      continue
    }
    const kind = frameMainMediaKind(onAir)
    if (kind !== 'video-blob' && kind !== 'hls') {
      continue
    }
    for (const urn of getAssetUrnsFromFrame(onAir)) {
      const entry = unref(assetIndex)[urn]
      if (entry != null && typeof entry === 'object' && 'src' in entry && entry.src === videoSrc) {
        playingFrame.value = onAir
        return
      }
    }
  }
}

/** ~1 Hz media metadata sample for duration invariant checks (not per-rAF). */
function sampleActiveVideoMediaDurationSec(): number | null {
  for (const el of document.querySelectorAll('video')) {
    if (!(el instanceof HTMLVideoElement) || !isVideoElementActivelyPlaying(el)) {
      continue
    }
    const durationSec = el.duration
    if (Number.isFinite(durationSec) && durationSec > 0) {
      return durationSec
    }
  }
  return null
}

function onVideoElementPlaying(ev: Event): void {
  if (!(ev.target instanceof HTMLVideoElement)) {
    return
  }
  if (ev.target.closest('[data-lucimark-sticky-hold]') != null) {
    return
  }
  syncPlayingFrameFromActiveVideo()
}

/** Frame currently used for proof-of-play (playing frame or visible buffer frame). */
const activeFrameForProofOfPlay = computed<IQueueFrame | null>(() => {
  const { viewIdx, frames: buf } = playbackSurface.value
  if (buf.length === 0) { return null }
  const surfaceFrame = buf[viewIdx] ?? buf[0] ?? null
  // Prefer playing only when it matches the committed on-air slot. After video→video sticky
  // skip, surface advances while playingFrame can still point at the outgoing clip until the
  // next `playing` event — credit the surface (same as sticky-hold path historically).
  return resolveActiveFrameForProofOfPlay({
    surfaceFrame,
    playingFrame: playingFrame.value
  })
})

/** When the visible frame changes, record start time and previous frame duration for the timer overlay (use queue for canonical duration). */
watch(
  currentFrame,
  (frame, oldFrame) => {
    if (frame?.id !== oldFrame?.id || frame?.duration !== oldFrame?.duration) {
      if (oldFrame != null) {
        const fullQueue = queue.value ?? []
        const durationMs = resolveOverlayFrameDurationMs(oldFrame, fullQueue)
        if (diagnosticsVisibleFrameStartMs > 0) {
          lastCompletedFrameTransition = {
            atMs: Date.now(),
            dwellMs: Math.max(0, Date.now() - diagnosticsVisibleFrameStartMs),
            durationMs,
            frameId: oldFrame.id
          }
        }
        const stamped = stampTimersLastFrameOnTransition({ outgoingFrameDurationMs: durationMs })
        pendingLastFrameDurationMsForOverlay = stamped.pendingLastFrameDurationMs
        lastFrameDurationMsForOverlay = stamped.committedLastFrameDurationMs
      }
      const nowMs = Date.now()
      visibleFrameStartMs = nowMs
      diagnosticsVisibleFrameStartMs = nowMs
      lastTimingFrameElapsedMsForStuckDetect = null
      lastTimingChangeMsForStuckDetect = null
      if (state.showTimersOverlay && state.playbackTiming) {
        mergeVisibleFrameIntoPlaybackTiming()
      }
      else {
        updatePlaybackTimerStuckDetection()
      }
      // Cold path (frame transition): layout canvas + painted media aspect → Sentry if vertical.
      try {
        const layout = frame?.layout
        reportPlaybackAspectTelemetrySample({
          onAirFrameId: frame?.id ?? null,
          onAirFrameLayoutWidthPx: layout?.width ?? null,
          onAirFrameLayoutHeightPx: layout?.height ?? null,
          includeVisibleMediaRect: true
        })
      }
      catch {
        /* never break frame transitions */
      }
    }
  },
  { flush: 'post' }
)

/** Track per-asset durations + spot starts whenever the active frame changes (registered tenant). */
watch(
  [activeFrameForProofOfPlay, () => state.tenant?.proofOfPlay !== false && state.tenant != null],
  ([frame, enabled]) => {
    if (!enabled) {
      frameStartMonoMs = null
      lastScheduledDurationSeconds = null
      lastAssetUrns = []
      lastPopCountedFrame = null
      assetDurationMsByUrn.clear()
      assetPlayCountByUrn.clear()
      return
    }
    const nowMono = accumulateOpenProofOfPlayInterval()
    frameStartMonoMs = nowMono
    lastScheduledDurationSeconds = scheduledDurationSecondsFromFrame(frame)
    lastAssetUrns = getAssetUrnsFromFrame(frame)
    // Count a spot only when presentation identity changes (not buffer remount / ref churn).
    const presentationChanged = frame != null
      && (lastPopCountedFrame == null || !sameQueueFrameIdentity(lastPopCountedFrame, frame))
    if (presentationChanged) {
      lastPopCountedFrame = {
        id: frame.id,
        playlistUrn: frame.playlistUrn,
        playlistSeqNo: frame.playlistSeqNo
      }
      for (const urn of lastAssetUrns) {
        assetPlayCountByUrn.set(urn, (assetPlayCountByUrn.get(urn) ?? 0) + 1)
      }
    }
  },
  { immediate: true }
)

/** Last asset list passed through {@link buildAssetIndex}; used to skip remounts when only queue facets changed. */
let lastBuiltCampaignAssets: TCampaignAsset[] | null = null

/**
 * Rebuild asset-index entries for the given URNs (new object URLs / VideoContainer vnodes).
 * Used for widget SW recovery and after asset-cache repair when blobs were not purged.
 */
async function buildAssetIndex(forceRemountUrns: Set<string> = new Set()) {
  const assets = getPlaybackAssets()
  const requiredAssetIds = new Set(assets.map(({ urn }) => urn))
  /** Snapshot before remounts so on-air creatives keep their current blob URL / vnode (#1352). */
  const retainUrns = collectOnScreenAssetUrns()
  for (const asset of assets) {
    const urn = asset.urn
    const isUrlWidget = 'urlTemplate' in asset && !!(asset as { urlTemplate?: string }).urlTemplate
    /**
     * Keep on-air blob / vnode entries across same-identity rebuilds (#1352).
     * URL widgets have no blob continuity — always fall through to the signature refresh path.
     */
    if (
      !isUrlWidget
      && retainUrns.has(urn)
      && unref(assetIndex)[urn] != null
      && !forceRemountUrns.has(urn)
    ) {
      continue
    }
    // URL widgets: refresh metadata when it changes; skip no-op updates so registration-status polls do not reset iframeLoaded / flash the slot.
    if (isUrlWidget) {
      const urlWidget = asset as { urn: string, urlTemplate: string, width?: number, height?: number, paramKeysOmitWhenEmpty?: string[], paramKeysFlag?: string[], fixedDefaultConfig?: Record<string, unknown>, defaultConfig?: Record<string, unknown> }
      const urlWidgetSig = canonicalStringify({
        urlTemplate: urlWidget.urlTemplate,
        width: urlWidget.width,
        height: urlWidget.height,
        paramKeysOmitWhenEmpty: urlWidget.paramKeysOmitWhenEmpty,
        paramKeysFlag: urlWidget.paramKeysFlag,
        fixedDefaultConfig: urlWidget.fixedDefaultConfig,
        defaultConfig: urlWidget.defaultConfig
      })
      const prevEntry = unref(assetIndex)[urn]
      if (
        prevEntry
        && typeof prevEntry === 'object'
        && 'isWidget' in prevEntry
        && prevEntry.isWidget === true
        && 'urlTemplate' in prevEntry
        && typeof (prevEntry as { urlTemplate?: unknown }).urlTemplate === 'string'
      ) {
        const p = prevEntry as {
          urlTemplate: string
          width?: number
          height?: number
          paramKeysOmitWhenEmpty?: string[]
          paramKeysFlag?: string[]
          fixedDefaultConfig?: Record<string, unknown>
          defaultConfig?: Record<string, unknown>
        }
        const prevSig = canonicalStringify({
          urlTemplate: p.urlTemplate,
          width: p.width,
          height: p.height,
          paramKeysOmitWhenEmpty: p.paramKeysOmitWhenEmpty,
          paramKeysFlag: p.paramKeysFlag,
          fixedDefaultConfig: p.fixedDefaultConfig,
          defaultConfig: p.defaultConfig
        })
        if (prevSig === urlWidgetSig) {
          continue
        }
      }
      unref(assetIndex)[urn] = {
        isWidget: true,
        urlTemplate: urlWidget.urlTemplate,
        iframeLoaded: false,
        ...(urlWidget.width != null && urlWidget.width >= 0 ? { width: urlWidget.width } : {}),
        ...(urlWidget.height != null && urlWidget.height >= 0 ? { height: urlWidget.height } : {}),
        ...(Array.isArray(urlWidget.paramKeysOmitWhenEmpty) && urlWidget.paramKeysOmitWhenEmpty.length > 0 ? { paramKeysOmitWhenEmpty: urlWidget.paramKeysOmitWhenEmpty } : {}),
        ...(Array.isArray(urlWidget.paramKeysFlag) && urlWidget.paramKeysFlag.length > 0 ? { paramKeysFlag: urlWidget.paramKeysFlag } : {}),
        ...(urlWidget.fixedDefaultConfig && Object.keys(urlWidget.fixedDefaultConfig).length > 0 ? { fixedDefaultConfig: urlWidget.fixedDefaultConfig } : {}),
        ...(urlWidget.defaultConfig && Object.keys(urlWidget.defaultConfig).length > 0 ? { defaultConfig: urlWidget.defaultConfig } : {})
      }
      scheduleIframeLoadTimeout(urn)
      continue
    }
    const prevEntry = unref(assetIndex)[urn]
    const incomingBlob
      = asset != null && typeof asset === 'object' && 'blob' in asset && asset.blob instanceof Blob
        ? asset.blob
        : null
    const incomingNativePath
      = asset != null
        && typeof asset === 'object'
        && 'nativeFilePath' in asset
        && typeof (asset as { nativeFilePath?: unknown }).nativeFilePath === 'string'
        ? String((asset as { nativeFilePath: string }).nativeFilePath).trim()
        : ''
    const incomingType
      = asset != null && typeof asset === 'object' && 'type' in asset && typeof (asset as { type?: unknown }).type === 'string'
        ? (asset as { type: string }).type
        : ''
    const isFileBackedVideoAsset = incomingNativePath !== '' && incomingType.includes('video') && incomingBlob == null
    const blobRefreshed
      = incomingBlob != null
        && prevEntry != null
        && typeof prevEntry === 'object'
        && 'blob' in prevEntry
        && prevEntry.blob instanceof Blob
        && prevEntry.blob !== incomingBlob
    const nativePathRefreshed
      = isFileBackedVideoAsset
        && prevEntry != null
        && typeof prevEntry === 'object'
        && (
          !('nativeFilePath' in prevEntry)
          || (prevEntry as { nativeFilePath?: string }).nativeFilePath !== incomingNativePath
        )

    const hlsUrl = (asset as { metadata?: { hlsUrl?: string } }).metadata?.hlsUrl
    const isHlsVideoAsset = typeof hlsUrl === 'string' && hlsUrl.length > 0
    const isBlobVideoAsset = incomingBlob != null && incomingBlob.type.includes('video')
    const allowVideoOnCapacity = isVideoPlaybackAllowedOnCapacity(
      state.capacityTier,
      state.tenant?.playerAllowVideoOnLowCapacity
    )
    const videoCapacityPolicyRemount
      = (isHlsVideoAsset || isBlobVideoAsset || isFileBackedVideoAsset)
        && shouldRemountVideoForCapacityPolicy(prevEntry, allowVideoOnCapacity)

    if (!prevEntry || forceRemountUrns.has(urn) || blobRefreshed || nativePathRefreshed || videoCapacityPolicyRemount) {
      if (isHlsVideoAsset) {
        const manifestUrl = hlsUrl as string
        if (allowVideoOnCapacity) {
          unref(assetIndex)[urn] = {
            src: manifestUrl,
            vnode: h(VideoContainer, { src: manifestUrl, isHls: true }),
            isHlsVideo: true
          }
        }
        else {
          // Queue filter should have skipped video frames; no SECTION UNAVAILABLE notice.
          unref(assetIndex)[urn] = {
            src: '',
            vnode: h('div', { class: 'h-full w-full bg-black', 'aria-hidden': 'true' }),
            isHlsVideo: true
          }
        }
        if (videoCapacityPolicyRemount && prevEntry != null) {
          releasePreviousBinaryAssetEntry(urn, prevEntry)
        }
        continue
      }
      // P2: progressive MP4 already on disk — no IDB blob required for Exo.
      if (isFileBackedVideoAsset) {
        if (allowVideoOnCapacity) {
          if (nativePlaybackSurfaceEnabled.value && !nativeHybridSurfaceBlocked(urn)) {
            unref(assetIndex)[urn] = {
              src: '',
              vnode: h('div', {
                class: 'h-full w-full bg-transparent',
                'aria-hidden': 'true',
                'data-lucimark-native-video': 'true'
              }),
              isVideoBlob: true as const,
              isNativeVideo: true as const,
              nativeFilePath: incomingNativePath
            }
          }
          else {
            const hydrated = await readHybridPlaybackFileAsBlob(
              urn,
              incomingType.includes('video') ? incomingType : 'video/mp4'
            )
            if (hydrated == null) {
              continue
            }
            const src = URL.createObjectURL(hydrated)
            unref(assetIndex)[urn] = {
              src,
              blob: hydrated,
              vnode: h(VideoContainer, { src }),
              isVideoBlob: true as const
            }
          }
        }
        else {
          unref(assetIndex)[urn] = {
            src: '',
            vnode: h('div', { class: 'h-full w-full bg-black', 'aria-hidden': 'true' }),
            isVideoBlob: true as const,
            nativeFilePath: incomingNativePath
          }
        }
        if ((nativePathRefreshed || videoCapacityPolicyRemount || forceRemountUrns.has(urn)) && prevEntry != null) {
          releasePreviousBinaryAssetEntry(urn, prevEntry)
        }
        continue
      }
      const blob = (asset as { blob?: Blob }).blob
      if (blob == null) {
        continue
      }
      let src: string
      let vnode: VNode | null = null
      let usesIframe = false
      let isVideoBlob = false
      let isNativeVideo = false
      if (blob.type.includes('image')) {
        src = URL.createObjectURL(blob)
        if (isPortalTailAssetUrn(urn)) {
          vnode = h(PortalTailFillerImage, { src, assetUrn: urn })
        }
        else {
          vnode = h(ImageContainer, { src })
        }
      }
      else if (blob.type.includes('video')) {
        isVideoBlob = true
        if (allowVideoOnCapacity) {
          if (nativePlaybackSurfaceEnabled.value && !nativeHybridSurfaceBlocked(urn)) {
            isNativeVideo = true
            src = ''
            // Transparent so Media3 TextureView under the WebView remains visible.
            vnode = h('div', {
              class: 'h-full w-full bg-transparent',
              'aria-hidden': 'true',
              'data-lucimark-native-video': 'true'
            })
          }
          else {
            src = URL.createObjectURL(blob)
            vnode = h(VideoContainer, { src })
          }
        }
        else {
          // Queue filter should have skipped video frames; no SECTION UNAVAILABLE notice.
          src = ''
          vnode = h('div', { class: 'h-full w-full bg-black', 'aria-hidden': 'true' })
        }
      }
      else if (blob.type === 'text/html') {
        usesIframe = true
        if (useAndroidHtmlFallback()) {
          if (shouldUseSrcdocForHtmlBlob(blob)) {
            const srcdoc = await htmlBlobToSrcdoc(blob)
            src = ''
            vnode = h('iframe', {
              class: 'w-full h-full',
              srcdoc,
              sandbox: 'allow-scripts',
              onLoad: () => markAssetIframeLoaded(urn)
            })
          }
          else {
            src = await htmlBlobToDataUrl(blob)
            vnode = h('iframe', {
              class: 'w-full h-full',
              src,
              sandbox: 'allow-scripts',
              onLoad: () => markAssetIframeLoaded(urn)
            })
          }
        }
        else {
          src = URL.createObjectURL(blob)
          vnode = h('iframe', {
            class: 'w-full h-full',
            src,
            sandbox: 'allow-scripts',
            onLoad: () => markAssetIframeLoaded(urn)
          })
        }
      }
      else if (isWidgetTgzBlob(blob) || (await isWidgetTgzBlobAsync(blob))) {
        if (forceRemountUrns.has(urn)) {
          // Revoke any blob URL from a previous failed mount before remounting.
          const prevEntry = unref(assetIndex)[urn]
          if (prevEntry && 'src' in prevEntry && typeof prevEntry.src === 'string' && prevEntry.src.startsWith('blob:')) {
            URL.revokeObjectURL(prevEntry.src)
          }
        }
        /**
         * For `.tgz` widgets, {@link mountWidgetBundle} repopulates the SW prefix for this `urn`. Unmount the previous
         * bundle **only** right before mounting so we do not leave a long blank while async work runs; do not revoke
         * blob URLs here (handled after assign for non-widget paths).
         */
        if (blobRefreshed && prevEntry != null && 'blob' in prevEntry && prevEntry.blob instanceof Blob && isWidgetTgzBlob(prevEntry.blob)) {
          unmountWidgetBundle(urn)
        }
        try {
          const [widgetSrc, pkg] = await Promise.all([
            mountWidgetBundle(urn, blob, window.location.origin),
            getWidgetPackageJsonFromBlob(blob)
          ])
          src = widgetSrc
          const widgetWidth = pkg?.lucimark?.width
          const widgetHeight = pkg?.lucimark?.height
          const responsive = normalizeWidgetAspectRatioPolicy(pkg?.lucimark?.aspectRatioPolicy) === 'optional'
          const dims
            = !responsive
              && typeof widgetWidth === 'number'
              && widgetWidth > 0
              && typeof widgetHeight === 'number'
              && widgetHeight > 0
              ? { width: widgetWidth, height: widgetHeight }
              : {}
          unref(assetIndex)[urn] = {
            src,
            blob,
            isWidget: true,
            iframeLoaded: false,
            ...dims
          }
          scheduleIframeLoadTimeout(urn)
          continue
        }
        catch (err) {
          console.warn('[VBuffer] Widget mount failed for', urn, err)
          let pkg: Awaited<ReturnType<typeof getWidgetPackageJsonFromBlob>> | null = null
          try {
            pkg = await getWidgetPackageJsonFromBlob(blob)
          }
          catch {
            pkg = null
          }

          const htmlBlob = await extractWidgetIndexHtmlBlob(blob)
          if (htmlBlob) {
            if (useAndroidHtmlFallback()) {
              src = await htmlBlobToDataUrl(htmlBlob)
            }
            else {
              src = URL.createObjectURL(htmlBlob)
            }
          }
          else {
            /** Broken package — do not pretend the iframe loaded (timeout would clear "widgets loading"). */
            src = 'about:blank'
          }

          const widgetWidth = pkg?.lucimark?.width
          const widgetHeight = pkg?.lucimark?.height
          const responsive = normalizeWidgetAspectRatioPolicy(pkg?.lucimark?.aspectRatioPolicy) === 'optional'
          const dims
            = !responsive
              && typeof widgetWidth === 'number'
              && widgetWidth > 0
              && typeof widgetHeight === 'number'
              && widgetHeight > 0
              ? { width: widgetWidth, height: widgetHeight }
              : {}
          unref(assetIndex)[urn] = {
            src,
            blob,
            isWidget: true,
            iframeLoaded: false,
            ...dims
          }
          /**
           * SW-mount fallback (HTML-only blob/data URL or about:blank): do not timeout-mark ready.
           * HTML-only shells must prove load via the iframe event; about:blank stays a hard fail.
           */
          continue
        }
      }
      else {
        throw new Error(`asset ${urn}: invalid type ${blob.type}`)
      }
      unref(assetIndex)[urn] = {
        src,
        blob,
        vnode,
        ...(isVideoBlob ? { isVideoBlob: true as const } : {}),
        ...(isNativeVideo ? { isNativeVideo: true as const } : {}),
        ...(usesIframe ? { usesIframe: true as const, iframeLoaded: false } : {})
      }
      if (isNativeVideo) {
        // Drop any prior stage promise so blob refresh / remount cannot reuse a stale path.
        nativeStagePromises.delete(urn)
        // Prefer campaign `nativeFilePath` (P2) when present; else stage from IDB blob (P1b).
        const cachedPath
          = asset != null
            && typeof asset === 'object'
            && 'nativeFilePath' in asset
            && typeof (asset as { nativeFilePath?: unknown }).nativeFilePath === 'string'
            ? String((asset as { nativeFilePath: string }).nativeFilePath).trim()
            : ''
        if (cachedPath !== '') {
          const entry = unref(assetIndex)[urn]
          if (entry != null && typeof entry === 'object') {
            unref(assetIndex)[urn] = { ...entry, nativeFilePath: cachedPath }
          }
        }
        else if (blob instanceof Blob) {
          void ensureNativeVideoStaged(urn, blob)
        }
      }
      /**
       * Revoke / unmount **after** the new entry is live so image/video/html never sit on a revoked `blob:` URL
       * while `await` or `URL.createObjectURL` work runs (visible flicker when billing toggles refetch binaries).
       */
      if ((blobRefreshed || videoCapacityPolicyRemount || forceRemountUrns.has(urn)) && prevEntry != null) {
        const prevWasTgzWidget = 'blob' in prevEntry && prevEntry.blob instanceof Blob && isWidgetTgzBlob(prevEntry.blob)
        if (!prevWasTgzWidget) {
          releasePreviousBinaryAssetEntry(urn, prevEntry)
        }
      }
      if (usesIframe) {
        scheduleIframeLoadTimeout(urn)
      }
    }
  }

  const assetsToRevoke = assetIndexUrnsSafeToRevoke({
    indexedUrns: Object.keys(unref(assetIndex)),
    requiredAssetIds,
    retainUrns
  })
  assetsToRevoke.forEach((assetId) => {
    revokeAssetIndexEntry(assetId)
  })
  lastBuiltCampaignAssets = assets
  if (pendingFlushAfterAssetBuild) {
    pendingFlushAfterAssetBuild = false
  }
  flushPendingPlaybackSurfaceCommit()
}

function resolvePlaybackBufferSegments(segments: PlaybackSegment[]): IQueueFrame[] {
  if (segments.length === 0) {
    return []
  }
  const q = unref(queue) ?? []
  const pl = unref(campaign)?.playlists
  let resolved = resolveQueueFramesFromWorkerPlaybackPlan(segments, q, pl)
  if (resolved.length === 0 && q.length > 0) {
    const plan = workerPlaybackPlanFromQueue()
    const fallbackLen = Math.min(2, plan.length)
    if (fallbackLen > 0) {
      resolved = resolveQueueFramesFromWorkerPlaybackPlan(plan.slice(0, fallbackLen), q, pl)
    }
  }
  return resolved
}

/** Last payload applied to PlaybackScheduler via debounced sync; avoids redundant setState when registration-status refires with identical spine + sync inputs (S1). */
let lastPlaybackWorkerSyncKey = ''
/** Portal book: last plan spine hash flushed to worker (skip cosmetic queue-hash-only resync mid-loop). */
let lastFlushedPortalBookPlanSpineHash = ''
/** While true, {@link syncPlaybackWorkerStateNow} is deferred until {@link initWorker} finishes (cold-start bootstrap). */
if (peekPlaybackWorkerInitGeneration() === 0) {
  setInitWorkerBootstrapPending(true)
}
/** Block {@link flushQueueAssetsWatch} worker sync until bootstrap finishes. */
let suppressQueueWatchWorkerSyncUntilMs = performance.now() + 10_000
/** Wall time of last house embed advance after a full loop (guards spurious sub-duration loop wraps). */
let lastHouseEmbedLoopAdvanceWallMs = 0

/** Call after local mutations that must reach the worker even when queue hash is unchanged (e.g. widget remount query params). */
function invalidatePlaybackWorkerSyncKey(): void {
  lastPlaybackWorkerSyncKey = ''
}

async function resolveResumePlanIdxForWorkerSyncAsync(playbackPlan: PlaybackSegment[]): Promise<number | null> {
  const peak = getPeakPresentationPlanIdx()
  let resumePlanIdx = resolveResumePlanIdxForWorkerSync()
  const sess = getPlaybackPresentationSession()
  if (resumePlanIdx == null && peak >= 0) {
    resumePlanIdx = peak
  }
  if (resumePlanIdx == null && sess.lastLoggedPlanIdx >= 0) {
    resumePlanIdx = sess.lastLoggedPlanIdx
  }
  if (resumePlanIdx == null && lastPresentationNotifiedIdx.value >= 0) {
    resumePlanIdx = lastPresentationNotifiedIdx.value
  }
  if (resumePlanIdx == null && playbackPlan.length > 0) {
    const onAir = playbackSurface.value.frames[playbackSurface.value.viewIdx]
    if (onAir != null) {
      const directIdx = playbackPlan.findIndex(seg => seg.frameId === onAir.id)
      if (directIdx >= 0) {
        resumePlanIdx = directIdx
      }
    }
  }
  if (resumePlanIdx == null && sess.lastOnAirFrameId != null && playbackPlan.length > 0) {
    const byStoredId = playbackPlan.findIndex(seg => seg.frameId === sess.lastOnAirFrameId)
    if (byStoredId >= 0) {
      resumePlanIdx = byStoredId
    }
  }
  if (resumePlanIdx == null) {
    try {
      const loop = await playbackScheduler.getLoopStateForTransition()
      const workerResume = Math.max(
        loop.lastSyncedResumePlanIdx,
        loop.lastStableNotifiedFrameIdx,
        loop.notifiedIdx
      )
      if (workerResume >= 0 && loop.segments.length > 0) {
        resumePlanIdx = peak >= 0 && workerResume < peak ? peak : workerResume
        recordPresentationPlanIdx(resumePlanIdx)
        lastPresentationNotifiedIdx.value = resumePlanIdx
      }
    }
    catch {
      // ignore
    }
  }
  if (
    resumePlanIdx === 0
    && peak >= 1
    && playbackPlan.length > 1
    && !playbackPlanHasBookCps(playbackPlan)
  ) {
    resumePlanIdx = peak
  }
  return resumePlanIdx
}

let playbackWorkerSyncChain: Promise<void> = Promise.resolve()

async function syncPlaybackWorkerStateNow(
  platform?: string,
  opts?: {
    immediate?: boolean
    registrationScheduleReplace?: boolean
    loopWrapScheduleResync?: boolean
    /** When set (e.g. loop-wrap), use the plan from the last materialize instead of re-deriving from Vue state. */
    playbackPlanOverride?: PlaybackSegment[]
    /** Bypass {@link initWorkerBootstrapPending} for the single post-init worker push. */
    allowDuringInit?: boolean
  }
): Promise<void> {
  const run = async (): Promise<void> => {
    if (isInitWorkerBootstrapPending() && opts?.allowDuringInit !== true && opts?.registrationScheduleReplace !== true) {
      return
    }
    const q = unref(queue) ?? []
    const pg = unref(playerGroup)
    const playbackEpochWallMs = null as number | null
    const clockOffsetMs = 0
    const snapshotPlan = q.length > 0 ? getWorkerPlaybackPlanNow() : []
    const queueDerivedPlan = workerPlaybackPlanFromQueue()
    /** Prefer materialized snapshot / explicit override — matches {@link resolveQueueFramesAt} segments immediately after rebuild. */
    const playbackPlan = opts?.playbackPlanOverride?.length
      ? opts.playbackPlanOverride
      : snapshotPlan.length > 0
        ? snapshotPlan
        : queueDerivedPlan
    if (
      q.length > 0
      && snapshotPlan.length > 0
      && queueDerivedPlan.length > 0
      && getPlaybackWorkerPayloadSequenceHash(snapshotPlan)
      !== getPlaybackWorkerPayloadSequenceHash(queueDerivedPlan)
    ) {
      console.warn('[VBuffer] worker snapshot plan !== queue-derived plan; syncing worker from snapshot', {
        snapshotLen: snapshotPlan.length,
        queueLen: q.length
      })
    }
    const queueContentHash = q.length > 0 ? getQueueHash(q) : HASH_EMPTY
    const planSpineHash = getPlaybackWorkerPayloadSequenceHash(playbackPlan)
    const syncKey = [
      queueContentHash,
      planSpineHash,
      getPlayerGroupPlaybackWorkerSyncFingerprint(pg),
      String(clockOffsetMs),
      platform ?? state.platform ?? '',
      String(q.length)
    ].join('|')
    let resumePlanIdx = await resolveResumePlanIdxForWorkerSyncAsync(playbackPlan)
    const mainHasBookCps = playbackPlanHasBookCps(playbackPlan)
    const planHeadFrameId = playbackPlan[0]?.frameId
    const visibleOnAir = playbackSurface.value.frames[playbackSurface.value.viewIdx]
    const visibleAtPlanHead = visibleOnAir != null
      && planHeadFrameId != null
      && visibleOnAir.id === planHeadFrameId
    const portalBookAuthority = mainHasBookCps ? portalBookPresentationAuthority() : -1
    const portalBookLoopWrapResync = opts?.loopWrapScheduleResync === true
      || (
        mainHasBookCps
        && playbackPlan.length > 1
        && resumePlanIdx === 0
        && portalBookAuthority === playbackPlan.length - 1
        && visibleAtPlanHead
      )
    let workerSetOpts = opts?.loopWrapScheduleResync === true
      ? { loopWrapScheduleResync: true as const }
      : opts?.registrationScheduleReplace === true
        ? { registrationScheduleReplace: true as const }
        : undefined
    if (portalBookLoopWrapResync) {
      resumePlanIdx = 0
      workerSetOpts = { loopWrapScheduleResync: true }
    }
    if (mainHasBookCps && playbackPlan.length > 1 && resumePlanIdx != null && resumePlanIdx >= 0) {
      const authority = portalBookPresentationAuthority()
      const planLen = playbackPlan.length
      if (isSpuriousPortalBookMidLoopHouseReset(authority, resumePlanIdx, planLen)) {
        resumePlanIdx = authority
      }
      else if (
        authority >= 0
        && resumePlanIdx !== authority
        && !shouldAcceptPortalBookResumePlanIdx(authority, resumePlanIdx, planLen)
      ) {
        resumePlanIdx = (authority + 1) % planLen
      }
    }
    if (resumePlanIdx != null && resumePlanIdx >= 0) {
      await playbackScheduler.syncResumePlanIdx(resumePlanIdx)
    }
    const payload = {
      playbackPlan,
      playbackEpochWallMs,
      clockOffsetMs,
      platform: platform ?? state.platform,
      queueContentHash,
      resumePlanIdx
    }
    if (syncKey === lastPlaybackWorkerSyncKey) {
      /**
       * Same content hash is not enough to skip: loop-wrap / registration replace must still reach
       * the worker so a desynced surface (queue advanced, paint stuck) can be forced to present (#1382).
       */
      if (
        opts?.loopWrapScheduleResync === true
        || opts?.registrationScheduleReplace === true
      ) {
        debouncedSetQueueUpdateState.cancel()
        await playbackScheduler.setStateImmediate(payload, workerSetOpts)
        lastPlaybackWorkerSyncKey = syncKey
        return
      }
      const workerQueueLen = await playbackScheduler.getPlaybackLoopQueueLen()
      const needsWorkerRefill = workerQueueLen === 0 && playbackPlan.length > 0
      const portalBookWorkerStale = mainHasBookCps && playbackPlan.length > 0
        && !(await playbackScheduler.workerPlanHasBookCps())
      if (needsWorkerRefill || portalBookWorkerStale) {
        let authorityIdx = Math.max(
          getPlaybackPresentationSession().lastLoggedPlanIdx,
          lastPresentationNotifiedIdx.value,
          getPeakPresentationPlanIdx()
        )
        if (authorityIdx < 0) {
          try {
            const loop = await playbackScheduler.getLoopStateForTransition()
            const peak = getPeakPresentationPlanIdx()
            const workerResume = Math.max(
              loop.lastSyncedResumePlanIdx,
              loop.lastStableNotifiedFrameIdx,
              loop.notifiedIdx
            )
            if (workerResume >= 0) {
              authorityIdx = peak >= 0 && workerResume < peak ? peak : workerResume
              recordPresentationPlanIdx(authorityIdx)
              lastPresentationNotifiedIdx.value = authorityIdx
            }
          }
          catch {
          // ignore
          }
        }
        if (authorityIdx >= 0) {
          resumePlanIdx = authorityIdx
          payload.resumePlanIdx = authorityIdx
          await playbackScheduler.syncResumePlanIdx(authorityIdx)
        }
        debouncedSetQueueUpdateState.cancel()
        await playbackScheduler.setStateImmediate(payload)
        lastPlaybackWorkerSyncKey = syncKey
      }
      return
    }
    debouncedSetQueueUpdateState.cancel()
    if (opts?.immediate === true || mainHasBookCps) {
      await playbackScheduler.setStateImmediate(payload, workerSetOpts)
    }
    else {
      await playbackScheduler.setState(payload)
    }
    lastPlaybackWorkerSyncKey = syncKey
  }
  const next = playbackWorkerSyncChain.then(run, run)
  playbackWorkerSyncChain = next.catch(() => {})
  return next
}

const debouncedSetQueueUpdateState = debounce(async (_qSnapshot: IQueueFrame[], _assetIndex: IAssetIndex, platform?: string) => {
  await syncPlaybackWorkerStateNow(platform)
}, 100)

/** Push queue + assetIndex to worker. Call after buildAssetIndex completes for the current queue to avoid black screen from missing assets. */
function pushStateToWorker() {
  debouncedSetQueueUpdateState(unref(queue) ?? [], unref(assetIndex), state.platform)
}

/** Mark an iframe asset as loaded (from load event or timeout) and push state. */
function markAssetIframeLoaded(urn: string) {
  const existing = iframeLoadTimeoutByUrn.get(urn)
  if (existing) {
    clearTimeout(existing)
    iframeLoadTimeoutByUrn.delete(urn)
  }
  const idx = unref(assetIndex)
  const entry = idx[urn]
  if (entry) {
    (entry as { iframeLoaded?: boolean }).iframeLoaded = true
    pushStateToWorker()
  }
}

/**
 * Schedule timeout so iframe that never fires load does not block display forever.
 * Skips `about:blank` / empty src — those are hard failures, not slow loads.
 */
function scheduleIframeLoadTimeout(urn: string) {
  const entry = unref(assetIndex)[urn]
  const src = entry && 'src' in entry ? entry.src : ''
  if (!src || src === 'about:blank') {
    return
  }
  const existing = iframeLoadTimeoutByUrn.get(urn)
  if (existing) {
    clearTimeout(existing)
  }
  const id = setTimeout(() => markAssetIframeLoaded(urn), IFRAME_LOAD_TIMEOUT_MS)
  iframeLoadTimeoutByUrn.set(urn, id)
}
provide('onAssetIframeLoaded', markAssetIframeLoaded)

interface QueueAssetsWatchSnapshot {
  queue: IQueueFrame[]
  campaignAssetsVersion: number
  playerGroup: typeof state.playerGroup
  platform: string | undefined
}

/** Debounce queue/assets watch bursts (registration-status emits queue then assets ~ms apart). */
const QUEUE_ASSETS_WATCH_DEBOUNCE_MS = 50

let queueAssetsWatchDebounceTimer: ReturnType<typeof setTimeout> | null = null
let queueAssetsWatchRetryTimer: ReturnType<typeof setTimeout> | null = null
let latestQueueAssetsWatch: QueueAssetsWatchSnapshot | null = null
let lastFlushedQueueHash = HASH_EMPTY

/** Re-run {@link flushQueueAssetsWatch} after a suppress/cooldown window without marking flushed. */
function scheduleQueueAssetsWatchRetry(readyAtMs: number): void {
  if (queueAssetsWatchRetryTimer != null) {
    clearTimeout(queueAssetsWatchRetryTimer)
  }
  const delayMs = Math.max(16, readyAtMs - performance.now() + 16)
  queueAssetsWatchRetryTimer = setTimeout(() => {
    queueAssetsWatchRetryTimer = null
    flushQueueAssetsWatch()
  }, delayMs)
}

function flushQueueAssetsWatch(): void {
  const snap = latestQueueAssetsWatch
  if (snap == null) {
    return
  }
  const q = snap.queue ?? []
  const queueHash = q.length > 0 ? getQueueHash(q) : HASH_EMPTY
  const queueHashChanged = queueHash !== lastFlushedQueueHash
  const assetsVersionChanged = snap.campaignAssetsVersion !== lastFlushedAssetsVersion
  const needWorkerPush = queueHashChanged || assetsVersionChanged || snap.playerGroup !== lastFlushedPlayerGroup || snap.platform !== lastFlushedPlatform
  if (!needWorkerPush) {
    // Sync campaignAssetsVersion watch may have pinned before debounce; nothing to rebuild.
    releaseContentTransitionHoldIfIdle()
    return
  }
  if (performance.now() < suppressQueueWatchWorkerSyncUntilMs) {
    /**
     * Do not mark queue/assets flushed when a rebuild is still owed — otherwise the sync-watch
     * pin is dropped and the new campaign list never enters the index (#1352 review).
     */
    if (assetsVersionChanged || queueHashChanged) {
      scheduleQueueAssetsWatchRetry(suppressQueueWatchWorkerSyncUntilMs)
      return
    }
    lastFlushedQueueHash = queueHash
    lastFlushedAssetsVersion = snap.campaignAssetsVersion
    lastFlushedPlayerGroup = snap.playerGroup
    lastFlushedPlatform = snap.platform
    releaseContentTransitionHoldIfIdle()
    return
  }
  const planForWatch = workerPlaybackPlanFromQueue()
  const planSpineHashForWatch = getPlaybackWorkerPayloadSequenceHash(planForWatch)
  const presentationAuthorityForWatch = portalBookPresentationAuthority()
  if (
    playbackPlanHasBookCps(planForWatch)
    && presentationAuthorityForWatch >= 2
    && queueHashChanged
    && !assetsVersionChanged
    && planSpineHashForWatch.length > 0
    && planSpineHashForWatch === lastFlushedPortalBookPlanSpineHash
  ) {
    lastFlushedQueueHash = queueHash
    lastFlushedAssetsVersion = snap.campaignAssetsVersion
    lastFlushedPlayerGroup = snap.playerGroup
    lastFlushedPlatform = snap.platform
    releaseContentTransitionHoldIfIdle()
    return
  }
  if (performance.now() - lastRegistrationResyncEndedAt < 1200) {
    if (assetsVersionChanged || queueHashChanged) {
      scheduleQueueAssetsWatchRetry(lastRegistrationResyncEndedAt + 1200)
      return
    }
    lastFlushedQueueHash = queueHash
    lastFlushedAssetsVersion = snap.campaignAssetsVersion
    lastFlushedPlayerGroup = snap.playerGroup
    lastFlushedPlatform = snap.platform
    releaseContentTransitionHoldIfIdle()
    return
  }
  const needFullAssetBuild = assetsVersionChanged || queueHashChanged
  const queueLen = q.length
  if (state.registrationPlaybackResyncPending) {
    lastFlushedQueueHash = queueHash
    lastFlushedAssetsVersion = snap.campaignAssetsVersion
    lastFlushedPlayerGroup = snap.playerGroup
    lastFlushedPlatform = snap.platform
    // Content pin from sync watch must not outlive a deferred registration resync flush.
    releaseContentTransitionHoldIfIdle()
    if (needFullAssetBuild && queueLen > 0) {
      queueAssetResyncInProgress.value = true
      void limiter.schedule(buildAssetIndex).then(() => {
        queueAssetResyncInProgress.value = false
        flushPendingPlaybackSurfaceCommit()
      }).catch((err) => {
        queueAssetResyncInProgress.value = false
        console.error('error while building asset index during registration resync...', err)
      })
    }
    return
  }
  if (needFullAssetBuild) {
    queueAssetResyncInProgress.value = true
    if (queueLen > 0) {
      /**
       * Same-identity asset edits do not go through {@link applyRegistrationPlaybackResync}.
       * Pin the current buffer so S1 / worker sync cannot remount mid-dwell while blobs revoke (#1352).
       */
      pinContentTransitionHoldFromCurrentSurface()
    }
  }
  const finishResync = () => {
    lastFlushedQueueHash = queueHash
    lastFlushedAssetsVersion = snap.campaignAssetsVersion
    lastFlushedPlayerGroup = snap.playerGroup
    lastFlushedPlatform = snap.platform
    if (playbackPlanHasBookCps(planForWatch)) {
      lastFlushedPortalBookPlanSpineHash = planSpineHashForWatch
    }
    const releaseContentHold = needFullAssetBuild
    void (async () => {
      if (isInitWorkerBootstrapPending() || performance.now() < suppressQueueWatchWorkerSyncUntilMs) {
        queueAssetResyncInProgress.value = false
        // Only drop a content-owned pin — never a registration resync hold (#1352 review).
        releaseContentTransitionHoldIfOwned()
        return
      }
      try {
        await syncPlaybackWorkerStateNow(state.platform, { immediate: true })
      }
      catch (err) {
        console.error('error while syncing playback worker state...', err)
      }
      finally {
        queueAssetResyncInProgress.value = false
        flushPendingPlaybackSurfaceCommit()
        const release = () => {
          releaseContentTransitionHoldIfOwned()
          purgeAssetIndexEntriesNotNeeded()
        }
        if (releaseContentHold) {
          requestAnimationFrame(release)
        }
        else {
          release()
        }
      }
    })()
  }
  if (needFullAssetBuild) {
    void limiter.schedule(buildAssetIndex).then(finishResync).catch((err) => {
      queueAssetResyncInProgress.value = false
      releaseContentTransitionHoldIfOwned()
      purgeAssetIndexEntriesNotNeeded()
      console.error('error while building asset index...', err)
    })
  }
  else {
    finishResync()
  }
}

let lastFlushedAssetsVersion = -1
let lastFlushedPlayerGroup: typeof state.playerGroup = null
let lastFlushedPlatform: string | undefined

/**
 * Pin outgoing pixels as soon as campaign assets bump — before post-flush S1 remounts (#1352).
 * Debounced {@link flushQueueAssetsWatch} alone was ~50ms too late.
 */
watch(
  campaignAssetsVersion,
  (next, prev) => {
    if (typeof prev !== 'number' || next === prev || next < 0) {
      return
    }
    if (state.registrationPlaybackResyncPending || registrationResyncInFlight != null) {
      return
    }
    pinContentTransitionHoldFromCurrentSurface()
  },
  { flush: 'sync' }
)

// Single watch: rebuild asset index when queue *content* or campaignAssetsVersion changes; coalesce burst updates (S1).
watch(
  [queue, campaignAssetsVersion, playerGroup, () => state.platform] as const,
  ([q, campaignAssetsVersion, playerGroup, platform]) => {
    latestQueueAssetsWatch = {
      queue: q ?? [],
      campaignAssetsVersion,
      playerGroup,
      platform
    }
    if (queueAssetsWatchDebounceTimer != null) {
      clearTimeout(queueAssetsWatchDebounceTimer)
    }
    queueAssetsWatchDebounceTimer = setTimeout(() => {
      queueAssetsWatchDebounceTimer = null
      flushQueueAssetsWatch()
    }, QUEUE_ASSETS_WATCH_DEBOUNCE_MS)
  },
  { immediate: true }
)

/**
 * Re-resolve the on-air buffer from the current queue while the worker playable sequence is unchanged.
 * Mid-dwell watches defer same-identity facet.asset swaps (`allowSameIdentityFacetSwap: false`) so
 * remounts do not race revoke. After a content hold releases, call with `true` so rematerialized
 * URNs reach the surface under sticky cover (#1352 review).
 */
function syncSameSequenceQueueFacetsToSurface(opts?: {
  allowSameIdentityFacetSwap?: boolean
}): void {
  if (registrationTransitionHoldActive.value || registrationResyncInFlight != null) {
    return
  }
  const allowSameIdentityFacetSwap = opts?.allowSameIdentityFacetSwap === true
  const slim = lastWorkerBufferSlim.value
  const q = unref(queue) ?? []
  if (slim.length === 0 || q.length === 0 || lastPresentationNotifiedIdx.value < 0) {
    return
  }
  const pl = unref(campaign)?.playlists
  const planFromQueue = queueFramesToWorkerPlaybackPlan(q, pl)
  if (
    getPlaybackWorkerPayloadSequenceHash(planFromQueue)
    !== getPlaybackWorkerPayloadSequenceHash(slim)
  ) {
    return
  }
  const cur = playbackSurface.value
  const pointer = cur.viewIdx >= 0 && cur.viewIdx < cur.frames.length ? cur.viewIdx : 0
  const coerced = coercePresentationToQueuePlan(
    slim,
    pointer,
    planFromQueue,
    lastPresentationNotifiedIdx.value
  )
  let frames = resolvePlaybackBufferSegments(coerced.segments)
  if (frames.length === 0 || frames.length !== slim.length) {
    return
  }
  let viewIdx = Math.min(Math.max(0, coerced.pointer), frames.length - 1)
  const bindPlanIdx = playbackPlanHasBookCps(planFromQueue)
    ? portalBookPresentationAuthority()
    : lastPresentationNotifiedIdx.value
  if (bindPlanIdx >= 0 && q.length > 0) {
    const bound = bindPresentationViewToQueueNotifiedIdx(frames, viewIdx, bindPlanIdx, q)
    frames = bound.frames
    viewIdx = bound.viewIdx
  }
  else {
    const visible = cur.frames[Math.min(Math.max(0, cur.viewIdx), Math.max(0, cur.frames.length - 1))]
    if (visible != null) {
      const matchedIdx = frames.findIndex(
        f =>
          f.id === visible.id
          && f.playlistUrn === visible.playlistUrn
          && (f.playlistSeqNo ?? 0) === (visible.playlistSeqNo ?? 0)
      )
      if (matchedIdx >= 0) {
        viewIdx = matchedIdx
      }
      else {
        return
      }
    }
  }
  const visibleAfter = frames[viewIdx]
  if (visibleAfter == null) {
    return
  }
  /**
   * Same spine slot with new facet.asset: do not remount the on-air buffer mid-dwell while
   * assets may still be revoking. Post-hold catch-up sets allowSameIdentityFacetSwap.
   */
  const visibleBefore = cur.frames[Math.min(Math.max(0, cur.viewIdx), Math.max(0, cur.frames.length - 1))]
  if (
    !allowSameIdentityFacetSwap
    && visibleBefore != null
    && sameQueueFrameIdentity(visibleBefore, visibleAfter)
    && queueFramesDifferInFacetAssets(visibleBefore, visibleAfter)
  ) {
    return
  }
  const presentationAuthorityIdx = portalBookPresentationAuthority()
  if (
    playbackPlanHasBookCps(planFromQueue)
    && presentationAuthorityIdx >= 0
    && presentationAuthorityIdx < planFromQueue.length
  ) {
    const authoritySeg = planFromQueue[presentationAuthorityIdx]!
    const visibleOnAir = cur.frames[Math.min(Math.max(0, cur.viewIdx), Math.max(0, cur.frames.length - 1))]
    if (
      visibleOnAir != null
      && visibleOnAir.id === authoritySeg.frameId
      && workerBufferMatchesNotifiedIdx(coerced.segments, planFromQueue, presentationAuthorityIdx)
    ) {
      return
    }
  }
  if (
    cur.frames.length === frames.length
    && cur.viewIdx === viewIdx
    && getQueueHash(cur.frames) === getQueueHash(frames)
  ) {
    return
  }
  const portalBookCommitOpts = playbackPlanHasBookCps(planFromQueue) && presentationAuthorityIdx >= 0
    ? { portalBookPlanIdx: presentationAuthorityIdx }
    : undefined
  commitPlaybackSurface(frames, viewIdx, portalBookCommitOpts)
}

schedulePostContentHoldSurfaceCatchUp = () => {
  requestAnimationFrame(() => {
    if (registrationTransitionHoldActive.value || registrationResyncInFlight != null) {
      return
    }
    syncSameSequenceQueueFacetsToSurface({ allowSameIdentityFacetSwap: true })
  })
}

// S1: same playable sequence but facets/metadata updated (e.g. widget defaultConfig) — re-resolve buffer from last worker slim rows without waiting for a frame advance.
watch(
  [queue, campaignAssetsVersion, campaign],
  () => {
    syncSameSequenceQueueFacetsToSurface({ allowSameIdentityFacetSwap: false })
  },
  { flush: 'post' }
)

// Expose loading count for debug status (CurrentState shows "n widgets loading...").
watch(
  assetIndex,
  () => {
    const idx = unref(assetIndex)
    let count = 0
    for (const urn of Object.keys(idx)) {
      const entry = idx[urn]
      if (!entry) { continue }
      const isIframe = ('isWidget' in entry && entry.isWidget === true) || ('usesIframe' in entry && entry.usesIframe === true)
      if (isIframe && !('iframeLoaded' in entry && entry.iframeLoaded === true)) { count++ }
    }
    state.widgetsLoadingCount = count
  },
  { deep: true, immediate: true }
)

async function initWorker() {
  const initGeneration = nextPlaybackWorkerInitGeneration()
  const isColdTabSession = initGeneration === 1
  if (isColdTabSession) {
    invalidatePlaybackWorkerSyncKey()
  }
  /** Block {@link flushQueueAssetsWatch} worker sync until bootstrap finishes — avoids double frame-0 commit (logs: sync + presentation + init commit same ms). */
  suppressQueueWatchWorkerSyncUntilMs = performance.now() + 2000
  await playbackScheduler.detachComlinkListeners()
  await playbackScheduler.setPlaybackReadyListener(
    proxy((ready: boolean) => {
      if (ready) {
        playbackScheduler.startTicker()
      }
    })
  )
  function refreshTimerOverlayFromWorker() {
    void playbackScheduler.getPlaybackTiming().then((t) => {
      if (!shouldPublishPlaybackTimingOverlay({
        queueLen: (unref(queue) ?? []).length,
        surfaceFrameCount: surfaceForRender.value.frames.length
      })) {
        state.playbackTiming = null
        state.playbackTimingSampleAtPerfMs = null
        notePlaybackTimingSampleForStuckDetect(null)
        updatePlaybackTimerStuckDetection()
        return
      }
      if (state.showTimersOverlay && !playbackTimingSampleMatchesPaintedFrame(t)) {
        return
      }
      state.playbackTiming = t
      state.playbackTimingSampleAtPerfMs = performance.now()
      notePlaybackTimingSampleForStuckDetect(t)
      if (state.showTimersOverlay) {
        mergeVisibleFrameIntoPlaybackTiming()
      }
      else {
        updatePlaybackTimerStuckDetection()
      }
    })
  }
  function applyWorkerSegmentsOnly(value: PlaybackSegment[]) {
    if (suppressRegistrationPresentationDuringSync > 0) {
      return
    }
    if (shouldDropPresentationForTransitionHold({
      registrationHoldActive: registrationTransitionHoldActive.value,
      awaitingRegistrationBootstrapCommit: resolveRegistrationBootstrapCommit != null,
      renderableFrameCount: surfaceForRender.value.frames.length
    })) {
      return
    }
    let notifiedIdxForBufferOnly = lastPresentationNotifiedIdx.value
    if (notifiedIdxForBufferOnly < 0) {
      return
    }
    const planNow = workerPlaybackPlanFromQueue()
    if (planNow.length > 1) {
      const visibleBuf = playbackSurface.value.frames[playbackSurface.value.viewIdx]
      const visibleBufFallback = visibleBuf != null
        ? findOnPlanIdxForVisibleFrame(planNow, visibleBuf)
        : -1
      const bufPeakHeal = healStalePresentationPeakAuthority({
        planLen: planNow.length,
        fallbackIdx: visibleBufFallback,
        frameIdForHealedIdx: idx => planNow[idx]?.frameId ?? null
      })
      if (bufPeakHeal != null) {
        notifiedIdxForBufferOnly = bufPeakHeal.afterIdx
        lastPresentationNotifiedIdx.value = bufPeakHeal.afterIdx
      }
    }
    if (getPlaybackPresentationSession().lastLoggedPlanIdx >= 0 && notifiedIdxForBufferOnly >= 0 && planNow.length > 1) {
      const correctedIdx = correctBufferOnlyNotifiedIdx(
        getPlaybackPresentationSession().lastLoggedPlanIdx,
        notifiedIdxForBufferOnly,
        planNow.length,
        playbackPlanHasBookCps(planNow)
      )
      if (correctedIdx !== notifiedIdxForBufferOnly) {
        notifiedIdxForBufferOnly = correctedIdx
        if (!playbackPlanHasBookCps(planNow)) {
          lastPresentationNotifiedIdx.value = correctedIdx
          recordPresentationPlanIdx(correctedIdx)
          void playbackScheduler.syncResumePlanIdx(correctedIdx).catch(() => {})
        }
      }
    }
    lastWorkerBufferSlim.value = [...value]
    const qNow = unref(queue) ?? []
    const cur = playbackSurface.value
    const pointer = cur.viewIdx >= 0 && cur.viewIdx < cur.frames.length ? cur.viewIdx : 0
    const coerced = coercePresentationToQueuePlan(
      value,
      pointer,
      planNow,
      notifiedIdxForBufferOnly
    )
    const presentationAuthorityIdx = portalBookPresentationAuthority()
    if (
      playbackPlanHasBookCps(planNow)
      && presentationAuthorityIdx >= 0
      && presentationAuthorityIdx >= 2
      && notifiedIdxForBufferOnly >= 0
      && notifiedIdxForBufferOnly < presentationAuthorityIdx
      && !(notifiedIdxForBufferOnly === 0 && presentationAuthorityIdx === planNow.length - 1)
    ) {
      refreshTimerOverlayFromWorker()
      return
    }
    if (
      playbackPlanHasBookCps(planNow)
      && presentationAuthorityIdx >= 0
      && !workerBufferMatchesNotifiedIdx(coerced.segments, planNow, presentationAuthorityIdx)
    ) {
      refreshTimerOverlayFromWorker()
      return
    }
    const visibleOnAir = cur.frames[Math.min(Math.max(0, cur.viewIdx), Math.max(0, cur.frames.length - 1))]
    const authoritySeg = presentationAuthorityIdx >= 0 && presentationAuthorityIdx < planNow.length
      ? planNow[presentationAuthorityIdx]
      : null
    if (
      playbackPlanHasBookCps(planNow)
      && authoritySeg != null
      && visibleOnAir != null
      && visibleOnAir.id === authoritySeg.frameId
      && workerBufferMatchesNotifiedIdx(coerced.segments, planNow, presentationAuthorityIdx)
    ) {
      lastWorkerBufferSlim.value = [...value]
      refreshTimerOverlayFromWorker()
      return
    }
    let frames = resolvePlaybackBufferSegments(coerced.segments)
    let viewIdx = frames.length > 0
      ? Math.min(Math.max(0, coerced.pointer), frames.length - 1)
      : Math.max(0, coerced.pointer)
    if (notifiedIdxForBufferOnly >= 0 && frames.length > 0 && qNow.length > 0) {
      const bound = bindPresentationViewToQueueNotifiedIdx(frames, viewIdx, notifiedIdxForBufferOnly, qNow)
      frames = bound.frames
      viewIdx = bound.viewIdx
    }
    else {
      const visible = cur.frames[Math.min(Math.max(0, cur.viewIdx), Math.max(0, cur.frames.length - 1))]
      if (visible != null && frames.length > 0) {
        const matchedIdx = frames.findIndex(
          f =>
            f.id === visible.id
            && f.playlistUrn === visible.playlistUrn
            && (f.playlistSeqNo ?? 0) === (visible.playlistSeqNo ?? 0)
        )
        if (matchedIdx >= 0) {
          viewIdx = matchedIdx
        }
        else {
          refreshTimerOverlayFromWorker()
          return
        }
      }
    }
    const curVisIdx = Math.min(Math.max(0, cur.viewIdx), Math.max(0, cur.frames.length - 1))
    const curVisible = cur.frames[curVisIdx]
    const nextVisible = frames[Math.min(Math.max(0, viewIdx), Math.max(0, frames.length - 1))]
    if (playbackPlanHasBookCps(planNow) && curVisible != null && nextVisible != null) {
      const sameOnAir = curVisible.id === nextVisible.id
        && curVisible.playlistUrn === nextVisible.playlistUrn
        && (curVisible.playlistSeqNo ?? 0) === (nextVisible.playlistSeqNo ?? 0)
      if (!sameOnAir) {
        if (cur.frames.length === frames.length && frames.length > 0 && frames.length <= 2) {
          const merged = frames.map((f, i) => (i === curVisIdx ? cur.frames[curVisIdx]! : f))
          if (getQueueHash(cur.frames) !== getQueueHash(merged)) {
            commitPlaybackFramesOnly(merged)
          }
        }
        refreshTimerOverlayFromWorker()
        return
      }
      if (cur.frames.length === frames.length && cur.viewIdx === viewIdx && getQueueHash(cur.frames) === getQueueHash(frames)) {
        refreshTimerOverlayFromWorker()
        return
      }
      const pinnedViewIdx = curVisIdx
      const merged = cur.frames.length === frames.length && frames.length <= 2
        ? frames.map((f, i) => (i === pinnedViewIdx ? cur.frames[pinnedViewIdx]! : f))
        : frames
      if (!tryApplyCommittedPlaybackSurface(merged, pinnedViewIdx)) {
        refreshTimerOverlayFromWorker()
        return
      }
      refreshTimerOverlayFromWorker()
      return
    }
    const prevVisibleId = curVisible?.id
    const bufferPortalBookOpts = playbackPlanHasBookCps(planNow) && notifiedIdxForBufferOnly >= 0
      ? { portalBookPlanIdx: notifiedIdxForBufferOnly }
      : undefined
    commitPlaybackSurface(frames, viewIdx, bufferPortalBookOpts)
    const nextVisibleId = frames[Math.min(Math.max(0, viewIdx), Math.max(0, frames.length - 1))]?.id
    if (notifiedIdxForBufferOnly >= 0 && notifiedIdxForBufferOnly > getPlaybackPresentationSession().lastLoggedPlanIdx) {
      recordPresentationPlanIdx(notifiedIdxForBufferOnly)
      lastPresentationNotifiedIdx.value = notifiedIdxForBufferOnly
      void playbackScheduler.syncResumePlanIdx(notifiedIdxForBufferOnly).catch(() => {})
    }
    refreshTimerOverlayFromWorker()
  }
  const initialPresentation = await playbackScheduler.setFramePresentationListener(
    proxy((segments: PlaybackSegment[], pointer: number, notifiedIdx: number, meta?: TFramePresentationMeta) => {
      portalBookPresentationCommitChain = portalBookPresentationCommitChain
        .then(() => runPortalBookFramePresentationCommit(segments, pointer, notifiedIdx, meta))
        .catch(() => {})
    })
  )
  if (initialPresentation.notifiedIdx >= 0) {
    const workerIdx = initialPresentation.notifiedIdx
    recordPresentationPlanIdx(workerIdx)
    if (workerIdx > getPlaybackPresentationSession().lastNotifiedPlanIdx) {
      getPlaybackPresentationSession().lastNotifiedPlanIdx = workerIdx
      lastPresentationNotifiedIdx.value = workerIdx
    }
  }
  async function runPortalBookFramePresentationCommit(
    segments: PlaybackSegment[],
    pointer: number,
    notifiedIdx: number,
    meta?: TFramePresentationMeta
  ): Promise<void> {
    const isWallDwellEscape = meta?.wallDwellEscape === true
    /**
     * Do not credit same-paint escapes (or remount) when registration sync/bootstrap hold
     * would no-op the commit — remount clears transition holds mid-bootstrap (#1551 Bugbot).
     * Match {@link shouldDropPresentationForTransitionHold} / Inner gate (#1553).
     */
    const presentationCommitOpen = suppressRegistrationPresentationDuringSync === 0
      && !shouldDropPresentationForTransitionHold({
        registrationHoldActive: registrationTransitionHoldActive.value,
        awaitingRegistrationBootstrapCommit: resolveRegistrationBootstrapCommit != null,
        renderableFrameCount: surfaceForRender.value.frames.length
      })
    const paintedFrameIdBeforeEscape = isWallDwellEscape && presentationCommitOpen
      ? lastPaintedVisibleFrameId
      : null
    await runPortalBookFramePresentationCommitInner(segments, pointer, notifiedIdx)
    if (isWallDwellEscape && presentationCommitOpen) {
      await noteWallDwellEscapePresentationLeave({
        paintedFrameIdBefore: paintedFrameIdBeforeEscape,
        notifiedPlanIdx: notifiedIdx
      })
    }
  }
  async function runPortalBookFramePresentationCommitInner(
    segments: PlaybackSegment[],
    pointer: number,
    notifiedIdx: number
  ): Promise<void> {
    if (suppressRegistrationPresentationDuringSync > 0) {
      return
    }
    if (shouldDropPresentationForTransitionHold({
      registrationHoldActive: registrationTransitionHoldActive.value,
      awaitingRegistrationBootstrapCommit: resolveRegistrationBootstrapCommit != null,
      renderableFrameCount: surfaceForRender.value.frames.length
    })) {
      return
    }
    const planNow = workerPlaybackPlanFromQueue()
    const qNow = unref(queue) ?? []
    let presentationSegments = segments
    let presentationPointer = pointer
    let effectiveNotifiedIdx = notifiedIdx
    /**
     * Stale peak after queue shrink (e.g. 16 on a 6-frame rematch) poisons last→head wrap:
     * clamp rolls to mid-slot and {@link isSpuriousMidLoopHeadReset} drops the notify (#1662).
     * Self-heal + Sentry + always-on fault via {@link healStalePresentationPeakAuthority}.
     */
    const visibleForAuthorityHeal = playbackSurface.value.frames[playbackSurface.value.viewIdx]
    const visibleAuthorityFallback = visibleForAuthorityHeal != null
      ? findOnPlanIdxForVisibleFrame(planNow, visibleForAuthorityHeal)
      : -1
    const peakHeal = healStalePresentationPeakAuthority({
      planLen: planNow.length,
      fallbackIdx: visibleAuthorityFallback,
      frameIdForHealedIdx: idx => planNow[idx]?.frameId ?? null
    })
    if (peakHeal != null) {
      lastPresentationNotifiedIdx.value = peakHeal.afterIdx
    }
    const presentationAuthorityIdx = Math.max(
      getPlaybackPresentationSession().lastLoggedPlanIdx,
      lastPresentationNotifiedIdx.value,
      getPeakPresentationPlanIdx()
    )
    if (presentationAuthorityIdx >= 0 && notifiedIdx >= 0 && planNow.length > 1) {
      const correctedIdx = correctPresentationCommitNotifiedIdx(
        presentationAuthorityIdx,
        notifiedIdx,
        planNow.length,
        playbackPlanHasBookCps(planNow)
      )
      if (correctedIdx !== notifiedIdx) {
        effectiveNotifiedIdx = correctedIdx
        const onAir = planNow[correctedIdx]!
        const preload = resolvePresentationPreloadSegment(planNow, correctedIdx)
        presentationSegments = preload != null ? [onAir, preload] : [onAir]
        presentationPointer = 0
        /**
         * Must lower worker `lastNotifiedFrameIdx` with the clock (#1546). Clock-only rephase left the
         * worker permanently +N ahead — clampSequential treated the rephased elapsed as a wrap-around
         * jump and ratcheted sticky `scheduler_main_desync` (Slimbox field: sticky Δ=+6 through wrap).
         */
        void playbackScheduler.alignOnAirPlanIdx(performance.now(), correctedIdx)
      }
    }
    const prevLoggedPresentationIdx = getPlaybackPresentationSession().lastLoggedPlanIdx
    const presentationAuthority = portalBookPresentationAuthority()
    if (
      playbackPlanHasBookCps(planNow)
      && isSpuriousPortalBookMidLoopHouseReset(presentationAuthority, effectiveNotifiedIdx, planNow.length)
    ) {
      return
    }
    const visibleNowForRewind = playbackSurface.value.frames[playbackSurface.value.viewIdx]
    const visibleBelongsToPlan = visibleNowForRewind != null
      && findOnPlanIdxForVisibleFrame(planNow, visibleNowForRewind) >= 0
    /**
     * After a full playlist swap the surface still holds the previous program while the worker
     * bootstraps at 0. Head-reset guards would drop that notify and leave timer/surface desynced.
     * Only block mid-loop→0 when the visible creative is still part of the current plan.
     */
    if (
      visibleBelongsToPlan
      && !playbackPlanHasBookCps(planNow)
      && isSpuriousMidLoopHeadReset(presentationAuthorityIdx, effectiveNotifiedIdx, planNow.length)
    ) {
      refreshTimerOverlayFromWorker()
      return
    }
    const visiblePlanIdxForRewind = visibleNowForRewind != null
      ? findOnPlanIdxForVisibleFrame(planNow, visibleNowForRewind)
      : -1
    if (
      visibleBelongsToPlan
      && !playbackPlanHasBookCps(planNow)
      && isRegressivePresentationToLoopHead({
        notifiedIdx: effectiveNotifiedIdx,
        planLen: planNow.length,
        visiblePlanIdx: visiblePlanIdxForRewind,
        loggedPlanIdx: prevLoggedPresentationIdx
      })
    ) {
      refreshTimerOverlayFromWorker()
      return
    }
    if (
      effectiveNotifiedIdx >= 0
      && effectiveNotifiedIdx < planNow.length
    ) {
      const expectedId = planNow[effectiveNotifiedIdx]?.frameId ?? ''
      const visibleNow = playbackSurface.value.frames[playbackSurface.value.viewIdx]
      /**
       * Compare against the last *committed* on-air id, not `planNow[prevLogged]`.
       * For len-1 rotating embeds the plan idx stays 0 while the queue head rotates — reading
       * `planNow[0]` after the rotate always equals `expectedId`, so every notify looked like a
       * duplicate and the surface never painted the new pick (#1382).
       */
      const lastCommittedOnAirId = getPlaybackPresentationSession().lastOnAirFrameId
      const alreadyLoggedSameIdx = expectedId !== ''
        && effectiveNotifiedIdx === prevLoggedPresentationIdx
        && lastCommittedOnAirId === expectedId
      const isDuplicatePresentation = alreadyLoggedSameIdx
        || (
          expectedId !== ''
          && visibleNow?.id === expectedId
          && (
            effectiveNotifiedIdx === presentationAuthorityIdx
            || (
              !playbackPlanHasBookCps(planNow)
              && effectiveNotifiedIdx === 0
              && presentationAuthorityIdx <= 0
            )
          )
        )
      if (isDuplicatePresentation) {
        refreshTimerOverlayFromWorker()
        return
      }
    }
    const prevPlanFrame = prevLoggedPresentationIdx >= 0 ? planNow[prevLoggedPresentationIdx] : null
    const onAirPlanFrame = planNow[effectiveNotifiedIdx]
    const bookToHf3Transition = playbackPlanHasBookCps(planNow)
      && prevPlanFrame != null
      && onAirPlanFrame != null
      && prevPlanFrame.frameId.startsWith('book-')
      && !onAirPlanFrame.frameId.startsWith('book-')
    if (bookToHf3Transition) {
      /** Drop stale HF1/HF2 hold so book→HF3 transition cannot flash first-visit house again. */
      lastNonEmptyPlaybackSurface.value = null
      pendingPlaybackSurfaceCommit = null
      resetPendingCommitRetryState()
    }
    const coerced = coercePresentationToQueuePlan(
      presentationSegments,
      presentationPointer,
      planNow,
      effectiveNotifiedIdx
    )
    lastWorkerBufferSlim.value = [...coerced.segments]
    let frames = resolvePlaybackBufferSegments(coerced.segments)
    let viewIdx = frames.length > 0
      ? Math.min(Math.max(0, coerced.pointer), frames.length - 1)
      : Math.max(0, coerced.pointer)
    if (notifiedIdx >= 0 && frames.length > 0 && qNow.length > 0) {
      const bound = bindPresentationViewToQueueNotifiedIdx(frames, viewIdx, effectiveNotifiedIdx, qNow)
      frames = bound.frames
      viewIdx = bound.viewIdx
    }
    /** Keep plan idx on pending commits so flush can advance authority after apply (#1479). */
    const portalBookCommitOpts = effectiveNotifiedIdx >= 0
      ? { portalBookPlanIdx: effectiveNotifiedIdx }
      : undefined
    if (playbackPlanHasBookCps(planNow)) {
      portalBookAllowVisibleSurfaceAdvance = true
    }
    try {
      commitPlaybackSurface(frames, viewIdx, portalBookCommitOpts)
      if (playbackPlanHasBookCps(planNow)) {
        await recordPortalBookPresentationAfterVisibleCommit(planNow, effectiveNotifiedIdx, frames, viewIdx)
      }
      else {
        await recordMainPresentationAfterVisibleCommit(planNow, effectiveNotifiedIdx, frames, viewIdx)
      }
    }
    finally {
      portalBookAllowVisibleSurfaceAdvance = false
    }
    refreshTimerOverlayFromWorker()
  }
  await playbackScheduler.setPlaylistLoopListener(
    proxy(async () => {
      notifyE2eLoopWrap()
      const wallNow = performance.now()
      const materializedHead = (unref(queue) ?? [])[0]
      const slotMs = materializedHead?.duration != null
        ? Math.max(0, materializedHead.duration * 1000)
        : 4000
      /** Floor must stay below the slot so short (1s) embeds can wrap (#1382). */
      const minDwellMs = houseEmbedLoopWrapMinDwellMs(slotMs)
      const dwellSinceLastAdvance = wallNow - lastHouseEmbedLoopAdvanceWallMs
      const forceWrapDespiteMinDwell = lastHouseEmbedLoopAdvanceWallMs > 0
        && dwellSinceLastAdvance >= Math.max(minDwellMs * 3, 5_000)
      if (
        lastHouseEmbedLoopAdvanceWallMs > 0
        && dwellSinceLastAdvance < minDwellMs
        && !forceWrapDespiteMinDwell
      ) {
        // Do not stamp lastLoopWrapAtMs here: throttled callbacks would look like healthy wraps
        // while refresh never runs, hiding a painted-content freeze from content_not_advancing.
        return
      }
      // Stamp only wraps that reach embed refresh (scheduler alive past min-dwell).
      lastLoopWrapAtMs = Date.now()
      suppressQueueWatchWorkerSyncUntilMs = wallNow + 800
      const embedAdvanced = refreshEmbeddedPicksAfterLoopWrap()
      lastLoopWrapAdvancedContent = embedAdvanced
      if (!embedAdvanced) {
        if (performance.now() >= suppressDeferredRegistrationBoundaryUntil) {
          notifyDeferredPlaybackScheduleFrameBoundary()
        }
        return
      }
      const materializedPlan = getWorkerPlaybackPlanNow()
      lastHouseEmbedLoopAdvanceWallMs = wallNow
      invalidatePlaybackWorkerSyncKey()
      debouncedSetQueueUpdateState.cancel()
      if (performance.now() >= suppressDeferredRegistrationBoundaryUntil) {
        notifyDeferredPlaybackScheduleFrameBoundary()
      }
      await syncPlaybackWorkerStateNow(state.platform, {
        immediate: true,
        loopWrapScheduleResync: true,
        playbackPlanOverride: materializedPlan.length > 0 ? materializedPlan : undefined
      })
    })
  )
  await playbackScheduler.setFrameBufferListener(
    proxy((value: PlaybackSegment[]) => {
      applyWorkerSegmentsOnly(value)
    })
  )
  syncPresentationAuthorityFromStorage()
  const sessionAuthorityBeforeBootstrap = playbackPresentationAuthorityFromSession()
  const workerQueueLenBeforeBootstrap = await playbackScheduler.getPlaybackLoopQueueLen()
  const remountBootstrapAction = resolveVBufferRemountBootstrapAction({
    initGeneration,
    sessionAuthority: sessionAuthorityBeforeBootstrap,
    workerQueueLen: workerQueueLenBeforeBootstrap,
    materializedQueueLen: (unref(queue) ?? []).length
  })
  if (remountBootstrapAction.kind !== 'cold_init') {
    lastWorkerBufferSlim.value = [...initialPresentation.segments]
    suppressQueueWatchWorkerSyncUntilMs = performance.now() + 200
    setInitWorkerBootstrapPending(false)
    if (remountBootstrapAction.kind === 'remount_refill_worker') {
      await playbackScheduler.syncResumePlanIdx(remountBootstrapAction.resumePlanIdx)
      try {
        await syncPlaybackWorkerStateNow(state.platform, { immediate: true, allowDuringInit: true })
      }
      catch (err) {
        console.error('error while refilling worker after remount...', err)
      }
      await commitRemountPlaybackSurfaceAtPlanIdx(remountBootstrapAction.resumePlanIdx)
    }
    else if (remountBootstrapAction.kind === 'remount_rephase') {
      await playbackScheduler.syncResumePlanIdx(remountBootstrapAction.resumePlanIdx)
      playbackScheduler.rephaseFrameFromStart(performance.now(), remountBootstrapAction.resumePlanIdx)
      await commitRemountPlaybackSurfaceAtPlanIdx(remountBootstrapAction.resumePlanIdx)
    }
    standByFrameCount.value = await playbackScheduler.setStandByFrameListener(
      proxy((value: number) => {
        standByFrameCount.value = value
      })
    )
    playbackScheduler.startTicker()
    syncWebPlaybackTick()
    flushPendingPlaybackSurfaceCommit()
    clearPlaybackReloadColdStartPending()
    return
  }
  lastWorkerBufferSlim.value = [...initialPresentation.segments]
  const initFrames = resolvePlaybackBufferSegments(initialPresentation.segments)
  const initPtr = initialPresentation.pointer
  let initViewIdx = initFrames.length > 0 ? Math.min(Math.max(0, initPtr), initFrames.length - 1) : Math.max(0, initPtr)
  const initPlan = workerPlaybackPlanFromQueue()
  if (initialPresentation.notifiedIdx >= 0) {
    const currentAuthority = Math.max(
      getPlaybackPresentationSession().lastLoggedPlanIdx,
      lastPresentationNotifiedIdx.value,
      getPeakPresentationPlanIdx()
    )
    const canApplyInitIdx = shouldApplyInitialWorkerNotifiedIdx({
      currentAuthority,
      initNotifiedIdx: initialPresentation.notifiedIdx,
      planLen: initPlan.length,
      isPortalBookPlan: playbackPlanHasBookCps(initPlan)
    })
    if (canApplyInitIdx) {
      recordPresentationPlanIdx(initialPresentation.notifiedIdx)
      lastPresentationNotifiedIdx.value = initialPresentation.notifiedIdx
      if (playbackPlanHasBookCps(initPlan)) {
        recordPortalBookPresentationAuthority(initialPresentation.notifiedIdx, initPlan.length)
      }
      void playbackScheduler.syncResumePlanIdx(initialPresentation.notifiedIdx).catch(() => {})
    }
  }
  if (initialPresentation.notifiedIdx >= 0 && initFrames.length > 0 && initPlan.length > 0) {
    initViewIdx = alignHoldViewToPriorLoopOnAir(
      { frames: initFrames, viewIdx: initViewIdx },
      { notifiedIdx: initialPresentation.notifiedIdx, segments: initPlan }
    ).viewIdx
  }
  /** Do not wipe a presentation the worker already pushed while this async init was in flight. */
  await portalBookPresentationCommitChain
  const initNotifiedIdx = initialPresentation.notifiedIdx
  const surfaceAlreadyShowsInit = initNotifiedIdx >= 0
    && playbackSurface.value.frames.length > 0
    && (() => {
      const vis = playbackSurface.value.frames[playbackSurface.value.viewIdx]
      const expectedId = initPlan[initNotifiedIdx]?.frameId
      return vis != null && expectedId != null && expectedId !== '' && vis.id === expectedId
    })()
  if ((initFrames.length > 0 || (unref(queue) ?? []).length === 0) && !surfaceAlreadyShowsInit) {
    if (playbackPlanHasBookCps(initPlan)) {
      portalBookAllowVisibleSurfaceAdvance = true
    }
    try {
      commitPlaybackSurface(initFrames, initViewIdx)
    }
    finally {
      portalBookAllowVisibleSurfaceAdvance = false
    }
  }
  else if ((initFrames.length > 0 || (unref(queue) ?? []).length === 0) && surfaceAlreadyShowsInit) {
  }
  else if ((unref(queue) ?? []).length > 0 && playbackSurface.value.frames.length === 0) {
    void (async () => {
      if (!queueAssetResyncInProgress.value) {
        queueAssetResyncInProgress.value = true
        try {
          await limiter.schedule(buildAssetIndex)
        }
        catch (err) {
          console.error('error while building asset index after initWorker...', err)
        }
        finally {
          queueAssetResyncInProgress.value = false
        }
      }
      try {
        await syncPlaybackWorkerStateNow(state.platform, { immediate: true })
      }
      catch (err) {
        console.error('error while syncing worker after initWorker...', err)
      }
      flushPendingPlaybackSurfaceCommit()
    })()
  }

  suppressQueueWatchWorkerSyncUntilMs = performance.now() + 200
  setInitWorkerBootstrapPending(false)
  clearPlaybackReloadColdStartPending()
  if ((unref(queue) ?? []).length > 0) {
    const resumeIdx = Math.max(
      getPlaybackPresentationSession().lastLoggedPlanIdx,
      lastPresentationNotifiedIdx.value,
      initialPresentation.notifiedIdx
    )
    if (resumeIdx >= 0) {
      await playbackScheduler.syncResumePlanIdx(resumeIdx)
    }
    try {
      await syncPlaybackWorkerStateNow(state.platform, { immediate: true, allowDuringInit: true })
    }
    catch (err) {
      console.error('error while syncing worker after initWorker...', err)
    }
    playbackScheduler.startTicker()
    syncWebPlaybackTick()
    flushPendingPlaybackSurfaceCommit()
  }

  standByFrameCount.value = await playbackScheduler.setStandByFrameListener(
    proxy((value: number) => {
      standByFrameCount.value = value
    })
  )
}

/** Interval (ms) for proactive SW mount check while widgets are playing. */
const SW_MOUNT_CHECK_INTERVAL_MS = 5_000
/** Timeout (ms) waiting for GET_MOUNT_STATUS reply from SW. */
const SW_MOUNT_STATUS_REPLY_TIMEOUT_MS = 2000
/** Debounce (ms) for recovery after WIDGET_MOUNT_MISSING to avoid multiple re-mounts. */
const SW_MOUNT_RECOVERY_DEBOUNCE_MS = 2500

/** Return widget path prefixes we expect to be mounted in the SW (from current assetIndex). */
function getExpectedWidgetPrefixes(): Set<string> {
  const idx = unref(assetIndex)
  const origin = typeof location !== 'undefined' ? location.origin : ''
  const prefixes = new Set<string>()
  for (const entry of Object.values(idx)) {
    if (!entry || !('src' in entry) || typeof entry.src !== 'string' || !entry.src.startsWith(`${origin}/w/`)) {
      continue
    }
    try {
      const pathname = new URL(entry.src).pathname
      const match = pathname.match(/^\/w\/[^/]+\//)
      if (match) {
        prefixes.add(match[0])
      }
    }
    catch {
      // ignore invalid URLs
    }
  }
  return prefixes
}

/**
 * After asset-cache repair, force remount blob/HLS media even when IndexedDB blobs were unchanged.
 * Schedule reload alone does not recreate `<video>` when the Blob instance is the same.
 */
async function remountBinaryPlaybackMediaAfterCacheRepair(): Promise<void> {
  invalidatePlaybackWorkerSyncKey()
  const forceRemountUrns = new Set<string>()
  const origin = typeof location !== 'undefined' ? location.origin : ''
  for (const [assetId, entry] of Object.entries(unref(assetIndex))) {
    if (!entry || 'urlTemplate' in entry) {
      continue
    }
    if ('isHlsVideo' in entry && entry.isHlsVideo === true) {
      forceRemountUrns.add(assetId)
      continue
    }
    if ('src' in entry && typeof entry.src === 'string' && entry.src.startsWith('blob:')) {
      forceRemountUrns.add(assetId)
    }
    if ('blob' in entry && entry.blob instanceof Blob && isWidgetTgzBlob(entry.blob)) {
      unmountWidgetBundle(assetId)
      forceRemountUrns.add(assetId)
    }
  }
  if (forceRemountUrns.size === 0) {
    return
  }
  await limiter.schedule(() => buildAssetIndex(forceRemountUrns))
  const remount = `_remount=${Date.now()}`
  for (const entry of Object.values(unref(assetIndex))) {
    if (!entry || !('src' in entry) || typeof entry.src !== 'string' || !entry.src.startsWith(`${origin}/w/`)) {
      continue
    }
    ;(entry as { src: string }).src = entry.src.includes('?') ? `${entry.src}&${remount}` : `${entry.src}?${remount}`
  }
  pushStateToWorker()
}

/**
 * Re-mount all campaign widgets in the SW and force widget iframes to reload.
 * Call after SW eviction or controllerchange so widgets are served again.
 *
 * Entries are kept in assetIndex during rebuild so the UI never shows
 * SECTION UNAVAILABLE while the async re-mount is in progress.
 */
async function runWidgetMountRecovery(): Promise<void> {
  invalidatePlaybackWorkerSyncKey()
  // Collect TGZ widget URNs that need remounting. Unmount them from the SW
  // only — do NOT delete from the reactive assetIndex so the UI keeps
  // displaying the current widget iframe instead of flashing SECTION UNAVAILABLE.
  const idx = unref(assetIndex)
  const origin = typeof location !== 'undefined' ? location.origin : ''
  const forceRemountUrns = new Set<string>()
  for (const [assetId, entry] of Object.entries(idx)) {
    if (!entry || 'urlTemplate' in entry) { continue }
    if ('blob' in entry && entry.blob instanceof Blob && isWidgetTgzBlob(entry.blob)) {
      unmountWidgetBundle(assetId)
      forceRemountUrns.add(assetId)
    }
  }

  await limiter.schedule(() => buildAssetIndex(forceRemountUrns))

  // Bust the iframe src cache so the new SW mount is actually fetched.
  const remount = `_remount=${Date.now()}`
  for (const entry of Object.values(unref(assetIndex))) {
    if (!entry || !('src' in entry) || typeof entry.src !== 'string' || !entry.src.startsWith(`${origin}/w/`)) {
      continue
    }
    ;(entry as { src: string }).src = entry.src.includes('?') ? `${entry.src}&${remount}` : `${entry.src}?${remount}`
  }

  pushStateToWorker()
}

/**
 * Ask the SW for its mounted prefixes; if any expected prefix is missing, run recovery.
 */
function checkSwMountStatus(): void {
  if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
    return
  }
  const expected = getExpectedWidgetPrefixes()
  if (expected.size === 0) {
    return
  }
  const channel = new MessageChannel()
  const timeoutId = setTimeout(() => {
    channel.port1.onmessage = null
    runWidgetMountRecovery().catch((err) => {
      console.error('[VBuffer] Proactive SW mount recovery failed', err)
    })
  }, SW_MOUNT_STATUS_REPLY_TIMEOUT_MS)
  channel.port1.onmessage = (e: MessageEvent) => {
    const data = e.data as { type?: string, payload?: { prefixes?: string[] } }
    if (data?.type !== 'MOUNT_STATUS' || !Array.isArray(data?.payload?.prefixes)) {
      return
    }
    clearTimeout(timeoutId)
    channel.port1.onmessage = null
    const mounted = new Set(data.payload.prefixes)
    const missing = [...expected].filter(p => !mounted.has(p))
    if (missing.length > 0) {
      runWidgetMountRecovery().catch((err) => {
        console.error('[VBuffer] Proactive SW mount recovery failed', err)
      })
    }
  }
  navigator.serviceWorker.controller.postMessage({ type: 'GET_MOUNT_STATUS' }, [channel.port2])
}

function clearWidgetEntriesFromAssetIndex() {
  const idx = unref(assetIndex)
  const origin = window.location.origin
  const widgetIds = Object.keys(idx).filter((id) => {
    const entry = idx[id]
    if (!entry || 'urlTemplate' in entry) { return false }
    return 'blob' in entry && entry.blob instanceof Blob && (isWidgetTgzBlob(entry.blob) || ('src' in entry && entry.src.startsWith(`${origin}/w/`)))
  })
  widgetIds.forEach((assetId) => {
    const entry = idx[assetId]
    if (entry && 'src' in entry) {
      if (entry.src.startsWith('blob:')) {
        releaseImagePredecode(entry.src)
        URL.revokeObjectURL(entry.src)
      }
      else if ('blob' in entry && entry.blob instanceof Blob && isWidgetTgzBlob(entry.blob)) {
        unmountWidgetBundle(assetId)
      }
    }
    delete idx[assetId]
  })
}

/** Track whether document was previously hidden; only resync when transitioning from hidden → visible to avoid resyncing while already visible (which can cause double-advance and drift). */
let wasDocumentHidden = typeof document !== 'undefined' && document.visibilityState !== 'visible'
function onVisibilityChange(): void {
  const visible = document.visibilityState === 'visible'
  if (wasDocumentHidden && visible) {
    if ((queue.value ?? []).length > 0) {
      const suppressVisibilitySnap = registrationResyncInFlight != null
        || registrationTransitionHoldActive.value
      if (suppressVisibilitySnap) {
        void playbackScheduler.getPlaybackTiming().then((t) => {
          void playbackScheduler.resyncWebToMainThreadNow(performance.now(), t.playlistElapsedMs)
        })
      }
      else {
        lastCompletedFrameTransition = null
        void playbackScheduler.snapWebPresentationToLoopElapsed(performance.now())
      }
    }
    checkSwMountStatus()
  }
  else if (!visible && state.tenant != null) {
    // P2b: flush accumulated PoP before the tab is backgrounded / screen sleeps.
    emitPlaybackHeartbeat(true)
  }
  wasDocumentHidden = !visible
  documentVisible.value = visible
}

/** Debounced recovery so a single eviction doesn't trigger multiple re-mounts. */
const runWidgetMountRecoveryDebounced = debounce(() => {
  runWidgetMountRecovery().catch((err) => {
    console.error('[VBuffer] SW mount recovery (after WIDGET_MOUNT_MISSING) failed', err)
  })
}, SW_MOUNT_RECOVERY_DEBOUNCE_MS, { leading: true, trailing: false })

let swMountCheckIntervalId: ReturnType<typeof setInterval> | null = null

function onSwMessage(event: MessageEvent): void {
  const data = event.data as { type?: string }
  if (data?.type === 'WIDGET_MOUNT_MISSING') {
    runWidgetMountRecoveryDebounced()
  }
}

onMounted(() => {
  installE2ePlaybackProbe()
  void refreshNativePlaybackSurfaceEnabled()
  setMediaLoopDeferredBoundaryListener(() => {
    if (performance.now() >= suppressDeferredRegistrationBoundaryUntil) {
      notifyDeferredPlaybackScheduleFrameBoundary()
    }
  })
  if (isE2ePlaybackProbeEnabled()) {
    registerE2eCompletedFrameTransitionInjector((transition) => {
      lastCompletedFrameTransition = transition
      updatePlaybackTimerStuckDetection()
    })
    registerE2eDiagnosticsSnapshotProvider(() => buildPlayerDiagnosticsSnapshot())
    registerE2ePopFlushHandler(() => {
      emitPlaybackHeartbeat(true)
    })
  }
  document.addEventListener('playing', onVideoElementPlaying, true)
  document.addEventListener('visibilitychange', onVisibilityChange)
  startEmptyPlaybackSurfaceStallPoll()
  startContentNotAdvancingPoll()
  startProphylacticRecyclePoll()
  if (!('serviceWorker' in navigator)) {
    return
  }
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!navigator.serviceWorker.controller) {
      // SW was unregistered or died — reload to re-register and recover.
      location.reload()
      return
    }
    // Same as runWidgetMountRecovery: unmount from the old SW but keep entries
    // visible while the new SW mounts so there is no SECTION UNAVAILABLE flash.
    const idxOnChange = unref(assetIndex)
    const forceRemountUrns = new Set<string>()
    for (const [assetId, entry] of Object.entries(idxOnChange)) {
      if (!entry || 'urlTemplate' in entry) { continue }
      if ('blob' in entry && entry.blob instanceof Blob && isWidgetTgzBlob(entry.blob)) {
        unmountWidgetBundle(assetId)
        forceRemountUrns.add(assetId)
      }
    }
    const originOnChange = typeof location !== 'undefined' ? location.origin : ''
    invalidatePlaybackWorkerSyncKey()
    limiter.schedule(() => buildAssetIndex(forceRemountUrns)).then(() => {
      const remount = `_remount=${Date.now()}`
      for (const entry of Object.values(unref(assetIndex))) {
        if (!entry || !('src' in entry) || typeof entry.src !== 'string' || !entry.src.startsWith(`${originOnChange}/w/`)) { continue }
        ;(entry as { src: string }).src = entry.src.includes('?') ? `${entry.src}&${remount}` : `${entry.src}?${remount}`
      }
      pushStateToWorker()
    }).catch((err) => {
      console.error('[VBuffer] Re-mount after controllerchange failed', err)
    })
  })
  navigator.serviceWorker.addEventListener('message', onSwMessage)
  swMountCheckIntervalId = setInterval(checkSwMountStatus, SW_MOUNT_CHECK_INTERVAL_MS)
})

/**
 * Drive the worker's frame timer from the UI thread.
 * Mid/high visible → rAF; low visible → 250ms interval (Comlink tick floods kill decode on SlimBOX);
 * mid + video-heavy queue → same interval; hidden → 500ms (tabs throttle rAF).
 * See {@link resolveWebPlaybackTickMode}.
 */
let webTickIntervalId: ReturnType<typeof setInterval> | null = null
let webTickRafId: number | null = null
let prophylacticRecyclePollTimer: ReturnType<typeof setInterval> | null = null
const PROPHYLACTIC_RECYCLE_POLL_MS = 60_000

function countVideoFramesInQueue(frames: readonly IQueueFrame[]): number {
  let n = 0
  for (const frame of frames) {
    if (frameMainExpectsVideo(frame)) {
      n += 1
    }
  }
  return n
}

function stopWebTick(): void {
  if (webTickIntervalId != null) {
    clearInterval(webTickIntervalId)
    webTickIntervalId = null
  }
  if (webTickRafId != null) {
    cancelAnimationFrame(webTickRafId)
    webTickRafId = null
  }
}

function syncWebPlaybackTick(): void {
  stopWebTick()
  const frames = queue.value ?? []
  const queueLen = frames.length
  if (queueLen <= 0) {
    return
  }
  const mode = resolveWebPlaybackTickMode({
    documentVisible: documentVisible.value,
    capacityTier: state.capacityTier,
    videoFrameCountInQueue: countVideoFramesInQueue(frames)
  })
  if (mode.kind === 'raf') {
    const loop = () => {
      if (registrationResyncInFlight != null) {
        webTickRafId = requestAnimationFrame(loop)
        return
      }
      void playbackScheduler.tick(performance.now())
      webTickRafId = requestAnimationFrame(loop)
    }
    webTickRafId = requestAnimationFrame(loop)
    return
  }
  webTickIntervalId = setInterval(() => {
    if (registrationResyncInFlight != null) {
      return
    }
    void playbackScheduler.tick(performance.now())
  }, mode.ms)
}

function pollProphylacticPlaybackRecycle(): void {
  const frames = queue.value ?? []
  const decision = shouldProphylacticPlaybackRecycle({
    navigationUptimeMs: performance.now(),
    capacityTier: state.capacityTier,
    videoFrameCountInQueue: countVideoFramesInQueue(frames),
    documentVisible: documentVisible.value
  })
  if (decision.recycle && decision.reason) {
    tryPerformPlaybackRecoveryReload(decision.reason)
  }
}

function startProphylacticRecyclePoll(): void {
  if (prophylacticRecyclePollTimer != null) {
    return
  }
  prophylacticRecyclePollTimer = setInterval(pollProphylacticPlaybackRecycle, PROPHYLACTIC_RECYCLE_POLL_MS)
}

function stopProphylacticRecyclePoll(): void {
  if (prophylacticRecyclePollTimer != null) {
    clearInterval(prophylacticRecyclePollTimer)
    prophylacticRecyclePollTimer = null
  }
}

watch(
  () => [
    (queue.value ?? []).length,
    documentVisible.value,
    state.capacityTier,
    countVideoFramesInQueue(queue.value ?? [])
  ] as const,
  () => {
    syncWebPlaybackTick()
  },
  { immediate: true }
)

watch(
  () => playbackSurface.value.frames.length,
  (len) => {
    if (len >= 2) {
      startPreloadPoll()
    }
    else {
      stopPreloadPoll()
    }
  },
  { immediate: true }
)

// Stop the preload poll while the device is under memory pressure; restart when it clears.
watch(
  () => state.lowMemoryMode,
  (isLow) => {
    if (isLow) {
      stopPreloadPoll()
    }
    else if (playbackSurface.value.frames.length >= 2) {
      startPreloadPoll()
    }
  }
)

/**
 * Warm the next still off-DOM (detached Image + decode) so we do not mount a full-size hidden
 * &lt;img&gt; in the buffer (Chrome compositor bleed). Runs even in lowMemoryMode.
 */
function resolveNextImageSrcToPredecode(): string | null {
  const s = surfaceForRender.value
  if (s.frames.length === 0) {
    return null
  }
  if (s.frames.length === 1) {
    return frameMainImageSrc(s.frames[0])
  }
  const hiddenIdx = s.viewIdx === 0 ? 1 : 0
  if (hiddenIdx >= 0 && hiddenIdx < s.frames.length) {
    const hiddenSrc = frameMainImageSrc(s.frames[hiddenIdx])
    if (hiddenSrc != null) {
      return hiddenSrc
    }
  }
  // Buffer may omit the next image from the hidden slot while a still is on-air; peek the queue.
  const q = unref(queue) ?? []
  const notifiedIdx = lastPresentationNotifiedIdx.value
  if (notifiedIdx >= 0 && q.length > 0) {
    const next = q[(notifiedIdx + 1) % q.length]
    return frameMainImageSrc(next)
  }
  return null
}

let lastImagePredecodeSrc: string | null = null
watch(
  [
    imagePredecodeNow,
    () => surfaceForRender.value.viewIdx,
    () => surfaceForRender.value.frames.map(f => f.id).join('\0'),
    () => lastPresentationNotifiedIdx.value
  ],
  () => {
    const frameCount = surfaceForRender.value.frames.length
    // Single-frame image playlists: warm immediately. Multi-frame: only inside the lead window.
    if (frameCount === 0 || (frameCount > 1 && !imagePredecodeNow.value)) {
      return
    }
    const src = resolveNextImageSrcToPredecode()
    if (src == null) {
      return
    }
    if (src === lastImagePredecodeSrc && isImagePredecoded(src)) {
      return
    }
    lastImagePredecodeSrc = src
    void predecodeImageSrc(src)
  },
  { flush: 'post' }
)

/** Proof-of-play extension for heartbeat (playlist-scoped; no campaign key in body schema). */
function getProofOfPlayPayload(attributionOverride?: {
  playlistUrn?: string | null
  orderId?: string | null
}): {
  assetPlays: {
    assetUrn: string
    durationSeconds: number
    playCount: number
    playedAt?: number
  }[]
  playlistUrn?: string
  orderId?: string | null
} | undefined {
  if (state.tenant == null || state.tenant.proofOfPlay === false) { return undefined }

  const frame = activeFrameForProofOfPlay.value
  const currentAssetUrns = getAssetUrnsFromFrame(frame)
  // Close the open interval onto the currently on-air URNs (mid-spot flush path).
  const nowMono = accumulateOpenProofOfPlayInterval(defaultMonotonicNowMs(), currentAssetUrns)
  frameStartMonoMs = nowMono
  lastScheduledDurationSeconds = scheduledDurationSecondsFromFrame(frame)
  lastAssetUrns = currentAssetUrns
  // Wall clock only for playedAt (ingest wall date/bucket); duration used monotonic above.
  const playedAtWallMs = defaultWallNowMs()

  // Duration-only gate: playCount alone must not invent durationSeconds (zero-elapsed flush /
  // same-ms cadence tick would otherwise POST a synthetic 1s spot and break POP-1 pin E2E).
  const durationEntries = [...assetDurationMsByUrn.entries()].filter(([, ms]) => ms > 0)
  if (durationEntries.length === 0) { return undefined }

  const assetPlays = durationEntries
    .map(([assetUrn, ms]) => {
      // Explicit 0 = continuation chunk after a mid-spot flush (spot already counted).
      // Do not default missing → 1 here; that re-counts on-air spots across cadence windows.
      const started = assetPlayCountByUrn.get(assetUrn)
      const durationSeconds = finalizeProofOfPlayDurationSeconds(ms)
      if (durationSeconds == null) {
        return null
      }
      return {
        assetUrn,
        durationSeconds,
        playCount: started != null ? Math.max(0, started) : 0,
        playedAt: playedAtWallMs
      }
    })
    .filter((play): play is NonNullable<typeof play> => play != null)

  assetDurationMsByUrn.clear()
  assetPlayCountByUrn.clear()
  // Frame still on-air: keep duration clock; do not re-count this presentation until the next identity change.

  if (assetPlays.length === 0) { return undefined }

  const playlistUrn = attributionOverride != null && Object.prototype.hasOwnProperty.call(attributionOverride, 'playlistUrn')
    ? (attributionOverride.playlistUrn ?? undefined)
    : (frame?.contentSourcePlaylistUrn ?? frame?.playlistUrn)
  const payload: {
    assetPlays: {
      assetUrn: string
      durationSeconds: number
      playCount: number
      playedAt?: number
    }[]
    playlistUrn?: string
    orderId?: string | null
  } = {
    assetPlays,
    ...(playlistUrn != null && playlistUrn !== '' ? { playlistUrn } : {})
  }
  if (attributionOverride != null && Object.prototype.hasOwnProperty.call(attributionOverride, 'orderId')) {
    payload.orderId = attributionOverride.orderId ?? null
  }
  return payload
}

function emitPlaybackHeartbeat(
  includeProofOfPlay: boolean,
  attributionOverride?: { playlistUrn?: string | null, orderId?: string | null }
): void {
  const payload = includeProofOfPlay ? getProofOfPlayPayload(attributionOverride) : undefined
  if (isE2ePlaybackProbeEnabled() && payload?.assetPlays?.length) {
    recordE2ePopHeartbeat({
      ts: performance.now(),
      assetPlays: payload.assetPlays,
      playlistUrn: payload.playlistUrn ?? null,
      popFrameId: activeFrameForProofOfPlay.value?.id ?? null
    })
  }
  reportPlaybackHeartbeat(payload).catch(() => {})
}

/**
 * P2b: PoP flushes on attribution / wall-bucket / visibility / teardown — not on the 60s uptime tick
 * and not on every active frame identity change.
 * Buffer `viewIdx` / preload slot advances must not trigger PoP while `playingFrame` is unchanged.
 */
watch(
  () => {
    const frame = activeFrameForProofOfPlay.value
    return proofOfPlayAttributionKey({
      playlistUrn: frame?.contentSourcePlaylistUrn ?? frame?.playlistUrn ?? null,
      orderId: state.proofOfPlayOrderId
    })
  },
  (nextKey, prevKey) => {
    if (state.tenant == null || state.tenant.proofOfPlay === false) {
      return
    }
    if (!shouldFlushProofOfPlayOnAttributionChange(prevKey ?? '', nextKey)) {
      return
    }
    const prevParts = (prevKey ?? '').split('\t')
    const prevPlaylist = prevParts[0] ?? ''
    const prevOrder = prevParts[1] ?? ''
    emitPlaybackHeartbeat(true, {
      playlistUrn: prevPlaylist === '' ? null : prevPlaylist,
      orderId: prevOrder === '' ? null : prevOrder
    })
  }
)

watch(
  [() => playbackSurface.value.viewIdx, () => playbackSurface.value.frames, playingFrame, activeFrameForProofOfPlay],
  () => {
    if (!isE2ePlaybackProbeEnabled()) {
      return
    }
    const { viewIdx, frames } = playbackSurface.value
    setE2ePopDiagnostics({
      viewIdx,
      surfaceFrameIds: frames.map(f => f.id),
      playingFrameId: playingFrame.value?.id ?? null,
      popFrameId: activeFrameForProofOfPlay.value?.id ?? null
    })
  },
  { flush: 'post' }
)

let popWallBucketFlushTimer: ReturnType<typeof setTimeout> | null = null
function clearPopWallBucketFlushTimer(): void {
  if (popWallBucketFlushTimer != null) {
    clearTimeout(popWallBucketFlushTimer)
    popWallBucketFlushTimer = null
  }
}
function armPopWallBucketFlushTimer(): void {
  clearPopWallBucketFlushTimer()
  if (state.tenant == null) {
    return
  }
  const utcOffsetMinutes = -new Date().getTimezoneOffset()
  const untilBoundary = msUntilNextProofOfPlayWallBucket(Date.now(), utcOffsetMinutes)
  const delay = Math.max(0, untilBoundary - PROOF_OF_PLAY_WALL_BUCKET_FLUSH_LEAD_MS)
  popWallBucketFlushTimer = setTimeout(() => {
    popWallBucketFlushTimer = null
    if (state.tenant != null) {
      emitPlaybackHeartbeat(true)
    }
    armPopWallBucketFlushTimer()
  }, delay)
}

watch(
  () => state.tenant != null,
  (enabled) => {
    if (enabled) {
      armPopWallBucketFlushTimer()
    }
    else {
      clearPopWallBucketFlushTimer()
    }
  },
  { immediate: true }
)

/**
 * Arm the playback-heartbeat cadence (uptime, storage, versions — metadata only).
 * Interval is jittered ±10% with a random initial phase (#1257) so mass reconnects do not herd one TenantDO.
 * P2b: PoP does **not** ride this 60s tick — duration/`playCount` accumulate until attribution,
 * wall-bucket (±PROOF_OF_PLAY_WALL_BUCKET_FLUSH_LEAD_MS), visibility loss, or teardown flush.
 *
 * When `visibleLen === 0` we **do not** clear the interval: the worker often commits an empty buffer for a tick
 * while `surfaceForRender` briefly has length 0; clearing would reset the cadence so interval ticks rarely
 * fired. If there is no timer yet (idle / no playlist), we **arm** the same cadence so metadata-only heartbeats
 * still run (viewport size for dashboard aspect ratio, storage, versions, etc.). Timer is cleared in
 * {@link onBeforeUnmount} when VBuffer leaves the tree.
 */
syncPlaybackHeartbeatCadence = () => {
  if (playbackHeartbeatCadence?.isArmed()) {
    return
  }
  // E2E: keep initial phase late so POP-1 pin assertions are not flaked by cadence jitter
  // landing inside the short video-preload window (phase ∈ [0, 60s) on CI).
  const e2eStablePhaseRandom = isE2ePlaybackProbeEnabled()
    ? () => 0.92
    : undefined
  playbackHeartbeatCadence = armPlaybackHeartbeatCadence({
    baseIntervalMs: PLAYBACK_HEARTBEAT_INTERVAL_MS,
    ...(e2eStablePhaseRandom != null ? { random: e2eStablePhaseRandom } : {}),
    onTick: () => {
      // P2b: uptime/metadata only — PoP flushes on wall-bucket / attribution / visibility / teardown.
      emitPlaybackHeartbeat(false)
    }
  })
}

watch(
  () => documentVisible.value,
  () => {
    if (state.showTimersOverlay) {
      updatePlaybackTimerStuckDetection()
    }
  }
)

registerRegistrationPlaybackResyncHandler(applyRegistrationPlaybackResync)
registerCacheRepairMediaRemountHandler(remountBinaryPlaybackMediaAfterCacheRepair)
void initWorker()

/** 500ms balances Comlink load vs frame-change resync; display ticks via rAF extrapolation. */
const TIMERS_OVERLAY_POLL_MS = 500
let timersOverlayPollTimer: ReturnType<typeof setInterval> | null = null
/** Ignore stale getPlaybackTiming replies after a newer fetch (frame change / poll overlap). */
let timersOverlayFetchGen = 0

/**
 * Overlay extrapolation stamps wall time against the painted creative. Discard worker samples
 * whose on-air plan row does not match what {@link surfaceForRender} is showing (holds /
 * buffer-only idx can diverge from {@link playbackSurface}).
 */
function playbackTimingSampleMatchesPaintedFrame(t: { notifiedIdx: number }): boolean {
  const surface = surfaceForRender.value
  const painted = surface.viewIdx >= 0 && surface.viewIdx < surface.frames.length
    ? surface.frames[surface.viewIdx]
    : null
  const paintedId = painted?.id
  if (paintedId == null) {
    return true
  }
  const q = unref(queue) ?? []
  if (t.notifiedIdx < 0 || t.notifiedIdx >= q.length) {
    return false
  }
  return q[t.notifiedIdx]!.id === paintedId
}

async function fetchTimersOverlayTimingFromWorker(): Promise<void> {
  if (!state.showTimersOverlay) {
    return
  }
  const fetchGen = ++timersOverlayFetchGen
  try {
    if (!shouldPublishPlaybackTimingOverlay({
      queueLen: (unref(queue) ?? []).length,
      surfaceFrameCount: surfaceForRender.value.frames.length
    })) {
      if (fetchGen !== timersOverlayFetchGen) {
        return
      }
      /** Cube / empty surface — withhold lines so extrapolation cannot keep ticking a stale sample. */
      state.playbackTiming = null
      state.playbackTimingSampleAtPerfMs = null
      notePlaybackTimingSampleForStuckDetect(null)
      updatePlaybackTimerStuckDetection()
      return
    }
    const t = await playbackScheduler.getPlaybackTiming()
    if (fetchGen !== timersOverlayFetchGen) {
      return
    }
    if (!playbackTimingSampleMatchesPaintedFrame(t)) {
      return
    }
    state.playbackTiming = t
    state.playbackTimingSampleAtPerfMs = performance.now()
    mergeVisibleFrameIntoPlaybackTiming()
  }
  catch {
    if (fetchGen !== timersOverlayFetchGen) {
      return
    }
    state.playbackTiming = null
    state.playbackTimingSampleAtPerfMs = null
    notePlaybackTimingSampleForStuckDetect(null)
    updatePlaybackTimerStuckDetection()
  }
}

watch(
  () => state.showTimersOverlay,
  (show) => {
    if (timersOverlayPollTimer) {
      clearInterval(timersOverlayPollTimer)
      timersOverlayPollTimer = null
    }
    if (!show) {
      timersOverlayFetchGen += 1
      state.playbackTiming = null
      state.playbackTimingSampleAtPerfMs = null
      lastTimingFrameElapsedMsForStuckDetect = null
      lastTimingChangeMsForStuckDetect = null
      state.playbackTimerStuck = null
      reportPlaybackTimerStuckToSentry({ suspected: false, overrunMs: 0, frameId: null, visibleDwellMs: 0, timingFlatlineMs: 0 })
      return
    }
    // So Frame line counts from 0 for the frame in view when overlay opens.
    // Do not reset diagnosticsVisibleFrameStartMs — overlay UX must not fabricate accelerated faults.
    visibleFrameStartMs = Date.now()
    lastTimingFrameElapsedMsForStuckDetect = null
    lastTimingChangeMsForStuckDetect = null
    void fetchTimersOverlayTimingFromWorker()
    timersOverlayPollTimer = setInterval(() => {
      void fetchTimersOverlayTimingFromWorker()
    }, TIMERS_OVERLAY_POLL_MS)
  },
  { immediate: true }
)

watch(
  playbackSurface,
  () => {
    if (state.showTimersOverlay && playbackSurface.value.frames.length > 0) {
      void fetchTimersOverlayTimingFromWorker()
    }
  },
  { deep: false }
)

/** Resync when the painted identity changes — use surfaceForRender (includes hold residual). */
watch(
  () => {
    const s = surfaceForRender.value
    const f = s.viewIdx >= 0 && s.viewIdx < s.frames.length ? s.frames[s.viewIdx] : null
    return f?.id ?? null
  },
  (id, prev) => {
    if (id == null || id === prev || !state.showTimersOverlay) {
      return
    }
    void fetchTimersOverlayTimingFromWorker()
  }
)

watch(
  () => state.tenant?.nativePlaybackSurface !== false,
  (enabled, prevEnabled) => {
    if (enabled && prevEnabled === false) {
      clearNativeHybridHalWedgeDisabled()
    }
    void refreshNativePlaybackSurfaceEnabled()
  }
)

watch(
  [
    nativePlaybackSurfaceEnabled,
    () => playbackSurface.value.viewIdx,
    () => playbackSurface.value.frames.map(f => f.id).join('\0')
  ],
  () => {
    scheduleNativeHybridSync()
  },
  { flush: 'post' }
)

watch(
  nativeHybridVideoOnAir,
  (onAir) => {
    if (onAir) {
      // Arm only from Media3 onReady / onFirstFrame — present() can return before paint.
      return
    }
    // Leave native path: disarm only after serialized sync releases/retargets Media3.
    void hybridSyncChain.then(() => {
      if (!nativeHybridVideoOnAir.value) {
        nativeUnderlaySurfaceArmed.value = false
      }
    })
  }
)

watch(
  nativeUnderlayChromeDesired,
  (want) => {
    if (want) {
      setNativePlaybackUnderlayChrome(true)
      return
    }
    void hybridSyncChain.then(() => {
      if (!nativeUnderlayChromeDesired.value) {
        setNativePlaybackUnderlayChrome(false)
      }
    })
  },
  { immediate: true, flush: 'post' }
)

onBeforeUnmount(async () => {
  registerE2eCompletedFrameTransitionInjector(null)
  registerE2eDiagnosticsSnapshotProvider(null)
  registerE2ePopFlushHandler(null)
  registerRegistrationPlaybackResyncHandler(null)
  registerCacheRepairMediaRemountHandler(null)
  setMediaLoopDeferredBoundaryListener(null)
  for (const u of hybridEventUnsubs) {
    u()
  }
  hybridEventUnsubs = []
  disarmHybridFirstFrameWatch()
  nativeStagePromises.clear()
  nativeHybridFailedUrns.clear()
  nativeHybridHalWedged = false
  hybridHalWedgeReported = false
  hybridHalRebootReported = false
  hybridRecoveryReloadArmed = false
  lastHybridSlotUri[0] = null
  lastHybridSlotUri[1] = null
  nativeUnderlaySurfaceArmed.value = false
  setNativePlaybackUnderlayChrome(false)
  void releaseHybridPlayback()
  document.removeEventListener('playing', onVideoElementPlaying, true)
  document.removeEventListener('visibilitychange', onVisibilityChange)
  stopEmptyPlaybackSurfaceStallPoll()
  stopContentNotAdvancingPoll()
  stopProphylacticRecyclePoll()
  runPlayerDiagnosticsSafely(() => clearContentNotAdvancingForPlaybackTeardown())
  // Flush coalesced PoP before clearing; idle-root heartbeats continue after unmount without this buffer.
  emitPlaybackHeartbeat(true)
  resetPendingCommitRetryState()
  if (queueAssetsWatchDebounceTimer != null) {
    clearTimeout(queueAssetsWatchDebounceTimer)
    queueAssetsWatchDebounceTimer = null
  }
  if (queueAssetsWatchRetryTimer != null) {
    clearTimeout(queueAssetsWatchRetryTimer)
    queueAssetsWatchRetryTimer = null
  }
  if (swMountCheckIntervalId !== null) {
    clearInterval(swMountCheckIntervalId)
    swMountCheckIntervalId = null
  }
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.removeEventListener('message', onSwMessage)
  }
  runWidgetMountRecoveryDebounced.cancel()
  stopWebTick()
  stopPreloadPoll()
  if (postVideoImageBlackoutTimer != null) {
    clearTimeout(postVideoImageBlackoutTimer)
    postVideoImageBlackoutTimer = null
  }
  postVideoImageBlackoutActive.value = false
  clearStickyOutgoingHold('teardown')
  if (timersOverlayPollTimer) {
    clearInterval(timersOverlayPollTimer)
    timersOverlayPollTimer = null
  }
  timersOverlayFetchGen += 1
  lastTimingFrameElapsedMsForStuckDetect = null
  lastTimingChangeMsForStuckDetect = null
  state.playbackTimerStuck = null
  clearPopWallBucketFlushTimer()
  playbackHeartbeatCadence?.clear()
  playbackHeartbeatCadence = null
  playingFrame.value = null
  clearImagePredecodeCache()
  Object.entries(unref(assetIndex)).forEach(([urn, entry]) => {
    if (entry && 'src' in entry && entry.src.startsWith('blob:')) {
      releaseImagePredecode(entry.src)
      URL.revokeObjectURL(entry.src)
    }
    else if (entry && 'blob' in entry && entry.blob instanceof Blob && isWidgetTgzBlob(entry.blob)) {
      unmountWidgetBundle(urn)
    }
  })
  assetIndex.value = {}
  try {
    await playbackScheduler.setStateImmediate(
      {
        playbackPlan: [],
        playbackEpochWallMs: null,
        platform: state.platform
      },
      { registrationScheduleReplace: true }
    )
  }
  catch (err) {
    console.warn('[VBuffer] failed to clear worker loop on unmount', err)
  }
  await playbackScheduler.stopTicker()
  await playbackScheduler.detachComlinkListeners()
})
</script>

<style>
/* First-paint reveal: loading scene dissolves into frames (absolute so layers overlap). */
.playback-surface-reveal-leave-active {
  transition: opacity 0.55s cubic-bezier(0.4, 0, 1, 1);
  position: absolute;
  inset: 0;
  z-index: 3;
  pointer-events: none;
}
.playback-surface-reveal-enter-active {
  transition: opacity 0.7s cubic-bezier(0.16, 1, 0.3, 1);
  position: absolute;
  inset: 0;
  z-index: 2;
}
.playback-surface-reveal-enter-from,
.playback-surface-reveal-leave-to {
  opacity: 0;
}
@media (prefers-reduced-motion: reduce) {
  .playback-surface-reveal-leave-active,
  .playback-surface-reveal-enter-active {
    transition-duration: 0.01ms;
  }
}
</style>
